<script lang="ts">
  import { onMount, tick, untrack } from 'svelte';
  import { agentName as agentNameStore, currentView, fundPins, fundLps, fundSyncFilterSummary, pollVersion, pollingActive, userEmail as userEmailStore, FUND_STAGES, fundStageColor, fundStageLabel, avatarColor, initialsFor } from '../lib/stores';
  import TodayHero from '../lib/TodayHero.svelte';
  import TodayNav from '../lib/TodayNav.svelte';
  import SyncButton from '../lib/SyncButton.svelte';
  import { createSectionSpy, jumpToSection } from '../lib/sectionSpy.svelte';
  import { renderSafeMarkdown, unwrapHardBreaks, repairLostApostrophes, stripCids } from '../lib/markdown';
  import EmailBodyEditor from '../lib/EmailBodyEditor.svelte';
  import BriefingView from '../lib/briefing/BriefingView.svelte';
  import { createBriefingStore } from '../lib/briefing/store.svelte';
  import type { Briefing, Source as BriefingSource } from '../lib/briefing/types';
  import { parseDay, senderName } from '../lib/format';
  import { downloadSupportFile, isSupportView, canUseAs } from '../lib/supportView';
  import {
    addFundNote, editFundNote, assignFundInstitution, confirmFundRole, undoFundReviewRole, createFundInstitution, getFundInstitutionActivity, fundChat, fundDocumentUrl, generateFundOutput, getFundContext,
    getFundMessage, getFundSent, getFundScheduled, getAllFundScheduled, getFundScheduledDetail,
    refreshFundThreads,
    editFundScheduled, getFundWorkspace, getFundPins, pinFundContact, unpinFundContact, pinFundOrganization, unpinFundOrganization,
    cancelFundScheduled, submitFundEmail, setFundStage, setFundFollowUp, resumeFundAutoStage,
    setFundInstitutionStage, setFundInstitutionFollowUp, resumeFundInstitutionAutoStage,
    getFundDashboardViews, getFundDashboardMailActivity, saveFundDashboardView, deleteFundDashboardView,
    getFundInstitutionBrief, generateFundInstitutionBrief, type FundInstitutionBrief,
    getFundBriefing, generateFundBriefing, putFundBriefingOverride, deleteFundBriefingOverride,
    lockFundBriefing, heartbeatFundBriefingLock, releaseFundBriefingLock,
    getFundTodayDecisions, getFundTodayCalls, actOnFundTodayDecision,
    type FundTodayDecision, type FundTodayCall,
    polishFundEmail, saveFundProviderDraft, saveFundReviewDraft, getFundEmailTracking,
    type FundContact, type FundRole, type FundWorkspace, type FundMailActivity, type FundSendReceipt, type FundInstitutionMessage,
    type FundScheduledSend, type FundScheduledDetail,
  } from '../lib/api/fund-ir';

  let { view = 'today', contactId = '', userName = '' }: {
    view?: 'today' | 'dashboard' | 'contacts' | 'scheduled' | 'detail';
    contactId?: string;
    userName?: string;
  } = $props();

  let workspace = $state<FundWorkspace | null>(null);
  let loading = $state(true);
  let error = $state('');
  let search = $state('');
  let roleFilter = $state<FundRole | 'all' | 'unclassified'>('all');
  let contactTab = $state<'auto' | 'organizations' | 'individuals' | 'ungrouped'>('auto');
  let detailTab = $state<'briefing' | 'assistant' | 'emails' | 'contacts' | 'memory' | 'files'>('briefing');
  let briefingView = $state<'web' | 'doc'>('web');
  let savingRole = $state('');
  let savingStage = $state(false);
  let savingFollowUp = $state(false);
  let savingPin = $state(false);
  let pinError = $state('');
  let moreOpen = $state(false);
  let followUpEditing = $state(false);
  let followUpEditorRequested = $state('');
  let nextStepDraft = $state('');
  let followUpDateDraft = $state('');
  let contactContext = $state<Awaited<ReturnType<typeof getFundContext>> | null>(null);
  let contextLoading = $state(false);
  let contextError = $state('');
  let instruction = $state('');
  let draftInstruction = $state('');
  let groundingDocumentIds = $state<string[]>([]);
  let generating = $state<'brief' | 'draft' | ''>('');
  let sentReceipts = $state<FundSendReceipt[]>([]);
  let emailTracking = $state<Awaited<ReturnType<typeof getFundEmailTracking>>['messages']>([]);
  let openedMessages = $state<Set<string>>(new Set());
  let emailQuery = $state('');
  let expandedEmailThreads = $state<Set<string>>(new Set());
  let activeEmailThreadKey = $state('');
  let refreshingThreads = $state(false);
  let messageBodies = $state<Record<string, string>>({});
  let messageErrors = $state<Record<string, string>>({});
  let composeOpen = $state(false);
  let composeSubject = $state('');
  let composeBody = $state('');
  let composeCc = $state('');
  let composeReplyMessageId = $state('');
  let composeFiles = $state<File[]>([]);
  let composeDocumentIds = $state<string[]>([]);
  let composeTracking = $state(false);
  let composeMeeting = $state(false);
  let meetingSummary = $state('');
  let meetingLocation = $state('');
  let meetingStart = $state('');
  let meetingEnd = $state('');
  let scheduleOpen = $state(false);
  let scheduleDate = $state('');
  let scheduleTime = $state('');
  let scheduledSends = $state<FundScheduledSend[]>([]);
  let allScheduled = $state<FundScheduledSend[]>([]);
  let scheduledLoading = $state(false);
  let scheduledError = $state('');
  let scheduledFilter = $state<'all' | 'pending' | 'sent' | 'attention'>('all');
  let selectedSchedule = $state<FundScheduledDetail | null>(null);
  let scheduleSubject = $state('');
  let scheduleBody = $state('');
  let scheduleCc = $state('');
  let scheduleEditDate = $state('');
  let scheduleEditTime = $state('');
  let scheduleSaving = $state(false);
  let savingInstitution = $state(false);
  let newInstitutionName = $state('');
  let institutionActivity = $state<FundInstitutionMessage[]>([]);
  let institutionActivityError = $state('');
  let institutionActivityLoading = $state(false);
  let todayDecisions = $state<FundTodayDecision[]>([]);
  let todayDecisionsError = $state('');
  let todayDecisionsLoaded = $state(false);
  let todayDecisionsSequence = 0;
  let todayCalls = $state<FundTodayCall[]>([]);
  let todayCallsError = $state('');
  let todayCallsLoading = $state(false);
  let todayCallsLoaded = $state(false);
  let todayBriefError = $state('');
  let todayBriefContactId = $state('');
  let todayDraftError = $state('');
  let todayDraftMessageId = $state('');
  let todayFocusKey = $state('');
  let todayFocusContactId = $state('');
  let todayBriefMemberIds = $state<string[]>([]);
  let todayFocusBusy = $state<'brief' | 'draft' | ''>('');
  let todayFocusLoading = $state(false);
  let todayFocusError = $state('');
  let todayFocusBrief = $state<FundInstitutionBrief | null>(null);
  let todayFocusDraft = $state<{ contactId: string; subject: string; body: string } | null>(null);
  let todayComposeRequest = $state<{ contactId: string; subject: string; body: string } | null>(null);
  let todayFocusGeneration = 0;
  let todayExpanded = $state<Record<string, boolean>>({});
  let todayCallsSequence = 0;
  let todayReplyRequest = $state<{ counterparty_id: string; message_id: string;
    subject: string; draftBody?: string; draftSubject?: string } | null>(null);
  let institutionBrief = $state<FundInstitutionBrief | null>(null);
  let institutionBriefContacts = $state<string[]>([]);
  let institutionBriefDocuments = $state<string[]>([]);
  let institutionBriefInstruction = $state('');
  let institutionBriefGenerating = $state(false);
  let institutionBriefError = $state('');
  let noteEditorOpen = $state(false);
  let noteEditId = $state('');
  let noteTitle = $state('');
  let noteBody = $state('');
  let noteSaving = $state(false);
  let composeRequestId = $state('');
  let sending = $state(false);
  let savingReviewDraft = $state(false);
  let polishing = $state(false);
  let polishChanges = $state('');
  let sendNotice = $state('');
  let actionError = $state('');
  let addOpen = $state(false);
  let newEmail = $state('');
  let newName = $state('');
  let newRole = $state<FundRole>('investor');
  /* Only filed LPs belong in the Dashboard. The separate review view
     retains model suggestions and bulk Neither correction. */
  let dashTab = $state<'ir' | 'portfolio' | 'sorting'>('ir');
  type IrStatus = 'overdue' | 'soon' | 'later' | 'undated' | 'none';
  /* Multi-select, like the Sales dashboard chips: an empty array means "All".
     Views saved before this change stored one string ('all' for no filter), so
     applySavedView() widens them on read rather than discarding the view. */
  let irStatusFilter = $state<IrStatus[]>([]);
  let irStageFilter = $state<string[]>([]);
  /* Sorting is driven by the column headers, the way the Sales dashboard does it. The
     union keeps `stage` and `last_email` so a view saved while those were select
     options still applies; nothing sets them any more. */
  let irSort = $state<'name' | 'stage' | 'last_email' | 'due' | 'status'
    | 'inbound' | 'outbound'>('due');
  let irSortDir = $state<'asc' | 'desc'>('asc');
  let irInstitutionFilter = $state('');
  let irPinnedOnly = $state(false);
  let openFilterDD = $state<'stage' | 'status' | 'institution' | null>(null);
  const IR_STATUS_OPTIONS: { key: IrStatus; label: string }[] = [
    { key: 'overdue', label: 'Overdue' }, { key: 'soon', label: 'Soon' },
    { key: 'later', label: 'On track' }, { key: 'undated', label: 'No due date' },
    { key: 'none', label: 'No follow-up' },
  ];
  /* `bookmarked` promotes the VIEW into the dropdown's shortcut section — it is not the
     row ★, which pins a relationship to the sidebar. The backend stores each view as a
     free-form JSON blob, so the field costs no migration; a view saved before it existed
     reads back as undefined and lands in "All views". */
  type FundViewPreset = { name: string; tab: 'ir' | 'portfolio' | 'sorting';
    status: IrStatus[] | string; sort: typeof irSort; institution: string;
    stage: string[] | string; pinned: boolean; search: string; bookmarked?: boolean;
    sortDir?: 'asc' | 'desc';
    /* Written by views saved while the Unchecked chip existed. Spec v3 replaced that chip
       with the table's "To review" group, so the field is read for nothing — kept in the
       type so an old view still parses instead of being dropped on load. */
    checked?: '' | 'unchecked' | 'checked' };
  let savedViews = $state<Record<string, FundViewPreset>>({});
  let activeViewKey = $state('');
  let newViewName = $state('');
  let viewNaming = $state(false);
  let viewError = $state('');
  let viewDropdownOpen = $state(false);
  let editingViewKey = $state<string | null>(null);
  /* The menu renders outside the scrolling table so its actions stay visible. */
  let openStageDD = $state<string | null>(null);
  let stageMenuPosition = $state({ top: 0, left: 0 });
  let dashboardMailActivity = $state<FundMailActivity>({});
  let dashboardMailError = $state('');
  let dashboardMailLoaded = $state(false);
  type FundTrackingMessage = Awaited<ReturnType<typeof getFundEmailTracking>>['messages'][number];
  let dashboardTracking = $state<Record<string, FundTrackingMessage[]>>({});
  let dashboardTrackingLoading = $state<Set<string>>(new Set());
  let dashboardTrackingErrors = $state<Record<string, string>>({});
  let viewSaving = $state(false);
  let reviewDone = $state<Record<string, { contact: FundContact; role: FundRole }>>({});
  let reviewMore = $state<Record<string, boolean>>({});
  let reviewCursor = $state('');
  let expandedFundOrganizations = $state<Set<string>>(new Set());
  let expandedContactOrganizations = $state<Set<string>>(new Set());
  let collapsedContactSearchGroups = $state<Set<string>>(new Set());
  let expandedIrStages = $state<Set<string>>(
    new Set([...FUND_STAGES.map(s => s.key), '', '__mixed__']));

  // Same pill-nav behavior as the Sales Today: the pill for the section in view
  // is the active one, and a click scrolls there rather than jumping.
  const spy = createSectionSpy();
  const spyOn = spy.spyOn;

  // The LP briefing owns the centre of this page. Mike has one conversation and
  // three display modes; moving its snippet never resets messages or the draft.
  const agent = $derived($agentNameStore || 'Dealton');
  let shellEl = $state<HTMLElement>();
  type MikeMode = 'rail' | 'bubble' | 'tab';
  const savedMikeMode = typeof localStorage === 'undefined' ? null : localStorage.getItem('fundMikeMode');
  let mikeMode = $state<MikeMode>(savedMikeMode === 'bubble' || savedMikeMode === 'tab' ? savedMikeMode : 'rail');
  let mikeNarrow = $state(typeof matchMedia !== 'undefined' && matchMedia('(max-width: 900px)').matches);
  let mikeOverlayOpen = $state(false);
  let mikeW = $state(Math.max(240, Math.min(520, Number(
    typeof localStorage === 'undefined' ? 340 : localStorage.getItem('fundRailW')) || 340)));
  let mikeDragging = $state(false);
  let readingLabel = $state('Summary');
  const showMikeRail = $derived(mikeMode === 'rail' && !mikeNarrow);
  const showMikeBubble = $derived(mikeMode === 'bubble' || (mikeMode === 'rail' && mikeNarrow));
  function setMikeMode(mode: MikeMode) {
    mikeMode = mode;
    mikeOverlayOpen = false;
    localStorage.setItem('fundMikeMode', mode);
    if (mode === 'tab') detailTab = 'assistant';
    else if (detailTab === 'assistant') detailTab = 'briefing';
  }
  function openMikeBubble() {
    if (mikeNarrow) mikeOverlayOpen = true;
    else setMikeMode('rail');
  }
  function resizeMike(event: PointerEvent) {
    if (event.button !== 0 || !shellEl) return;
    event.preventDefault();
    mikeDragging = true;
    const move = (e: PointerEvent) => {
      if (!shellEl) return;
      const box = shellEl.getBoundingClientRect();
      mikeW = Math.max(240, Math.min(520, box.width - 429, box.right - e.clientX));
    };
    const stop = () => {
      mikeDragging = false;
      localStorage.setItem('fundRailW', String(Math.round(mikeW)));
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop, { once: true });
  }
  function resizeMikeKey(event: KeyboardEvent) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    mikeW = Math.max(240, Math.min(520, mikeW + (event.key === 'ArrowLeft' ? 20 : -20)));
    localStorage.setItem('fundRailW', String(mikeW));
  }
  function updateMikeNarrow() {
    mikeNarrow = window.matchMedia('(max-width: 900px)').matches
      || (!!shellEl && shellEl.clientWidth < 687);
    if (!mikeNarrow) mikeOverlayOpen = false;
  }
  onMount(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    mq.addEventListener('change', updateMikeNarrow);
    updateMikeNarrow();
    return () => mq.removeEventListener('change', updateMikeNarrow);
  });
  $effect(() => {
    const node = shellEl;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateMikeNarrow);
    observer.observe(node);
    updateMikeNarrow();
    return () => observer.disconnect();
  });
  $effect(() => {
    if (mikeMode !== 'tab' && detailTab === 'assistant') detailTab = 'briefing';
  });



  const allContacts = $derived(workspace?.counterparties ?? []);
  // Sales groups contacts by external company. Fund uses the same visual clue,
  // but keeps it advisory until a person confirms an LP institution. A shared
  // email domain can suggest a label; it never sets institution_id or LP role.
  const organizationNamesByDomain = $derived.by(() => {
    const names = new Map<string, Map<string, number>>();
    for (const contact of allContacts) {
      const domain = contact.suggested_organization_domain;
      const name = contact.suggested_organization_name;
      if (!domain || !name) continue;
      const counts = names.get(domain) || new Map<string, number>();
      counts.set(name, (counts.get(name) || 0) + 1);
      names.set(domain, counts);
    }
    const best = new Map<string, string>();
    for (const [domain, counts] of names) {
      best.set(domain, [...counts].sort((a, b) => b[1] - a[1] || b[0].length - a[0].length)[0][0]);
    }
    return best;
  });
  function organizationHint(contact: FundContact): { name: string; source: string } | null {
    const confirmed = (workspace?.institutions || []).find(i => i.institution_id === contact.institution_id);
    if (confirmed) return { name: confirmed.name, source: 'Confirmed LP institution' };
    if (contact.suggested_organization_name)
      return { name: contact.suggested_organization_name, source: 'Suggested from mail' };
    const domain = contact.suggested_organization_domain;
    const sharedName = domain ? organizationNamesByDomain.get(domain) : null;
    if (sharedName) return { name: sharedName, source: 'Shared email domain · unconfirmed' };
    return domain ? { name: domain, source: 'Email domain only' } : null;
  }
  function relationshipInstitution(contact: FundContact) {
    return (workspace?.institutions || []).find(i => i.institution_id === contact.institution_id);
  }
  function relationshipStage(contact: FundContact): string | null {
    return relationshipInstitution(contact) ? relationshipInstitution(contact)!.stage : contact.stage;
  }
  function relationshipStageSource(contact: FundContact) {
    const institution = relationshipInstitution(contact);
    return institution ? institution.stage_set_by || null : contact.stage_set_by || null;
  }
  function relationshipStageReason(contact: FundContact) {
    const institution = relationshipInstitution(contact);
    return institution ? institution.stage_reason || null : contact.stage_reason || null;
  }
  function relationshipNextStep(contact: FundContact): string | null {
    return relationshipInstitution(contact) ? relationshipInstitution(contact)!.next_step : contact.next_step || null;
  }
  function relationshipDue(contact: FundContact): string | null {
    return relationshipInstitution(contact) ? relationshipInstitution(contact)!.follow_up_on : contact.follow_up_on || null;
  }
  // A group is a display aid, not an LP institution or a confirmed relationship.
  // Personal addresses stay as individual rows; a shared corporate domain alone
  // never writes institution_id or changes anyone's role.
  function groupFundContacts(contacts: FundContact[]) {
    type Group = { key: string; name: string; source: string; contacts: FundContact[]; organization: boolean };
    const groups = new Map<string, Group>();
    for (const contact of contacts) {
      const institution = (workspace?.institutions || []).find(i => i.institution_id === contact.institution_id);
      const domain = contact.suggested_organization_domain;
      const key = institution ? `institution:${institution.institution_id}`
        : domain ? `domain:${domain}` : `person:${contact.counterparty_id}`;
      const hint = organizationHint(contact);
      const existing = groups.get(key);
      if (existing) {
        existing.contacts.push(contact);
        // Prefer an evidenced organization name over a raw domain fallback.
        if (hint && (existing.source === 'Email domain only' ||
            (existing.source === 'Shared email domain · unconfirmed' && hint.source === 'Suggested from mail'))) {
          existing.name = hint.name;
          existing.source = hint.source;
        }
      } else {
        groups.set(key, { key, name: hint?.name || contact.display_name,
          source: hint?.source || '', contacts: [contact], organization: !!institution || !!domain });
      }
    }
    return [...groups.values()].map(group => ({ ...group,
      source: group.organization && group.source !== 'Confirmed LP institution' && !group.source.includes('unconfirmed')
        ? `${group.source} · unconfirmed` : group.source }));
  }
  function groupTodayDecisions(decisions: FundTodayDecision[]) {
    const ids = new Set(decisions.map(decision => decision.counterparty_id));
    return groupFundContacts(allContacts.filter(contact => ids.has(contact.counterparty_id)))
      .map(group => ({ ...group, decisions: decisions.filter(decision =>
        group.contacts.some(contact => contact.counterparty_id === decision.counterparty_id)) }))
      .sort((a, b) => Math.max(...b.decisions.map(decision => mailTime(decision.sent_at)))
        - Math.max(...a.decisions.map(decision => mailTime(decision.sent_at))));
  }
  function groupNextAction(contacts: FundContact[]): FundContact | null {
    return [...contacts].filter(c => !!relationshipNextStep(c)).sort((a, b) =>
      (relationshipDue(a) || '9999-12-31').localeCompare(relationshipDue(b) || '9999-12-31')
      || a.display_name.localeCompare(b.display_name))[0] || null;
  }
  function groupLatestMail(contacts: FundContact[]): FundContact | null {
    return [...contacts].filter(c => !!c.last_seen).sort(mostRecentFirst)[0] || null;
  }
  function groupMailActivity(contacts: FundContact[], direction: 'inbound' | 'outbound'): string | null {
    return contacts.map(c => dashboardMailActivity[c.counterparty_id]?.[direction] || null)
      .filter((stamp): stamp is string => !!stamp)
      .sort((a, b) => mailTime(b) - mailTime(a))[0] || null;
  }
  function activityAgo(stamp: string | null): string {
    if (!stamp || !mailTime(stamp)) return '—';
    const days = Math.max(0, Math.floor((Date.now() - mailTime(stamp)) / 86400000));
    return `${days}d ago`;
  }
  function toggleFundOrganization(key: string) {
    const next = new Set(expandedFundOrganizations);
    if (next.has(key)) next.delete(key);
    else {
      next.add(key);
      if (view === 'dashboard' && dashTab === 'ir') {
        const entity = irSortedEntities.find(item => item.key === key);
        if (entity) void loadEntityTracking(entity);
      }
    }
    expandedFundOrganizations = next;
  }
  async function loadEntityTracking(entity: IrEntity) {
    if (dashboardTracking[entity.key] || dashboardTrackingLoading.has(entity.key)) return;
    dashboardTrackingLoading = new Set([...dashboardTrackingLoading, entity.key]);
    const requests = await Promise.allSettled(entity.contacts.filter(c => c.role === 'investor')
      .map(c => getFundEmailTracking(c.counterparty_id)));
    const messages = requests.flatMap(result => result.status === 'fulfilled' ? result.value.messages : []);
    dashboardTracking = { ...dashboardTracking, [entity.key]: messages.sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) };
    if (requests.some(result => result.status === 'rejected')) {
      dashboardTrackingErrors = { ...dashboardTrackingErrors,
        [entity.key]: 'Some tracked updates could not be loaded.' };
    } else {
      const { [entity.key]: _old, ...otherErrors } = dashboardTrackingErrors;
      dashboardTrackingErrors = otherErrors;
    }
    const next = new Set(dashboardTrackingLoading);
    next.delete(entity.key);
    dashboardTrackingLoading = next;
  }
  function toggleContactOrganization(key: string) {
    if (search.trim()) {
      const next = new Set(collapsedContactSearchGroups);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      collapsedContactSearchGroups = next;
      return;
    }
    const next = new Set(expandedContactOrganizations);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    expandedContactOrganizations = next;
  }
  function displayRole(contact: FundContact): FundRole | null {
    return contact.role || (contact.suggested_source === 'model_review'
      ? contact.suggested_role || null : null);
  }
  const investors = $derived(allContacts.filter(c => c.role === 'investor').sort(mostRecentFirst));
  const todayFocusGroups = $derived(groupFundContacts(investors).sort((a, b) =>
    mailTime(groupLatestMail(b.contacts)?.last_seen) - mailTime(groupLatestMail(a.contacts)?.last_seen)
    || a.name.localeCompare(b.name)));
  const todayFocusGroup = $derived(todayFocusGroups.find(group => group.key === todayFocusKey) || null);
  const todayFocusMembers = $derived((todayFocusGroup?.contacts || []).filter(contact =>
    contact.role === 'investor' && !!contact.email && !contact.automated_address));
  const todayFocusPerson = $derived(todayFocusMembers.length === 1 ? todayFocusMembers[0]
    : todayFocusMembers.find(contact => contact.counterparty_id === todayFocusContactId) || null);
  const dashboardInvestors = $derived(allContacts.filter(c => displayRole(c) === 'investor').sort(mostRecentFirst));
  const respondDecisions = $derived(todayDecisions.filter(d => d.kind === 'respond'));
  const chaseDecisions = $derived(todayDecisions.filter(d => d.kind === 'chase'));
  const respondGroups = $derived(groupTodayDecisions(respondDecisions));
  const nextActions = $derived.by(() => {
    const seenInstitutions = new Set<string>();
    return investors.filter(c => {
      if (!relationshipNextStep(c)) return false;
      if (!c.institution_id) return true;
      if (seenInstitutions.has(c.institution_id)) return false;
      seenInstitutions.add(c.institution_id);
      return true;
    }).sort((a, b) => (relationshipDue(a) || '9999-12-31').localeCompare(relationshipDue(b) || '9999-12-31')
      || a.display_name.localeCompare(b.display_name));
  });
  const dueActions = $derived(nextActions.filter(c =>
    followUpStatus(c) === 'overdue' || followUpStatus(c) === 'soon'));
  const nextActionGroups = $derived(groupFundContacts(nextActions));
  type ChaseItem = { key: string; contact: FundContact | undefined;
    decision: FundTodayDecision | null };
  // A shared outbound message can have several recipients. Chase is a work list,
  // so each recipient gets their own row and their own snooze/dismiss action.
  const chaseItems = $derived<ChaseItem[]>([
    ...dueActions.map(contact => ({ key: `due:${contact.counterparty_id}`, contact, decision: null })),
    ...[...chaseDecisions].sort((a, b) => b.age_days - a.age_days
      || a.display_name.localeCompare(b.display_name)).map(decision => ({
        key: `mail:${decision.counterparty_id}:${decision.message_id}`,
        contact: allContacts.find(c => c.counterparty_id === decision.counterparty_id), decision,
      })),
  ]);
  const newLpSuggestions = $derived(dashboardInvestors.filter(c => !c.role && !c.automated_address));
  const newLpGroups = $derived(groupFundContacts(newLpSuggestions));
  // A relationship is quiet only when the latest synced email across all its
  // known people is old. One quiet person must not label an active LP firm cold.
  const quietGroups = $derived(groupFundContacts(investors)
    .filter(group => {
      const latest = mailTime(groupLatestMail(group.contacts)?.last_seen);
      return latest > 0 && Date.now() - latest >= 30 * 86400000;
    }).sort((a, b) => mailTime(groupLatestMail(a.contacts)?.last_seen)
      - mailTime(groupLatestMail(b.contacts)?.last_seen)));
  const todaySortingGroups = $derived(groupFundContacts(allContacts.filter(contact =>
    !contact.role && !contact.automated_address && displayRole(contact) !== 'investor'))
    .sort((a, b) => mailTime(groupLatestMail(b.contacts)?.last_seen)
      - mailTime(groupLatestMail(a.contacts)?.last_seen)));
  type TodayHighlight = { key: string; name: string; source: string; label: string;
    detail: string; facts: string[]; contactId: string; organization: boolean;
    action: 'follow-up' | 'open'; priority: number; recency: number };
  // Sales Today ranks George's generated notes. Fund has no equivalent background
  // note generator yet, so rank only recorded LP actions and mailbox metadata.
  // The expanded facts explain the ranking without presenting an inferred story
  // as a generated briefing or an obligation to send mail.
  function briefingDisplay(highlight: TodayHighlight) {
    if (highlight.label === 'LP call today')
      return { tone: 'blue', tag: 'Call today', verb: 'Open LP →' };
    if (highlight.label === 'Overdue next step')
      return { tone: 'red', tag: 'Overdue', verb: 'Follow up →' };
    if (highlight.label === 'Inbound mail to review')
      return { tone: 'amber', tag: 'Reply to review', verb: 'Open LP →' };
    if (highlight.action === 'follow-up')
      return { tone: 'blue', tag: 'Next step', verb: 'Follow up →' };
    if (highlight.label === 'Outbound timing check')
      return { tone: 'amber', tag: 'Timing check', verb: 'Open LP →' };
    if (highlight.label === 'Latest team LP mail')
      return { tone: 'blue', tag: 'Mail activity', verb: 'Open LP →' };
    return { tone: 'neutral', tag: 'LP record', verb: 'Open LP →' };
  }
  const todayHighlights = $derived.by(() => {
    const highlights: TodayHighlight[] = [];
    for (const group of todayFocusGroups) {
      const members = group.contacts.filter(contact =>
        contact.role === 'investor' && !!contact.email && !contact.automated_address);
      if (!members.length) continue;
      const memberIds = new Set(members.map(contact => contact.counterparty_id));
      const call = todayCalls.filter(item => item.investors.some(person =>
        memberIds.has(person.counterparty_id))).sort((a, b) =>
          mailTime(a.start) - mailTime(b.start))[0];
      const due = groupNextAction(members.filter(contact =>
        ['overdue', 'soon'].includes(followUpStatus(contact))));
      const inbound = respondDecisions.find(item => memberIds.has(item.counterparty_id));
      const outbound = chaseDecisions.find(item => memberIds.has(item.counterparty_id));
      const latestInbound = groupMailActivity(members, 'inbound');
      const latestOutbound = groupMailActivity(members, 'outbound');
      const latest = groupLatestMail(members)?.last_seen || null;
      const recentMail = Math.max(mailTime(latestInbound), mailTime(latestOutbound), mailTime(latest));
      const facts: string[] = [];
      if (call) facts.push(`Calendar: ${call.summary} · ${new Date(call.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`);
      if (due) facts.push(`Team next step: ${relationshipNextStep(due)} · Due ${emailListDate(relationshipDue(due))}`);
      if (inbound) facts.push(`Your inbox: ${inbound.display_name} sent “${inbound.subject || '(No subject)'}” · ${emailListDate(inbound.sent_at)}`);
      if (outbound) facts.push(`Your inbox: last message to ${outbound.display_name} was ${outbound.age_days} days ago · “${outbound.subject || '(No subject)'}”`);
      if (latestInbound) facts.push(`Latest team inbound: ${emailListDate(latestInbound)}`);
      if (latestOutbound) facts.push(`Latest team outbound: ${emailListDate(latestOutbound)}`);
      if (!latestInbound && !latestOutbound && latest) facts.push(`Last indexed email: ${emailListDate(latest)} · direction unverified`);
      const signal = call
        ? { priority: 100, label: 'LP call today', detail: `Prepare for ${call.summary} at ${new Date(call.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.` }
        : due && followUpStatus(due) === 'overdue'
          ? { priority: 90, label: 'Overdue next step', detail: `${relationshipNextStep(due)} · Due ${emailListDate(relationshipDue(due))}` }
          : inbound
            ? { priority: 80, label: 'Inbound mail to review', detail: `${inbound.display_name}: ${inbound.subject || '(No subject)'} · ${emailListDate(inbound.sent_at)}` }
            : due
              ? { priority: 70, label: 'Recorded next step', detail: `${relationshipNextStep(due)} · Due ${emailListDate(relationshipDue(due))}` }
              : outbound
                ? { priority: 60, label: 'Outbound timing check', detail: `Your last message to ${outbound.display_name} was ${outbound.age_days} days ago. Review the thread before following up.` }
                : recentMail && Date.now() - recentMail <= 14 * 86400000
                  ? { priority: 30, label: 'Latest team LP mail', detail: latestInbound && mailTime(latestInbound) >= mailTime(latestOutbound)
                    ? `Last inbound ${emailListDate(latestInbound)}` : latestOutbound ? `Last outbound ${emailListDate(latestOutbound)}` : `Last indexed email ${emailListDate(latest)}` }
                  : null;
      if (!signal) continue;
      const leadId = call?.investors.find(person => memberIds.has(person.counterparty_id))?.counterparty_id
        || due?.counterparty_id || inbound?.counterparty_id || outbound?.counterparty_id
        || groupLatestMail(members)?.counterparty_id || members[0].counterparty_id;
      highlights.push({ key: group.key, name: group.name, source: group.source,
        label: signal.label, detail: signal.detail, facts, contactId: leadId,
        organization: group.organization, action: due && !call && !inbound ? 'follow-up' : 'open',
        priority: signal.priority, recency: Math.max(recentMail, mailTime(call?.start), mailTime(inbound?.sent_at)) });
    }
    return highlights.sort((a, b) => b.priority - a.priority || b.recency - a.recency
      || a.name.localeCompare(b.name)).slice(0, 5);
  });
  // Wait for the three signal feeds before ranking. Otherwise a recent-mail
  // card briefly appears first and jumps below a call or inbound reply.
  const todaySignalsLoaded = $derived(todayDecisionsLoaded && dashboardMailLoaded && todayCallsLoaded);
  const todayBriefingTotal = $derived(todaySignalsLoaded ? todayHighlights.length : 0);
  /* Fund Today is the sales Today, section for section (designs/today/today-ir). Two
     consequences live here:

     1. EVERY section keeps its header in its own place, empty or not. The page used to
        hide an empty section and collect the names into one "All clear" block, which
        moved a heading the reader had learnt the position of, and made "nothing in
        Respond" and "Respond is still loading" look like the same page. An empty section
        now shows one line under its own header. That is why every `hideEmpty*` flag and
        `clearTodayLabels` is gone rather than merely unused.
     2. A recorded follow-up that is DUE is a Chase row, so there is no separate "Next
        steps" section — `chaseItems` includes it. Follow-ups that are not due yet
        are not Today's business; they live on the dashboard. */
  const chaseTotal = $derived(chaseItems.length);
  /* The stage pill on a Chase recipient. An unconfirmed contact reads "To review" rather
     than "Not set": on this page the question is whether anyone has checked them, and
     that is the same group name the dashboard files them under. */
  function chaseStage(contact: FundContact | undefined) {
    if (!contact || !contact.role) return { label: 'To review', color: 'var(--text-dim)' };
    const stage = relationshipStage(contact);
    return stage
      ? { label: fundStageLabel(stage), color: fundStageColor(stage) }
      : { label: 'No stage', color: 'var(--text-dim)' };
  }
  const todayNavItems = $derived([
    { id: 'fund-briefing', label: 'Briefing', count: todayBriefingTotal },
    { id: 'fund-calls', label: 'Calls', count: todayCalls.length },
    { id: 'fund-respond', label: 'Respond', count: respondGroups.length },
    { id: 'fund-chase', label: 'Chase', count: chaseTotal },
    { id: 'fund-new-lps', label: 'Possible LPs', count: newLpGroups.length },
    { id: 'fund-quiet', label: 'Quiet', count: quietGroups.length },
  ]);
  const todayStatus = $derived.by(() => {
    if (!workspace?.inbox_connected)
      return 'Connect your inbox to bring LP relationships and recorded work into Today.';
    if (!allContacts.length)
      return 'Your inbox is connected. Sync it to discover contacts and build your LP relationship view.';
    const parts = [`${nextActionGroups.length} recorded next step${nextActionGroups.length === 1 ? '' : 's'}`];
    if (todayDecisionsLoaded && !todayDecisionsError) {
      parts.push(`${respondDecisions.length} inbound message${respondDecisions.length === 1 ? '' : 's'} to review`);
      parts.push(`${chaseDecisions.length} outbound timing check${chaseDecisions.length === 1 ? '' : 's'}`);
    }
    if (!todayCallsLoading && !todayCallsError && todayCallsLoaded)
      parts.push(`${todayCalls.length} confirmed LP call${todayCalls.length === 1 ? '' : 's'} today`);
    return `For ${workspace?.profile.manager_name || 'your team'}: ${parts.join(' · ')}. Mail timing is a review signal, not a follow-up obligation.`;
  });
  const pendingSends = $derived(allScheduled.filter(send => send.status === 'pending')
    .sort((a, b) => a.send_at.localeCompare(b.send_at)));
  const portfolio = $derived(allContacts.filter(c => c.role === 'portfolio').sort(mostRecentFirst));
  const neitherContacts = $derived(allContacts.filter(c => displayRole(c) === 'neither'));
  // Suggestions are display hints only. They remain in Review until a human saves a role.
  const unclassified = $derived(allContacts.filter(c => !c.role).sort((a, b) =>
    Number(b.suggested_source === 'company_investor') - Number(a.suggested_source === 'company_investor')
    || mostRecentFirst(a, b)));
  const selected = $derived(allContacts.find(c => c.counterparty_id === contactId) ?? null);
  const selectedOrganization = $derived(contactId.startsWith('org:')
    ? groupFundContacts(allContacts).find(group => group.key === contactId.slice(4)) || null : null);
  const selectedOrganizationRecord = $derived(selectedOrganization?.key.startsWith('institution:')
    ? (workspace?.institutions || []).find(row => row.institution_id === selectedOrganization.key.slice('institution:'.length)) || null : null);
  const groundableDocs = $derived((workspace?.documents || []).filter(doc =>
    doc.size_bytes <= 5 * 1024 * 1024 && /\.(pdf|docx|txt|md|csv)$/i.test(doc.filename)));
  let followUpContactId = '';
  $effect(() => {
    if (!selected || selected.counterparty_id === followUpContactId) return;
    followUpContactId = selected.counterparty_id;
    nextStepDraft = relationshipNextStep(selected) || '';
    followUpDateDraft = relationshipDue(selected) || '';
    followUpEditing = followUpEditorRequested === selected.counterparty_id;
    followUpEditorRequested = '';
  });
  const selectedInstitution = $derived((workspace?.institutions || []).find(i => i.institution_id === selected?.institution_id) || null);
  const institutionMembers = $derived(allContacts.filter(c => c.institution_id && c.institution_id === selected?.institution_id && c.counterparty_id !== selected?.counterparty_id));
  const institutionBriefMembers = $derived(allContacts.filter(c => c.role === 'investor'
    && c.institution_id && c.institution_id === selected?.institution_id && !c.automated_address));

  // ── the structured seven-section briefing (lib/briefing/) ──────────────────
  // The API is injected rather than imported by the store, so `BriefingView`
  // stays page-agnostic: the Sales client page can mount the same component
  // against its own endpoints.
  const briefing = createBriefingStore({
    get: getFundBriefing,
    generate: generateFundBriefing,
    putOverride: putFundBriefingOverride,
    deleteOverride: deleteFundBriefingOverride,
    lock: lockFundBriefing,
    heartbeat: heartbeatFundBriefingLock,
    release: releaseFundBriefingLock,
  });
  let briefingNotice = $state('');
  // CLAUDE.md: only a user-confirmed LP may have a model-generated brief, and an
  // automated address is never a person to brief on. The same gate the
  // relationship-context effect uses — without it the tab does not exist.
  const briefingAllowed = $derived(
    !!selected && selected.role === 'investor' && !selected.automated_address);
  // Only fall back after a person is selected. This component also stays mounted
  // on Today, where `selected` is null; changing tabs there would make a later
  // sidebar click open an LP on Emails instead of the default Briefing.
  $effect(() => {
    if (view !== 'detail' || loading || !selected) return;
    if (detailTab === 'briefing' && !briefingAllowed) detailTab = 'emails';
  });
  // Every workspace reload replaces `selected` with an equal copy, and loading
  // the briefing again on that blanks it mid-read or mid-edit, so only a change
  // of LP (or leaving and returning) loads it. Not reactive on purpose.
  let briefingLoadedFor = '';
  $effect(() => {
    const target = view === 'detail' && briefingAllowed ? (selected?.counterparty_id ?? '') : '';
    untrack(() => {
      if (target === briefingLoadedFor) return;
      briefingLoadedFor = target;
      if (!target) return;
      briefingNotice = '';
      briefing.load(target);
    });
  });
  // Poll and heartbeat timers outlive the component otherwise.
  $effect(() => () => briefing.destroy());
  // A closed tab cannot run the view's teardown; release the edit lock on the way out.
  $effect(() => {
    const release = () => briefing.releaseOnUnload();
    window.addEventListener('pagehide', release);
    return () => window.removeEventListener('pagehide', release);
  });

  // Dev only: the three primary contacts from seed-fund-ui-demo.sh each have a
  // distinct synthetic briefing. The production bundle excludes the loader.
  let briefingFixture = $state<Briefing | null>(null);
  let briefingFixtureNotice = $state('');
  const sandboxDemoIds = new Set(['ui-demo-elliot', 'ui-demo-david', 'ui-demo-lena']);
  const sandboxDemoAvailable = $derived(import.meta.env.DEV
    && $userEmailStore === 'ui@jojo-ui.test'
    && !!selected && sandboxDemoIds.has(selected.counterparty_id));
  const visibleBriefingFixture = $derived(briefingFixture?.counterparty_id === selected?.counterparty_id
    ? briefingFixture : null);
  async function loadSandboxDemo(id: string) {
    // Keep the import inside this direct compile-time guard: Vite must omit
    // both the fixture and its text from dist-stage and live builds.
    if (import.meta.env.DEV) {
      const { sandboxDemoBriefing, sandboxDemoNotice } = await import('../lib/briefing/sandbox-demo');
      if (selected?.counterparty_id === id) {
        briefingFixture = sandboxDemoBriefing(id);
        briefingFixtureNotice = sandboxDemoNotice;
      }
    }
  }
  $effect(() => {
    const id = selected?.counterparty_id;
    if (!sandboxDemoAvailable || !id) return;
    void loadSandboxDemo(id);
  });

  /** A footnote on an email source opens that message in the Emails tab. Only
   *  email sources reach here — this page has nowhere to send a meeting. */
  function openBriefingSource(source: BriefingSource) {
    if (!selected || !source.message_id) return;
    const owner = source.owner_email || $userEmailStore;
    const key = `${selected.counterparty_id}:${owner}:${source.message_id}`;
    detailTab = 'emails';
    // A message renders only inside its expanded thread, and a search can hide that thread.
    emailQuery = '';
    const thread = emailThreads.find(item => item.messages.some(message =>
      message.message_id === source.message_id && message.owner_email === owner));
    if (thread) {
      expandedEmailThreads = new Set(expandedEmailThreads).add(thread.key);
      activeEmailThreadKey = thread.key;
    }
    // toggleMessage closes an open message, so only call it to open one.
    if (!openedMessages.has(key)) void toggleMessage(selected.counterparty_id, owner, source.message_id);
    requestAnimationFrame(() => document.getElementById(`fund-msg-${key}`)
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }));
  }
  // A confirmed institution owns the relationship view; each email still belongs
  // to its actual contact and synced mailbox. Unconfirmed domain hints never widen it.
  const relationshipEmails = $derived(selectedInstitution
    ? institutionActivity
    : (contactContext?.messages || []).map(message => ({ ...message,
        counterparty_id: selected?.counterparty_id || '',
        display_name: selected?.display_name || '',
        email: selected?.email || '' })));
  const visibleRelationshipEmails = $derived(relationshipEmails.filter(message =>
    !emailQuery.trim() || `${message.subject} ${message.sender} ${message.display_name}`
      .toLowerCase().includes(emailQuery.trim().toLowerCase())));
  const emailThreads = $derived.by(() => {
    type Message = (typeof relationshipEmails)[number];
    const groups = new Map<string, { key: string; subject: string; sent_at: string; messages: Message[] }>();
    const seen = new Set<string>();
    for (const message of relationshipEmails) {
      // A provider thread ID is scoped to the connected mailbox. Old evidence
      // without one stays as a single message; subject equality is not proof.
      const key = message.thread_id
        ? `${message.owner_email}:thread:${message.thread_id}`
        : `${message.owner_email}:message:${message.message_id}`;
      const evidenceKey = `${message.owner_email}:${message.message_id}`;
      if (seen.has(evidenceKey)) continue;
      seen.add(evidenceKey);
      const group = groups.get(key);
      if (group) group.messages.push(message);
      else groups.set(key, { key, subject: message.subject || '(No subject)',
        sent_at: message.sent_at, messages: [message] });
    }
    return [...groups.values()];
  });
  function emailListDate(value: string | null | undefined): string {
    if (!value) return '—';
    const date = /^\d{13}$/.test(value) ? new Date(Number(value)) : parseDay(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, {
      month: 'short', day: 'numeric',
      ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
    });
  }
  /* Direction comes off the headers the way `state_db.py` derives mail activity:
     exactly one From address, matched against the synced mailbox and the LP's own
     address. An ambiguous sender gets no label rather than a guess. */
  function senderAddress(raw: string): string {
    const found = (raw || '').match(/[^\s<>,;"]+@[^\s<>,;"]+/g) || [];
    return found.length === 1 ? found[0].toLowerCase() : '';
  }
  function messageDirection(message: { sender: string; owner_email: string; email?: string }): string {
    const address = senderAddress(message.sender);
    if (!address) return '';
    if (address === (message.owner_email || '').toLowerCase()) return 'outbound';
    if (address === (message.email || '').toLowerCase()) return 'inbound';
    return '';
  }
  /** One line of the already-synced excerpt — a collapsed row costs no request. */
  function messageSnippet(excerpt: string | undefined): string {
    return repairLostApostrophes(stripCids(excerpt || ''))
      .replace(/[#*>`_~-]/g, '').replace(/\s+/g, ' ').trim().slice(0, 140);
  }
  /* What a thread-level Respond answers: the newest message that is both this LP's
     and in the viewer's own mailbox, because that is what the provider threads on.
     `relationshipEmails` is newest-first, so the first match is the newest. */
  function threadReplyTarget(thread: { messages: (typeof relationshipEmails)[number][] }) {
    return thread.messages.find(message => message.counterparty_id === selected?.counterparty_id
      && message.owner_email.toLowerCase() === $userEmailStore.toLowerCase());
  }
  const replyThreadAvailable = $derived(!!composeReplyMessageId && emailThreads.some(thread =>
    thread.messages.some(message => message.message_id === composeReplyMessageId
      && message.owner_email.toLowerCase() === $userEmailStore.toLowerCase())));
  const visibleEmailThreads = $derived(emailThreads.filter(thread => !emailQuery.trim()
    || thread.messages.some(message => visibleRelationshipEmails.includes(message))));
  function replyDecisionForThread(thread: (typeof emailThreads)[number]) {
    return respondDecisions.find(decision => thread.messages.some(message =>
      message.counterparty_id === decision.counterparty_id
      && message.message_id === decision.message_id
      && message.owner_email.toLowerCase() === $userEmailStore.toLowerCase()));
  }
  const orderedEmailThreads = $derived([...visibleEmailThreads].sort((a, b) =>
    Number(!!replyDecisionForThread(b)) - Number(!!replyDecisionForThread(a))));
  const activeEmailThread = $derived(visibleEmailThreads.find(thread => thread.key === activeEmailThreadKey)
    || (visibleEmailThreads.length === 1 ? visibleEmailThreads[0] : null));
  const mikeContextLabel = $derived(detailTab === 'briefing' ? `Reading: ${readingLabel}`
    : detailTab === 'emails' && activeEmailThread
      && activeEmailThread.messages.some(message => message.counterparty_id === selected?.counterparty_id)
      ? `Looking at: ${activeEmailThread.subject.replace(/^\[Synthetic sandbox\]\s*/i, '')} (thread)`
      : `Looking at: ${detailTab === 'assistant' ? 'this relationship' : detailTab[0].toUpperCase() + detailTab.slice(1)}`);
  function toggleEmailThread(thread: (typeof emailThreads)[number]) {
    const next = new Set(expandedEmailThreads);
    if (next.has(thread.key)) { next.delete(thread.key); activeEmailThreadKey = ''; }
    else {
      next.add(thread.key);
      activeEmailThreadKey = thread.key;
      const message = thread.messages[0];
      if (message && !openedMessages.has(`${message.counterparty_id}:${message.owner_email}:${message.message_id}`))
        toggleMessage(message.counterparty_id, message.owner_email, message.message_id);
    }
    expandedEmailThreads = next;
  }
  /* Who on our side has actually touched this LP. No ownership is stored, so this is
     read back off the records — the mailboxes the synced messages came from, plus
     whoever confirmed the relationship — rather than claimed as an account owner. */
  /* The people on this LP entity. An LP is an organization holding 1..n people; a
     confirmed `fund_institutions` grouping wins, and failing that colleagues are read
     off the shared corporate domain and must say so — a domain is a display hint, never
     a confirmed institution (Sigma Lithium alone spans three registrable domains).
     A personal-email contact has no `suggested_organization_domain` at all — the backend
     leaves it null on consumer domains — so this is empty and they stand alone. */
  const lpColleagues = $derived.by(() => {
    if (!selected) return [] as { contact: FundContact; source: string }[];
    if (selected.institution_id)
      return institutionMembers.map(contact => ({ contact, source: 'Confirmed LP institution' }));
    const domain = selected.suggested_organization_domain;
    if (!domain) return [];
    return allContacts
      .filter(c => c.counterparty_id !== selected.counterparty_id
        && c.suggested_organization_domain === domain)
      .map(contact => ({ contact, source: 'Shared email domain · unconfirmed' }));
  });
  const lpIsIndividual = $derived(!!selected && !selected.institution_id
    && !selected.suggested_organization_domain);
  /* "no reply yet" from the synced mail itself — a message whose sender carries their
     address. Only the open contact's evidence is loaded, so colleagues get no such claim
     rather than a guessed one. `null` means we cannot say. */
  const selectedHasReplied = $derived.by(() => {
    const messages = contactContext?.messages || [];
    const address = (selected?.email || '').toLowerCase();
    if (!address || !messages.length) return null;
    return messages.some(message => (message.sender || '').toLowerCase().includes(address));
  });
  /* Memory is read back off records only. Every line names the record it came from,
     and a gap is the absence of a record — never a guess about the LP. Nothing here
     calls the model, so it is correct before any brief has been generated. */
  /* `fix` is the spec's per-fact "Correct". These facts are derived from records rather
     than written by the model, so correcting one means editing the record it came from —
     the button goes to that control instead of opening a free-text override. Mailbox
     truth (the address, what was synced, whether they replied) has no fix: it is not
     ours to edit. */
  const lpKnown = $derived.by(() => {
    const facts: { text: string; src: string; fix?: { label: string; run: () => void } }[] = [];
    if (!selected) return facts;
    if (selected.email) facts.push({ text: `Reachable at ${selected.email}.`, src: 'Their email address' });
    const messages = contactContext?.messages || [];
    if (messages.length) {
      const dates = messages.map(m => m.sent_at).filter(Boolean).sort();
      facts.push({
        text: `${messages.length} synced message${messages.length === 1 ? '' : 's'}`
          + (dates.length ? `, ${mailDate(dates[0])} to ${mailDate(dates[dates.length - 1])}.` : '.'),
        src: 'Synced email',
      });
      const address = (selected.email || '').toLowerCase();
      const replied = !!address && messages.some(m => (m.sender || '').toLowerCase().includes(address));
      facts.push({
        text: replied ? 'Has replied at least once.' : 'Has not replied in the synced messages.',
        src: 'Synced email',
      });
    }
    if (selected.role) facts.push({
      text: selected.set_by === 'model'
        ? `Filed as ${roleLabel(selected.role)} from synced mail.`
        : `Confirmed as ${roleLabel(selected.role)}.`,
      src: selected.set_by === 'model' ? 'Mike classified this relationship'
        : selected.confirmed_by ? `Confirmed by ${selected.confirmed_by}` : 'Confirmed by your team',
      fix: { label: 'Change role', run: () => { moreOpen = true; } } });
    if (selectedInstitution) facts.push({ text: `Grouped under ${selectedInstitution.name}.`,
      src: 'Grouped by your team',
      fix: { label: 'Regroup', run: () => { detailTab = 'contacts'; } } });
    if (relationshipStage(selected)) facts.push({ text: `Stage is ${fundStageLabel(relationshipStage(selected))}.`,
      src: 'Set by your team',
      fix: { label: 'Change stage', run: () => { moreOpen = true; } } });
    if (relationshipNextStep(selected)) facts.push({ text: `Next step: ${relationshipNextStep(selected)}`,
      src: 'Set by your team',
      fix: { label: 'Edit', run: () => openFollowUpEditor() } });
    return facts;
  });
  const lpUnknown = $derived.by(() => {
    const gaps: string[] = [];
    if (!selected) return gaps;
    if (!(contactContext?.messages || []).length) gaps.push('Any synced email with this LP');
    if (!selected.role) gaps.push('Whether this is an LP, a portfolio contact, or neither');
    if (!selected.institution_id) gaps.push('Whether they invest as an individual or through an institution');
    if (!relationshipStage(selected)) gaps.push('Relationship stage');
    if (!relationshipNextStep(selected)) gaps.push('The next step you intend to take');
    if (!(contactContext?.notes || []).length) gaps.push('Team notes');
    return gaps;
  });
  const teamOnLp = $derived.by(() => {
    const roles = new Map<string, Set<string>>();
    const note = (email: string, text: string) => {
      const key = email.trim().toLowerCase();
      if (!key) return;
      if (!roles.has(key)) roles.set(key, new Set());
      roles.get(key)!.add(text);
    };
    for (const message of contactContext?.messages || []) note(message.owner_email, 'Synced this thread');
    note(selected?.confirmed_by || '', 'Confirmed the relationship');
    return [...roles.entries()].map(([email, texts]) => ({ email, notes: [...texts].join(' · ') }));
  });
  /* CC candidates: the other people on this LP entity, plus the teammates who already
     have mail with it. The recipient stays locked to the confirmed LP — only CC is picked
     here, it only ever appends to what you typed, and the server re-validates every
     address before the send ledger sees it. */
  const unique = (addresses: string[], exclude: string[] = []) => addresses.filter(
    (address, index, all) => !!address && address !== selected?.email
      && !exclude.includes(address) && all.indexOf(address) === index);
  const ccLpContacts = $derived(unique(lpColleagues.map(entry => entry.contact.email || '')));
  const ccTeam = $derived(unique(teamOnLp.map(person => person.email), ccLpContacts));
  const ccSuggestions = $derived([...ccLpContacts, ...ccTeam]);
  function toggleComposeDocument(documentId: string) {
    composeDocumentIds = composeDocumentIds.includes(documentId)
      ? composeDocumentIds.filter(id => id !== documentId)
      : [...composeDocumentIds, documentId];
  }
  /* The picker appends: choosing a second time in a `<input type="file">` replaces its
     own FileList, which silently dropped everything attached before it. */
  function addComposeFiles(event: Event & { currentTarget: HTMLInputElement }) {
    const picked = Array.from(event.currentTarget.files || []);
    const known = new Set(composeFiles.map(file => `${file.name}:${file.size}`));
    composeFiles = [...composeFiles,
      ...picked.filter(file => !known.has(`${file.name}:${file.size}`))];
    event.currentTarget.value = '';
  }
  function removeComposeFile(index: number) {
    composeFiles = composeFiles.filter((_, position) => position !== index);
  }
  const ccAlreadyIncluded = (address: string) =>
    composeCc.toLowerCase().split(/[,;]/).map(part => part.trim()).includes(address.toLowerCase());
  function addCc(address: string) {
    if (ccAlreadyIncluded(address)) return;
    const current = composeCc.trim().replace(/,$/, '');
    composeCc = current ? `${current}, ${address}` : address;
  }
  $effect(() => {
    const id = selectedInstitution?.institution_id;
    if (view !== 'detail' || detailTab !== 'contacts' || !id) {
      institutionBrief = null;
      return;
    }
    institutionBrief = null;
    institutionBriefContacts = [];
    institutionBriefDocuments = [];
    institutionBriefError = '';
    getFundInstitutionBrief(id).then(data => {
      if (selected?.institution_id === id) institutionBrief = data;
    }).catch((err: any) => {
      if (selected?.institution_id === id) institutionBriefError = err?.message || 'Could not load the institution brief.';
    });
  });
  $effect(() => {
    const id = selectedInstitution?.institution_id || selectedOrganizationRecord?.institution_id;
    if (view !== 'detail' || (!selectedOrganizationRecord && detailTab !== 'contacts' && detailTab !== 'emails') || !id) {
      institutionActivity = [];
      institutionActivityError = '';
      return;
    }
    institutionActivity = [];
    institutionActivityError = '';
    institutionActivityLoading = true;
    getFundInstitutionActivity(id).then(result => {
      if (selected?.institution_id === id || selectedOrganizationRecord?.institution_id === id) institutionActivity = result.messages;
    }).catch((err: any) => {
      if (selected?.institution_id === id || selectedOrganizationRecord?.institution_id === id) institutionActivityError = err?.message || 'Could not load institution activity.';
    }).finally(() => {
      if (selected?.institution_id === id || selectedOrganizationRecord?.institution_id === id) institutionActivityLoading = false;
    });
  });
  const shown = $derived(allContacts.filter(c => {
    // A model suggestion is still unconfirmed and must stay in the review list.
    if (roleFilter === 'unclassified' && c.role) return false;
    if (roleFilter !== 'all' && roleFilter !== 'unclassified' && displayRole(c) !== roleFilter) return false;
    const term = search.trim().toLowerCase();
    return !term || `${c.display_name} ${c.email ?? ''} ${organizationHint(c)?.name ?? ''} ${c.suggested_reason ?? ''}`.toLowerCase().includes(term);
  }).sort(mostRecentFirst));
  const contactDirectoryGroups = $derived(groupFundContacts(shown));
  const contactOrganizations = $derived(contactDirectoryGroups.filter(group => group.organization)
    .sort((a, b) => a.name.localeCompare(b.name)));
  const ungroupedDirectoryContacts = $derived(contactDirectoryGroups.filter(group => !group.organization)
    .flatMap(group => group.contacts));
  const individualContacts = $derived(ungroupedDirectoryContacts.filter(c => c.personal_email_domain)
    .sort((a, b) => a.display_name.localeCompare(b.display_name)));
  const otherContacts = $derived(ungroupedDirectoryContacts.filter(c => !c.personal_email_domain)
    .sort((a, b) => a.display_name.localeCompare(b.display_name)));
  const activeContactTab = $derived(contactTab === 'auto'
    ? contactOrganizations.length ? 'organizations'
      : individualContacts.length ? 'individuals' : 'ungrouped'
    : contactTab);
  const visibleContactCount = $derived(activeContactTab === 'organizations'
    ? contactOrganizations.reduce((total, group) => total + group.contacts.length, 0)
    : activeContactTab === 'individuals' ? individualContacts.length : otherContacts.length);
  const shownScheduled = $derived(allScheduled.filter(item => scheduledFilter === 'all'
    || item.status === scheduledFilter
    || (scheduledFilter === 'attention' && (item.status === 'uncertain' || item.status === 'claimed'))));

  /* Relationships filters on search alone — the view tabs do the rest. `shown` stays the
     Contacts page's list, which keeps the full Role filter including Neither. */
  const searchTerm = $derived(search.trim().toLowerCase());
  /* Takes the term as an argument so the saved-view dropdown can count what each view
     would show — it needs to match against a stored term, not the one in the box. */
  const matchesSearch = (c: FundContact, term: string) => !term
    || `${c.display_name} ${c.email ?? ''} ${organizationHint(c)?.name ?? ''} ${c.suggested_reason ?? ''}`.toLowerCase().includes(term);
  const bySearch = (c: FundContact) => matchesSearch(c, searchTerm);
  function localDay(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }
  function followUpStatus(contact: FundContact): 'overdue' | 'soon' | 'later' | 'undated' | 'none' {
    if (!relationshipNextStep(contact)) return 'none';
    if (!relationshipDue(contact)) return 'undated';
    const today = localDay();
    if (relationshipDue(contact)! < today) return 'overdue';
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    const soon = `${nextWeek.getFullYear()}-${String(nextWeek.getMonth() + 1).padStart(2, '0')}-${String(nextWeek.getDate()).padStart(2, '0')}`;
    return relationshipDue(contact)! <= soon ? 'soon' : 'later';
  }
  const isPinned = (id: string) => $fundPins.some(pin => pin.counterparty_id === id);
  const isOrganizationPinned = (key: string) => $fundPins.some(pin => pin.entity_key === key);
  function entityKeyForContact(contact: FundContact): string | null {
    return contact.institution_id ? `institution:${contact.institution_id}`
      : contact.suggested_organization_domain ? `domain:${contact.suggested_organization_domain}` : null;
  }
  /* DESIGN-SYSTEM §4/§6: a read-only outline pill, and only when the contact has
     both a next step and a due date. Anything else renders an em dash — the page
     never guesses a status from mail activity alone. */
  function statusPill(contact: FundContact): { label: string; cls: string } | null {
    const status = followUpStatus(contact);
    if (status === 'overdue') return { label: 'Overdue', cls: 'fund-ir-overdue' };
    if (status === 'soon') return { label: 'Soon', cls: 'status-pill-amber' };
    if (status === 'later') return { label: 'On track', cls: 'status-pill-green' };
    return null;
  }
  function toggleIrFilter<T extends string>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter(entry => entry !== value) : [...list, value];
  }
  function clearIrFilters() {
    irStatusFilter = []; irStageFilter = []; irInstitutionFilter = '';
    irPinnedOnly = false; search = ''; openFilterDD = null;
  }
  function openInvestorReview() {
    dashTab = 'sorting';
    search = '';
    reviewCursor = reviewGroups[0]?.rows[0]?.counterparty_id || '';
    $currentView = 'dashboard';
  }
  /* Only plausible Fund relationships needing final confirmation enter Review. */
  const isUnchecked = (c: FundContact) => !c.role && c.suggested_source === 'model_review';
  function toggleIrStageGroup(key: string) {
    const next = new Set(expandedIrStages);
    if (next.has(key)) next.delete(key); else next.add(key);
    expandedIrStages = next;
  }
  const irFiltersSet = $derived(irStatusFilter.length > 0 || irStageFilter.length > 0
    || !!irInstitutionFilter || irPinnedOnly || !!searchTerm);
  const irUnchecked = $derived(dashboardInvestors.filter(isUnchecked));
  /* Confirmed investors with no stage use Not set. Suggestions stay in Review. */
  const IR_STAGE_OPTIONS = [...FUND_STAGES.map(s => ({ key: s.key, label: s.label })),
                            { key: '', label: 'Not set' }];
  const irStageLbl = $derived(!irStageFilter.length ? 'All'
    : irStageFilter.length === 1
      ? IR_STAGE_OPTIONS.find(option => option.key === irStageFilter[0])?.label || 'All'
      : `${irStageFilter.length} selected`);
  const irStatusLbl = $derived(!irStatusFilter.length ? 'All'
    : irStatusFilter.length === 1
      ? (IR_STATUS_OPTIONS.find(o => o.key === irStatusFilter[0])?.label ?? 'All')
      : `${irStatusFilter.length} selected`);
  const irInstitutionLbl = $derived(!irInstitutionFilter ? 'All'
    : (workspace?.institutions || []).find(i => i.institution_id === irInstitutionFilter)?.name || 'All');
  // Clicking anywhere outside an open chip or the ⋯ menu closes it, same as the Sales dashboard.
  function onWindowClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (openFilterDD && !target?.closest?.('.fchip-wrap')) openFilterDD = null;
    if (openStageDD && !target?.closest?.('.fund-ir-stage-dd')) openStageDD = null;
    if (moreOpen && !target?.closest?.('.lp-more')) moreOpen = false;
    if (viewDropdownOpen && !target?.closest?.('.dash-view-bar')) {
      // Drop the in-place rename with the menu, or reopening shows a stray edit box.
      viewDropdownOpen = false; editingViewKey = null;
    }
  }
  function toggleStageMenu(key: string, button: HTMLButtonElement) {
    if (openStageDD === key) { openStageDD = null; return; }
    const rect = button.getBoundingClientRect();
    stageMenuPosition = {
      top: Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - 300)),
      left: Math.max(8, Math.min(rect.right - 240, window.innerWidth - 248)),
    };
    openStageDD = key;
  }
  /** The inline rename starts with the old name selected, so typing replaces it. */
  function focusSelect(node: HTMLInputElement) { node.focus(); node.select(); }
  /** The next-step line and the ⋯ entry open the same editor, seeded from the record. */
  function openFollowUpEditor() {
    nextStepDraft = selected ? relationshipNextStep(selected) || '' : '';
    followUpDateDraft = selected ? relationshipDue(selected) || '' : '';
    followUpEditing = true;
  }
  /* Chip counts are taken over the search result rather than the fully filtered
     rows: a count that shrank as you ticked a sibling chip reads as rows vanishing. */
  const irSearched = $derived(investors.filter(bySearch));
  const stageRank = (stage: string | null) => {
    const index = FUND_STAGES.findIndex(option => option.key === stage);
    return index < 0 ? FUND_STAGES.length : index;
  };
  /* ONE predicate for "does this contact pass the IR filters". The table reads it with
     the live chips; the saved-view dropdown reads it with each view's stored chips to
     count what that view would show. A second copy of this is exactly how a count goes
     stale the next time someone adds a chip. */
  type IrFilters = { term: string; status: IrStatus[]; institution: string;
    stage: string[]; pinned: boolean };
  function irMatches(c: FundContact, f: IrFilters): boolean {
    return matchesSearch(c, f.term)
      && (!f.status.length || f.status.includes(followUpStatus(c)))
      && (!f.institution || c.institution_id === f.institution)
      && (!f.stage.length || f.stage.includes(relationshipStage(c) || ''))
      && (!f.pinned || isPinned(c.counterparty_id)
        || (!!entityKeyForContact(c) && isOrganizationPinned(entityKeyForContact(c)!)));
  }
  const irCurrentFilters = $derived<IrFilters>({ term: searchTerm, status: irStatusFilter,
    institution: irInstitutionFilter, stage: irStageFilter, pinned: irPinnedOnly });
  /* Contact order inside an entity, not row order: the rows are entities and they are
     sorted by `compareIrEntities` after grouping. */
  const irRows = $derived(investors.filter(c => irMatches(c, irCurrentFilters))
    .sort((a, b) => (relationshipDue(a) || '9999-12-31').localeCompare(relationshipDue(b) || '9999-12-31')
      || a.display_name.localeCompare(b.display_name)));
  // The Dashboard counts and renders relationship entities. Stage remains a
  // contact-level filter until there is a persisted institution-level stage;
  // grouping an institution under one person's stage would misstate its state.
  const irEntities = $derived(groupFundContacts(irRows));
  const allIrEntities = $derived(groupFundContacts(investors));
  function entityContactStage(contacts: FundContact[]): string {
    const stages = new Set(contacts.map(c => relationshipStage(c) || ''));
    return stages.size === 1 ? [...stages][0] : '__mixed__';
  }
  /* Sorting runs over ENTITIES, not the contacts underneath them. Every sortable cell
     shows an entity-level value — the soonest recorded action across its contacts, the
     most recent synced mail in either direction — so sorting the contacts and letting
     grouping order fall out of Map insertion would order rows by numbers the row does
     not display. `asc` always means "the order the column reads in": soonest date, most
     urgent status, most recent mail. Rows with nothing recorded sort last in `asc`,
     never first, because an empty cell is not the top of any ranking. */
  type IrEntity = ReturnType<typeof groupFundContacts>[number];
  const IR_STATUS_RANK: Record<IrStatus, number> = {
    overdue: 0, soon: 1, later: 2, undated: 3, none: 4,
  };
  const entityAction = (entity: IrEntity) => groupNextAction(entity.contacts);
  function entityStatusRank(entity: IrEntity): number {
    const action = entityAction(entity);
    return action ? IR_STATUS_RANK[followUpStatus(action)] : IR_STATUS_RANK.none;
  }
  function entityDue(entity: IrEntity): string {
    const action = entityAction(entity);
    return (action && relationshipDue(action)) || '9999-12-31';
  }
  function entityMail(entity: IrEntity, key: typeof irSort): number {
    const stamp = key === 'last_email'
      ? groupLatestMail(entity.contacts)?.last_seen || null
      : groupMailActivity(entity.contacts, key === 'outbound' ? 'outbound' : 'inbound');
    return stamp ? mailTime(stamp) : 0;
  }
  function compareIrEntities(a: IrEntity, b: IrEntity): number {
    const byName = a.name.localeCompare(b.name);
    // A bigger timestamp is a SMALLER "Nd ago", and a missing one is 0, so
    // descending time is ascending age and puts never-synced rows last.
    const ranked = irSort === 'name' ? byName
      : irSort === 'status' ? entityStatusRank(a) - entityStatusRank(b) || byName
      : irSort === 'stage' ? stageRank(entityContactStage(a.contacts) || null)
          - stageRank(entityContactStage(b.contacts) || null) || byName
      : irSort === 'due' ? entityDue(a).localeCompare(entityDue(b)) || byName
      : entityMail(b, irSort) - entityMail(a, irSort) || byName;
    return irSortDir === 'desc' ? -ranked : ranked;
  }
  const irSortedEntities = $derived([...irEntities].sort(compareIrEntities));
  /* The row ★ pins the ENTITY: an organization goes to `fund_org_pins`, a standalone
     person to `fund_pins`. A person can only be pinned once a human has confirmed the
     role — `add_fund_pin` selects on `role = 'investor'`, so offering the star earlier
     would be a control that silently does nothing. Rows that cannot be pinned render an
     empty cell rather than a dead star. */
  const entityPinContact = (entity: IrEntity) =>
    (!entity.organization && entity.contacts[0]?.role === 'investor'
      && !entity.contacts[0]?.automated_address) ? entity.contacts[0] : null;
  const entityPinnable = (entity: IrEntity) => entity.organization || !!entityPinContact(entity);
  const entityPinned = (entity: IrEntity) => {
    if (entity.organization) return isOrganizationPinned(entity.key);
    const contact = entityPinContact(entity);
    return !!contact && isPinned(contact.counterparty_id);
  };
  function toggleEntityPin(entity: IrEntity) {
    if (entity.organization) { toggleOrganizationPin(entity.key); return; }
    const contact = entityPinContact(entity);
    if (contact) togglePin(contact.counterparty_id);
  }
  /* Which record a stage write lands on. An institution owns its members' stage; a
     standalone person owns their own. A domain-grouped entity owns nothing yet —
     `saveStage` refuses it, so the rail shows the reason instead of a dead select. */
  const entityStageContact = (entity: IrEntity) =>
    entity.contacts.find(c => !!c.institution_id && c.role === 'investor')
    || entity.contacts.find(c => !!c.institution_id)
    || (entity.organization ? null : entity.contacts[0] || null);
  /* The Dashboard currently passes filed LPs. Keep the confirmation guard
     for callers that may pass an unconfirmed contact, and let the backend enforce the
     confirmed role again before brief, draft, or send. */
  async function setEntityStage(entity: IrEntity, stage: string) {
    const target = entityStageContact(entity);
    if (!target || savingStage) return;
    openStageDD = null;
    const needsConfirmation = !target.role;
    if (needsConfirmation && !target.email) {
      actionError = 'This contact needs an email address before confirming the LP relationship.';
      return;
    }
    savingStage = true; actionError = '';
    try {
      // Role first: a stage written onto a row that then fails to confirm would read as
      // a staged LP nobody approved.
      if (needsConfirmation) {
        await confirmFundRole({ email: target.email!, name: target.display_name }, 'investor');
      }
      if (target.institution_id) await setFundInstitutionStage(target.institution_id, stage || null);
      else await setFundStage(target.counterparty_id, stage || null);
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not save this relationship stage.';
      await load();
    } finally { savingStage = false; }
  }
  async function resumeEntityAutoStage(entity: IrEntity) {
    const target = entityStageContact(entity);
    if (!target || savingStage) return;
    savingStage = true; actionError = '';
    try {
      if (target.institution_id) await resumeFundInstitutionAutoStage(target.institution_id);
      else await resumeFundAutoStage(target.counterparty_id);
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not resume automatic stage updates.';
    } finally { savingStage = false; }
  }
  /* The rail's date field and the LP page's editor are two entry points on ONE write
     path (spec v3). `next_step` is passed through untouched — a date-only save must not
     blank the text someone wrote. */
  async function setEntityFollowUpDate(entity: IrEntity, date: string) {
    const target = entityStageContact(entity);
    if (!target || savingFollowUp) return;
    savingFollowUp = true; actionError = '';
    try {
      const step = relationshipNextStep(target) || '';
      if (target.institution_id) await setFundInstitutionFollowUp(target.institution_id, step, date || null);
      else await setFundFollowUp(target.counterparty_id, step, date || null);
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not save this follow-up date.';
      await load();
    } finally { savingFollowUp = false; }
  }
  const irGroups = $derived([...FUND_STAGES.map(s => s.key), '', '__mixed__']
    .map(key => ({
      key,
      label: key === '__mixed__' ? 'Mixed contact stages'
        : key ? fundStageLabel(key) : 'Not set',
      color: key === '__mixed__' || !key ? 'var(--text-dim)'
        : key === 'commitment_in_progress' || key === 'due_diligence'
          ? 'var(--accent)' : fundStageColor(key),
      rows: irSortedEntities.filter(entity => entityContactStage(entity.contacts) === key),
    })).filter(group => group.rows.length));
  /* One click sorts by a column; clicking the active one flips it. The design marks the
     sorted header with a filled arrow — `.th-sort` / `.sort-ind` are the shared Sales
     affordance in app.css, not a second vocabulary for this page. */
  function sortIr(key: typeof irSort) {
    if (irSort === key) irSortDir = irSortDir === 'asc' ? 'desc' : 'asc';
    else { irSort = key; irSortDir = 'asc'; }
  }
  const irSortIndicator = (key: typeof irSort) =>
    irSort !== key ? '▼' : irSortDir === 'asc' ? '▲' : '▼';
  /* The spec's note under the table. It states what the scan is actually doing, because
     with stage groups the first row is not the top of the sort — it is the top of its
     group, and that has read as a broken sort before. Same glyph as the live header, so
     the two cannot disagree. */
  const IR_SORT_LABELS: Record<typeof irSort, string> = { name: 'LP', status: 'Status',
    due: 'Follow-up', inbound: 'Inbound', outbound: 'Outbound',
    last_email: 'Last email', stage: 'Stage' };
  const irScanNote = $derived(`Showing ${irEntities.length} of ${allIrEntities.length}`
    + ` · grouped by stage, sorted within each group by ${IR_SORT_LABELS[irSort]}`
    + ` ${irSortIndicator(irSort)}`);
  const portfolioRows = $derived(portfolio.filter(bySearch));
  const unreviewedPeople = $derived(unclassified.filter(c => !c.automated_address
    && (c.suggested_source === 'model_review' || c.suggested_source === 'company_investor')
    && (c.suggested_role === 'investor' || c.suggested_role === 'portfolio')));
  type ReviewGroup = 'all';
  const REVIEW_INITIAL_ROWS = 50;
  const autoFiledInvestors = $derived(investors.filter(c => c.set_by === 'model'
    && (c.suggested_source === 'model_auto' || c.suggested_source === 'company_auto')));
  const filteredAutomated = $derived(allContacts.filter(c => c.automated_address));
  const reviewRows = $derived.by(() => {
    const rows = new Map<string, FundContact>();
    for (const contact of unreviewedPeople) {
      if (bySearch(contact)) rows.set(contact.counterparty_id, contact);
    }
    for (const done of Object.values(reviewDone)) {
      if (bySearch(done.contact)) rows.set(done.contact.counterparty_id, done.contact);
    }
    return [...rows.values()];
  });
  const reviewGroups = $derived(reviewRows.length ? [{
    key: 'all' as ReviewGroup, label: 'Needs your call',
    rows: [...reviewRows].sort(mostRecentFirst),
  }] : []);
  const reviewDoneCount = $derived(reviewRows.filter(c => !!reviewDone[c.counterparty_id]).length);
  const reviewVisibleCount = $derived(reviewGroups.reduce((total, group) =>
    total + Math.min(group.rows.length, reviewMore[group.key] ? group.rows.length : REVIEW_INITIAL_ROWS), 0));

  /* The assistant reads this counterparty's synced mail, so it carries the same
     confirmation gate as the brief and the draft — `/fund/contacts/{id}/chat`
     refuses anything else server-side, and this keeps the rail honest about why. */
  const canAskAssistant = $derived(selected?.role === 'investor' && !!selected?.email && !selected?.automated_address);
  const canGenerateDraft = $derived(!!contactContext?.messages.some(m => m.evidence_number)
    || !!contactContext?.notes?.length);
  const selectedName = $derived(selected?.display_name || 'this contact');
  let messages = $state<{ role: string; content: string }[]>([]);
  let chatInput = $state('');
  let chatLoading = $state(false);
  let chatError = $state('');
  let chatGeneration = 0;
  // A conversation belongs to one LP contact. A deep link changes `contactId`
  // without going through openContact(), so the reset lives here, not in the click.
  let chatContactId = $state('');
  $effect(() => {
    if (contactId === chatContactId) return;
    chatGeneration += 1;
    chatContactId = contactId;
    detailTab = 'briefing';
    messages = []; chatInput = ''; chatLoading = false; chatError = '';
    sentReceipts = []; emailTracking = []; openedMessages = new Set(); emailQuery = ''; expandedEmailThreads = new Set(); activeEmailThreadKey = ''; messageBodies = {}; messageErrors = {};
    refreshingThreads = false;
    scheduledSends = []; composeFiles = []; composeDocumentIds = [];
    groundingDocumentIds = [];
    noteEditorOpen = false; noteEditId = ''; noteTitle = ''; noteBody = '';
    composeOpen = false; composeSubject = ''; composeBody = ''; composeRequestId = '';
    sendNotice = '';
    if (todayReplyRequest?.counterparty_id === contactId && selected?.role === 'investor') {
      startReply({ message_id: todayReplyRequest.message_id,
        subject: todayReplyRequest.subject, owner_email: $userEmailStore });
      if (todayReplyRequest.draftBody) composeBody = todayReplyRequest.draftBody;
      if (todayReplyRequest.draftSubject) composeSubject = todayReplyRequest.draftSubject;
      todayReplyRequest = null;
    }
    if (todayComposeRequest?.contactId === contactId && selected?.role === 'investor') {
      startFundEmail();
      composeSubject = todayComposeRequest.subject;
      composeBody = todayComposeRequest.body;
      todayComposeRequest = null;
    }
  });

  async function handleChat() {
    const asked = chatInput.trim();
    if (!asked || chatLoading || !selected) return;
    const askedContactId = selected.counterparty_id;
    const generation = chatGeneration;
    chatInput = '';
    chatError = '';
    const turns = [...messages, { role: 'user', content: asked }];
    messages = turns;
    chatLoading = true;
    try {
      const answer = await fundChat(askedContactId, turns);
      if (generation !== chatGeneration || contactId !== askedContactId) return;
      messages = [...messages, { role: 'assistant', content: answer.reply }];
    } catch (err: any) {
      if (generation !== chatGeneration || contactId !== askedContactId) return;
      // Restore the unsent turn to the composer so Retry sends it exactly once.
      messages = turns.slice(0, -1);
      chatInput = asked;
      chatError = err?.message || 'Could not answer from the synced context.';
    } finally {
      if (generation === chatGeneration && contactId === askedContactId) chatLoading = false;
    }
  }
  function quickAsk(question: string) { chatInput = question; handleChat(); }
  function restartConversation() {
    if (messages.length && !confirm('Restart the conversation? This clears the current chat.')) return;
    messages = []; chatError = '';
  }

  /* One request feeds both sidebar stores: `fundPins` is the star state this page
     reads, `fundLps` the full list the shell renders. Called from every reload, not
     just the pin buttons — confirming a role adds a row, and a stage edit repaints
     its dot, so a list refreshed only on pin/unpin goes stale while you work. */
  async function refreshSidebarLps() {
    const data = await getFundPins();
    $fundPins = data.pinned || [];
    $fundLps = data.lps || [];
  }

  /* Only on a RELOAD. The shell's boot already fetched the list, so refreshing it on
     mount is one redundant request per page visit; what goes stale is the roster and
     the stage dots AFTER a write, and every one of those paths calls load() again.
     Not awaited either: `setReviewRole` holds `savingRole` until load() resolves, so a
     second request in that path swallows the next review keystroke. The stores repaint
     when it lands, and its failure must not blank the workspace. */
  let sidebarListLoaded = false;

  async function load() {
    if (sidebarListLoaded) void refreshSidebarLps().catch(() => {});
    sidebarListLoaded = true;
    try {
      const data = await getFundWorkspace();
      workspace = data;
      groundingDocumentIds = groundingDocumentIds.filter(id =>
        data.documents.some(doc => doc.document_id === id));
      error = '';
    } catch (err: any) {
      error = err?.message || 'Could not load your Investor Relations workspace.';
    } finally {
      loading = false;
    }
  }

  async function loadViews() {
    try {
      const data = await getFundDashboardViews();
      savedViews = Object.fromEntries(Object.entries(data.view_states)
        .filter(([, value]) => typeof value.name === 'string' && typeof value.tab === 'string')) as Record<string, FundViewPreset>;
      viewError = '';
    } catch (err: any) { viewError = err?.message || 'Could not load saved views.'; }
  }
  async function loadDashboardMailActivity() {
    try {
      dashboardMailActivity = (await getFundDashboardMailActivity()).activity;
      dashboardMailError = '';
    } catch (err: any) {
      dashboardMailActivity = {};
      dashboardMailError = err?.message || 'Mail activity is unavailable.';
    } finally {
      dashboardMailLoaded = true;
    }
  }
  /* A view saved before the chips became multi-select holds a single string, and
     the status one used 'all' to mean no filter. Widen it rather than drop the view. */
  const widenStatus = (value: IrStatus[] | string): IrStatus[] =>
    Array.isArray(value) ? value : (!value || value === 'all' ? [] : [value as IrStatus]);
  const widenStage = (value: string[] | string): string[] =>
    Array.isArray(value) ? value : (value ? [value] : []);
  /* A view is a PRESET — the filters and sort you come back to — and the live chips are
     remembered tweaks on top of it. That is the model the Sales dashboard settled on
     (2026-07-17); this page reads the same control, so it must not invent a third one.
     `· modified` means "the chips no longer match the preset", and the two buttons are
     the only ways to resolve it: overwrite the preset, or keep it and save a new one. */
  const ALL_INVESTORS_KEY = '';
  const PORTFOLIO_VIEW_KEY = '__portfolio__';
  const PINNED_VIEW_KEY = '__builtin_pinned__';
  const UP_NEXT_VIEW_KEY = '__builtin_up_next__';
  const PIPELINE_VIEW_KEY = '__builtin_pipeline__';
  type FundSavedView = { key: string; name: string; bookmarked: boolean;
    builtin: boolean; preset: FundViewPreset };
  const emptyFundPreset = (): FundViewPreset => ({ name: 'All LPs', tab: 'ir',
    status: [], sort: 'due', sortDir: 'asc', institution: '', stage: [], pinned: false,
    search: '' });
  const currentFundPreset = (name: string, bookmarked = false): FundViewPreset =>
    ({ name, bookmarked, tab: dashTab, status: irStatusFilter, sort: irSort,
      sortDir: irSortDir, institution: irInstitutionFilter, stage: irStageFilter,
      pinned: irPinnedOnly, search });
  /* `name` and `bookmarked` are presentation, not filters. The builtin Portfolio
     view is a separate destination, so selecting it must not mark the active filter
     preset dirty. Older saved views can still restore their original destination. */
  const sameFundPreset = (a: FundViewPreset, b: FundViewPreset) =>
    JSON.stringify([[...widenStatus(a.status)].sort(), a.sort, a.sortDir || 'asc',
      a.institution, [...widenStage(a.stage)].sort(), !!a.pinned,
      (a.search || '').trim(), a.checked || ''])
    === JSON.stringify([[...widenStatus(b.status)].sort(), b.sort, b.sortDir || 'asc',
      b.institution, [...widenStage(b.stage)].sort(), !!b.pinned,
      (b.search || '').trim(), b.checked || '']);
  const allFundViews = $derived<FundSavedView[]>([
    { key: ALL_INVESTORS_KEY, name: 'All LPs', bookmarked: true, builtin: true,
      preset: emptyFundPreset() },
    { key: PINNED_VIEW_KEY, name: 'Pinned', bookmarked: false, builtin: true,
      preset: { ...emptyFundPreset(), name: 'Pinned', pinned: true } },
    { key: UP_NEXT_VIEW_KEY, name: 'Up next', bookmarked: false, builtin: true,
      preset: { ...emptyFundPreset(), name: 'Up next', status: ['overdue', 'soon'] } },
    { key: PIPELINE_VIEW_KEY, name: 'Pipeline', bookmarked: false, builtin: true,
      preset: { ...emptyFundPreset(), name: 'Pipeline', stage: [
        'commitment_in_progress', 'due_diligence', 'engaged', 'prospect'] } },
    { key: PORTFOLIO_VIEW_KEY, name: 'Portfolio context', bookmarked: false, builtin: true,
      preset: { ...emptyFundPreset(), name: 'Portfolio context', tab: 'portfolio' } },
    ...Object.entries(savedViews).map(([key, preset]) => ({ key, name: preset.name,
      bookmarked: !!preset.bookmarked, builtin: false, preset })),
  ]);
  const activeFundView = $derived(allFundViews.find(v => v.key === activeViewKey)
    || allFundViews[0]);
  const fundViewDirty = $derived(!sameFundPreset(activeFundView.preset,
    currentFundPreset(activeFundView.name)));
  /* The count each view would show, computed through the one `irMatches` predicate. The
     chips only apply on the IR tab, exactly as the markup does it, so a Portfolio or
     Needs-sorting view counts its own source list with the search term alone. */
  function fundViewCount(preset: FundViewPreset): number {
    const term = (preset.search || '').trim().toLowerCase();
    const source = preset.tab === 'portfolio' ? portfolio
      : preset.tab === 'sorting' ? unclassified : investors;
    const rows = preset.tab === 'ir'
      ? source.filter(c => irMatches(c, { term, status: widenStatus(preset.status),
          institution: preset.institution, stage: widenStage(preset.stage),
          pinned: !!preset.pinned }))
      : source.filter(c => matchesSearch(c, term));
    if (preset.tab === 'portfolio') return rows.length;
    return groupFundContacts(rows).length;
  }
  function applyFundView(viewToApply: FundSavedView) {
    const preset = viewToApply.preset;
    activeViewKey = viewToApply.key;
    viewDropdownOpen = false;
    dashTab = preset.tab;
    irStatusFilter = widenStatus(preset.status);
    irSort = preset.sort;
    irSortDir = preset.sortDir || 'asc';
    irInstitutionFilter = preset.institution;
    irStageFilter = widenStage(preset.stage);
    irPinnedOnly = preset.pinned;
    search = preset.search;
  }
  // Kept for the deep-link/back paths that address a view by key.
  function applySavedView(key: string) {
    const match = allFundViews.find(v => v.key === key);
    if (match) applyFundView(match);
  }
  async function writeFundView(key: string, preset: FundViewPreset) {
    viewSaving = true; viewError = '';
    try {
      await saveFundDashboardView(key, preset);
      savedViews = { ...savedViews, [key]: preset };
      return true;
    } catch (err: any) {
      viewError = err?.message || 'Could not save view.';
      return false;
    } finally { viewSaving = false; }
  }
  async function saveCurrentView() {
    const name = newViewName.trim();
    if (!name || name.length > 60 || viewSaving) return;
    const key = crypto.randomUUID().replaceAll('-', '');
    if (await writeFundView(key, currentFundPreset(name))) {
      activeViewKey = key; newViewName = ''; viewNaming = false;
    }
  }
  async function updateActiveFundView() {
    if (activeFundView.builtin || viewSaving) return;
    await writeFundView(activeFundView.key,
      currentFundPreset(activeFundView.name, activeFundView.bookmarked));
  }
  function openSaveAsNew() {
    newViewName = ''; viewNaming = true; viewDropdownOpen = false;
  }
  async function toggleFundViewBookmark(viewToToggle: FundSavedView, event: MouseEvent) {
    event.stopPropagation();
    if (viewToToggle.builtin || viewSaving) return;
    await writeFundView(viewToToggle.key,
      { ...viewToToggle.preset, bookmarked: !viewToToggle.bookmarked });
  }
  async function renameFundView(viewToRename: FundSavedView, name: string) {
    const trimmed = name.trim();
    if (viewToRename.builtin || !trimmed || trimmed.length > 60
        || trimmed === viewToRename.name) return;
    await writeFundView(viewToRename.key, { ...viewToRename.preset, name: trimmed });
  }
  async function deleteFundView(viewToDelete: FundSavedView, event: MouseEvent) {
    event.stopPropagation();
    if (viewToDelete.builtin || viewSaving) return;
    if (!confirm(`Delete the saved view “${viewToDelete.name}”?`)) return;
    viewSaving = true; viewError = '';
    try {
      await deleteFundDashboardView(viewToDelete.key);
      const { [viewToDelete.key]: _removed, ...remaining } = savedViews;
      savedViews = remaining;
      if (activeViewKey === viewToDelete.key) activeViewKey = ALL_INVESTORS_KEY;
    } catch (err: any) { viewError = err?.message || 'Could not delete view.'; }
    finally { viewSaving = false; }
  }
  async function loadTodayDecisions() {
    const sequence = ++todayDecisionsSequence;
    try {
      const data = await getFundTodayDecisions();
      if (sequence === todayDecisionsSequence) {
        todayDecisions = data.decisions; todayDecisionsError = '';
      }
    } catch (err: any) {
      if (sequence === todayDecisionsSequence)
        todayDecisionsError = err?.message || 'Could not load mailbox suggestions.';
    } finally {
      if (sequence === todayDecisionsSequence) todayDecisionsLoaded = true;
    }
  }
  async function loadTodayCalls() {
    const sequence = ++todayCallsSequence;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    todayCallsLoading = true;
    try {
      const data = await getFundTodayCalls(start.toISOString(), end.toISOString());
      if (!data || !Array.isArray(data.events)) throw new Error('Calendar response was incomplete.');
      if (sequence === todayCallsSequence && view === 'today') {
        todayCalls = data.events;
        todayCallsError = '';
      }
    } catch (err: any) {
      if (sequence === todayCallsSequence && view === 'today')
        todayCallsError = err?.message || 'Could not load today’s LP calls.';
    } finally {
      if (sequence === todayCallsSequence) {
        todayCallsLoading = false;
        todayCallsLoaded = true;
      }
    }
  }
  async function actOnTodayDecision(decision: FundTodayDecision, action: 'dismiss' | 'snooze') {
    try {
      await actOnFundTodayDecision(decision, action);
      todayDecisions = todayDecisions.filter(item =>
        item.counterparty_id !== decision.counterparty_id || item.kind !== decision.kind
        || item.message_id !== decision.message_id);
      todayDecisionsError = '';
    } catch (err: any) {
      todayDecisionsError = err?.message || 'Could not update this suggestion.';
      await loadTodayDecisions();
    }
  }
  onMount(() => { load(); loadViews(); loadTodayDecisions(); });
  $effect(() => {
    if (view === 'today' || view === 'dashboard' || (view === 'detail' && selectedOrganization)) {
      $pollVersion; loadDashboardMailActivity();
    }
  });
  $effect(() => {
    if (view === 'today') { $pollVersion; loadTodayCalls(); }
  });
  async function loadAllScheduled() {
    scheduledLoading = true; scheduledError = '';
    try {
      const data = await getAllFundScheduled();
      allScheduled = data.messages;
    } catch (err: any) {
      scheduledError = err?.message || 'Could not load scheduled emails.';
    } finally { scheduledLoading = false; }
  }
  $effect(() => { if (view === 'scheduled' || view === 'today') loadAllScheduled(); });
  $effect(() => {
    if ($pollVersion > 0) { load(); loadTodayDecisions(); }
  });
  async function reloadContext(id: string) {
    contextLoading = true; contextError = '';
    try {
      const data = await getFundContext(id);
      if (contactId === id) contactContext = data;
    } catch (err: any) {
      if (contactId === id) contextError = err?.message || 'Could not load relationship context.';
    } finally {
      if (contactId === id) contextLoading = false;
    }
  }
  $effect(() => {
    if (view !== 'detail' || !selected || selected.role !== 'investor' || selected.automated_address) {
      contactContext = null;
      return;
    }
    const id = selected.counterparty_id;
    contactContext = null;
    contextError = '';
    reloadContext(id);
    getFundSent(id).then(data => {
      if (contactId === id) sentReceipts = data.messages;
    }).catch(() => { /* Email context error remains the actionable alert. */ });
    getFundEmailTracking(id).then(data => {
      if (contactId === id) emailTracking = data.messages;
    }).catch(() => { /* Tracking is optional. */ });
    getFundScheduled(id).then(data => {
      if (contactId === id) scheduledSends = data.messages;
    }).catch(() => { /* The composer still works if the schedule list cannot load. */ });
  });

  function openContact(contact: FundContact) {
    instruction = '';
    draftInstruction = '';
    actionError = '';
    contactContext = null;
    detailTab = 'briefing';
    $currentView = contact.counterparty_id;
  }

  function openFollowUp(contact: FundContact) {
    if (contact.role !== 'investor' || contact.automated_address) return;
    if (!contact.institution_id && contact.suggested_organization_domain) {
      openOrganization(`domain:${contact.suggested_organization_domain}`);
      return;
    }
    followUpEditorRequested = contact.counterparty_id;
    followUpEditing = true;
    openContact(contact);
  }

  function openTodayHighlight(highlight: TodayHighlight) {
    const contact = allContacts.find(item => item.counterparty_id === highlight.contactId);
    if (!contact) return;
    if (highlight.action === 'follow-up') openFollowUp(contact);
    else if (highlight.organization) openOrganization(highlight.key);
    else openContact(contact);
  }

  function chooseTodayFocus(key: string, preferredContactId = '') {
    const generation = ++todayFocusGeneration;
    todayFocusKey = key;
    const members = (todayFocusGroups.find(group => group.key === key)?.contacts || [])
      .filter(contact => contact.role === 'investor' && !!contact.email && !contact.automated_address)
      .sort(mostRecentFirst);
    todayFocusContactId = members.find(contact => contact.counterparty_id === preferredContactId)?.counterparty_id
      || members[0]?.counterparty_id || '';
    todayBriefMemberIds = members.slice(0, 8).map(contact => contact.counterparty_id);
    todayFocusBusy = '';
    todayFocusLoading = !!key;
    todayFocusBrief = null;
    todayFocusDraft = null;
    todayFocusError = '';
    if (!key) return;
    const group = todayFocusGroups.find(item => item.key === key);
    const institutionId = key.startsWith('institution:') ? key.slice('institution:'.length) : '';
    const request = institutionId ? getFundInstitutionBrief(institutionId)
      : group?.contacts.length === 1 ? getFundContext(group.contacts[0].counterparty_id).then(context => ({
        brief: context.outputs.brief || null,
      })) : Promise.resolve({ brief: null });
    request.then(result => {
      if (generation === todayFocusGeneration && view === 'today') todayFocusBrief = result;
    }).catch(() => { /* A saved brief is optional; generation remains available. */ })
      .finally(() => { if (generation === todayFocusGeneration) todayFocusLoading = false; });
  }

  function toggleTodayHighlight(highlight: TodayHighlight) {
    const open = !todayExpanded[highlight.key];
    todayExpanded = { [highlight.key]: open };
    if (open) chooseTodayFocus(highlight.key, highlight.contactId);
  }

  async function prepareTodayFocus(kind: 'brief' | 'draft') {
    const group = todayFocusGroup;
    if (!group || todayFocusBusy) return;
    const person = todayFocusPerson;
    if (kind === 'draft' && !person) {
      todayFocusError = 'Choose one confirmed LP contact to receive the draft.';
      return;
    }
    const institutionId = group.key.startsWith('institution:')
      ? group.key.slice('institution:'.length) : '';
    if (kind === 'brief' && !institutionId && !person) {
      todayFocusError = 'Choose one LP contact for this suggested organization.';
      return;
    }
    const generation = ++todayFocusGeneration;
    todayFocusBusy = kind;
    todayFocusLoading = false;
    todayFocusError = '';
    if (kind === 'brief') todayFocusBrief = null;
    else todayFocusDraft = null;
    try {
      if (kind === 'brief') {
        if (institutionId) {
          const ids = todayBriefMemberIds.filter(id => todayFocusMembers.some(contact => contact.counterparty_id === id));
          if (!ids.length) throw new Error('No confirmed LP contact with an email is available for this institution.');
          const result = await generateFundInstitutionBrief(institutionId, ids, '', []);
          if (generation === todayFocusGeneration && view === 'today') todayFocusBrief = result;
        } else if (person) {
          const result = await generateFundOutput(person.counterparty_id, 'brief', '', []);
          if (generation === todayFocusGeneration && view === 'today')
            todayFocusBrief = { brief: result.text, review_only: true,
              source_manifest: { contact_ids: [person.counterparty_id], sources: [], documents: [] } };
        }
      } else if (person) {
        const result = await generateFundOutput(person.counterparty_id, 'draft',
          'Write a concise, review-only LP email based on the documented relationship. Do not invent an open commitment.', []);
        const parsed = parseEmailDraft(result.text);
        if (!parsed) throw new Error('The generated text is not a sendable email. Open this LP’s Memory to review the full output.');
        if (generation === todayFocusGeneration && view === 'today')
          todayFocusDraft = { contactId: person.counterparty_id, ...parsed };
      }
    } catch (err: any) {
      if (generation === todayFocusGeneration && view === 'today')
        todayFocusError = err?.message || `Could not prepare the ${kind}.`;
    } finally {
      if (generation === todayFocusGeneration) todayFocusBusy = '';
    }
  }

  function reviewTodayFocusDraft() {
    const draft = todayFocusDraft;
    const person = todayFocusMembers.find(contact => contact.counterparty_id === draft?.contactId);
    if (!draft || !person || !draft.subject.trim() || !draft.body.trim()) return;
    todayComposeRequest = { contactId: person.counterparty_id,
      subject: draft.subject.trim(), body: draft.body.trim() };
    openContact(person);
  }

  function openTodayReply(decision: FundTodayDecision) {
    const contact = allContacts.find(item => item.counterparty_id === decision.counterparty_id);
    if (!contact || contact.role !== 'investor' || contact.automated_address) return;
    todayReplyRequest = { counterparty_id: contact.counterparty_id,
      message_id: decision.message_id, subject: decision.subject };
    openContact(contact);
  }

  function reviewReadyTodayDraft(decision: FundTodayDecision) {
    const draft = decision.ready_draft;
    const contact = allContacts.find(item => item.counterparty_id === decision.counterparty_id);
    if (!draft || draft.source_message_id !== decision.message_id
        || !contact || contact.role !== 'investor' || contact.automated_address) return;
    todayReplyRequest = { counterparty_id: contact.counterparty_id,
      message_id: decision.message_id, subject: decision.subject,
      draftSubject: draft.subject, draftBody: draft.body };
    openContact(contact);
  }

  async function prepareTodayDraft(decision: FundTodayDecision) {
    const contact = investors.find(item => item.counterparty_id === decision.counterparty_id);
    if (!contact || contact.automated_address || todayDraftMessageId) return;
    todayDraftMessageId = decision.message_id;
    todayDraftError = '';
    try {
      const focus = decision.kind === 'respond'
        ? 'Reply to the latest inbound question using only supported context. Keep it concise.'
        : 'Write a short follow-up to the existing conversation. Do not imply a reply is owed.';
      // A Respond card names the message it answers and the composer threads the reply
      // onto it, so the model has to answer that one rather than the newest synced mail.
      const result = await generateFundOutput(contact.counterparty_id, 'draft', focus, [],
        decision.kind === 'respond' ? decision.message_id : '');
      if (view !== 'today') return;
      const parsed = parseEmailDraft(result.text);
      if (!parsed) {
        todayDraftError = `The draft for ${contact.display_name} needs review before it can enter the composer. Open their Memory to read it.`;
        return;
      }
      todayReplyRequest = { counterparty_id: contact.counterparty_id,
        message_id: decision.message_id, subject: decision.subject, draftBody: parsed.body };
      openContact(contact);
    } catch (err: any) {
      if (view === 'today') todayDraftError = err?.message || 'Could not prepare the LP draft.';
    } finally {
      todayDraftMessageId = '';
    }
  }

  async function prepareTodayBrief(contactId: string) {
    const contact = investors.find(item => item.counterparty_id === contactId);
    if (!contact || contact.automated_address || todayBriefContactId) return;
    todayBriefContactId = contactId;
    todayBriefError = '';
    try {
      // This click selects one confirmed LP. Optional documents stay opt-in in Memory.
      await generateFundOutput(contactId, 'brief', '', []);
      if (view === 'today') {
        openContact(contact);
        detailTab = 'memory';
      }
    } catch (err: any) {
      if (view === 'today') todayBriefError = err?.message || 'Could not prepare this LP brief.';
    } finally {
      todayBriefContactId = '';
    }
  }

  function roleLabel(role: FundRole | null): string {
    return role === 'investor' ? 'LP' : role === 'portfolio' ? 'Portfolio'
      : role === 'neither' ? 'Neither' : 'Unclassified';
  }

  function mailDate(value: string | null | undefined, fallback = '—'): string {
    if (!value) return fallback;
    // Gmail metadata uses epoch milliseconds; Graph and saved evidence use ISO.
    const date = /^\d{13}$/.test(value) ? new Date(Number(value)) : parseDay(value);
    return Number.isNaN(date.getTime())
      ? fallback
      : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function mailTime(value: string | null | undefined): number {
    if (!value) return 0;
    const date = /^\d{13}$/.test(value) ? new Date(Number(value)) : parseDay(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  function mostRecentFirst(a: FundContact, b: FundContact): number {
    return mailTime(b.last_seen) - mailTime(a.last_seen)
      || a.display_name.localeCompare(b.display_name);
  }

  async function setRole(contact: FundContact, role: FundRole, control?: HTMLSelectElement) {
    if (!contact.email || savingRole) return;
    savingRole = contact.counterparty_id;
    actionError = '';
    try {
      await confirmFundRole({ email: contact.email, name: contact.display_name }, role);
      await load();
      // The investor context effect loads after the saved role appears in the
      // workspace. A failed context read must not be reported as a failed save.
    } catch (err: any) {
      // A native select changes before the request starts. If persistence fails,
      // restore the server-backed role so the control does not claim a save.
      if (control) control.value = displayRole(contact) || '';
      actionError = err?.message || 'Could not save this relationship.';
    } finally {
      savingRole = '';
    }
  }

  function nextReviewContact(from: string): FundContact | null {
    const ordered = reviewGroups.flatMap(group => group.rows);
    const index = ordered.findIndex(c => c.counterparty_id === from);
    const next = ordered.slice(index + 1).find(c => !reviewDone[c.counterparty_id]) || null;
    const nextGroup = reviewGroups[0];
    if (next && nextGroup && nextGroup.rows.findIndex(c => c.counterparty_id === next.counterparty_id) >= REVIEW_INITIAL_ROWS)
      reviewMore = { ...reviewMore, [nextGroup.key]: true };
    return next;
  }

  async function setReviewRole(contact: FundContact, role: FundRole) {
    if (!contact.email || contact.automated_address || savingRole) return;
    savingRole = contact.counterparty_id;
    actionError = '';
    try {
      await confirmFundRole({ email: contact.email, name: contact.display_name }, role);
      reviewDone = { ...reviewDone, [contact.counterparty_id]: { contact, role } };
      reviewCursor = nextReviewContact(contact.counterparty_id)?.counterparty_id || '';
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not save this relationship.';
    } finally { savingRole = ''; }
  }

  async function undoReviewRole(contact: FundContact) {
    const done = reviewDone[contact.counterparty_id];
    if (!done || savingRole) return;
    savingRole = contact.counterparty_id;
    actionError = '';
    try {
      await undoFundReviewRole(contact.counterparty_id, done.role);
      const { [contact.counterparty_id]: _old, ...remaining } = reviewDone;
      reviewDone = remaining;
      reviewCursor = contact.counterparty_id;
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not undo this decision.';
    } finally { savingRole = ''; }
  }

  function handleReviewKey(event: KeyboardEvent) {
    if (event.key === 'Escape' && mikeOverlayOpen) { mikeOverlayOpen = false; return; }
    if (view !== 'dashboard' || dashTab !== 'sorting' || event.altKey || event.ctrlKey || event.metaKey) return;
    const target = event.target;
    if (target instanceof HTMLElement
        && target.closest('input, textarea, select, [contenteditable="true"]')) return;
    const rows = reviewGroups.flatMap(group => group.rows)
      .filter(c => !reviewDone[c.counterparty_id]);
    if (!rows.length) return;
    const key = event.key.toLowerCase();
    const index = rows.findIndex(c => c.counterparty_id === reviewCursor);
    if (key === 'arrowdown' || key === 'j' || key === 'arrowup' || key === 'k') {
      const next = key === 'arrowdown' || key === 'j'
        ? rows[Math.min(index + 1, rows.length - 1)]
        : rows[Math.max(index - 1, 0)];
      reviewCursor = next.counterparty_id;
      const group = reviewGroups[0];
      if (group && group.rows.findIndex(c => c.counterparty_id === next.counterparty_id) >= REVIEW_INITIAL_ROWS)
        reviewMore = { ...reviewMore, [group.key]: true };
      event.preventDefault();
      void tick().then(() => document.getElementById(`fund-review-${reviewCursor}`)
        ?.scrollIntoView?.({ block: 'nearest' }));
    } else if (key === 'i' || key === 'p' || key === 'n') {
      const contact = rows[index >= 0 ? index : 0];
      reviewCursor = contact.counterparty_id;
      event.preventDefault();
      void setReviewRole(contact, key === 'i' ? 'investor' : key === 'p' ? 'portfolio' : 'neither');
    }
  }

  async function saveStage(contact: FundContact, value: string, control: HTMLSelectElement) {
    if (savingStage) return;
    if (!contact.institution_id && contact.suggested_organization_domain) {
      control.value = contact.stage || '';
      actionError = 'Set the institution stage after confirming this organization.';
      return;
    }
    savingStage = true; actionError = '';
    try {
      if (contact.institution_id) await setFundInstitutionStage(contact.institution_id, value || null);
      else await setFundStage(contact.counterparty_id, value || null);
      await load();
    } catch (err: any) {
      control.value = relationshipStage(contact) || '';
      actionError = err?.message || 'Could not save this relationship stage.';
    } finally { savingStage = false; }
  }

  async function saveFollowUp() {
    if (!selected || savingFollowUp) return;
    if (!selected.institution_id && selected.suggested_organization_domain) {
      actionError = 'Set an institution follow-up after confirming this organization.';
      return;
    }
    savingFollowUp = true; actionError = '';
    try {
      if (selected.institution_id)
        await setFundInstitutionFollowUp(selected.institution_id, nextStepDraft, followUpDateDraft || null);
      else await setFundFollowUp(selected.counterparty_id, nextStepDraft, followUpDateDraft || null);
      followUpEditing = false;
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not save this next step.';
    } finally { savingFollowUp = false; }
  }

  /* Takes the id so a dashboard row can pin without first opening the contact.
     `pinError` is separate from `actionError`: the detail rail owns that one, and a
     failed pin from the table must not blank a message the rail is showing. */
  async function togglePin(counterpartyId: string | undefined) {
    if (!counterpartyId || savingPin) return;
    savingPin = true; pinError = '';
    try {
      if (isPinned(counterpartyId)) await unpinFundContact(counterpartyId);
      else await pinFundContact(counterpartyId);
      await refreshSidebarLps();
    } catch (err: any) {
      pinError = err?.message || 'Could not update this pin.';
    } finally { savingPin = false; }
  }

  async function toggleOrganizationPin(key: string) {
    if (savingPin) return;
    savingPin = true; pinError = '';
    try {
      if (isOrganizationPinned(key)) await unpinFundOrganization(key);
      else await pinFundOrganization(key);
      await refreshSidebarLps();
    } catch (err: any) {
      pinError = err?.message || 'Could not update this organization pin.';
    } finally { savingPin = false; }
  }

  function openOrganization(key: string) {
    const organization = groupFundContacts(allContacts).find(group => group.key === key);
    const contact = organization?.contacts.find(member => member.role === 'investor')
      || organization?.contacts.find(member => member.suggested_role === 'investor')
      || organization?.contacts[0];
    if (!contact) return;
    openContact(contact);
  }

  function openOrganizationFollowUp() {
    if (!selectedOrganizationRecord) return;
    nextStepDraft = selectedOrganizationRecord.next_step || '';
    followUpDateDraft = selectedOrganizationRecord.follow_up_on || '';
    followUpEditing = true;
  }

  async function saveOrganizationFollowUp() {
    if (!selectedOrganizationRecord || savingFollowUp) return;
    savingFollowUp = true; actionError = '';
    try {
      await setFundInstitutionFollowUp(selectedOrganizationRecord.institution_id,
        nextStepDraft, followUpDateDraft || null);
      followUpEditing = false;
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not save this institution follow-up.';
    } finally { savingFollowUp = false; }
  }

  async function addContact() {
    if (savingRole) return;
    const email = newEmail.trim();
    if (!email) {
      actionError = 'Enter a contact email address.';
      return;
    }
    savingRole = email;
    actionError = '';
    try {
      await confirmFundRole({ email, name: newName.trim() }, newRole);
      await load();
      newEmail = '';
      newName = '';
      addOpen = false;
    } catch (err: any) {
      actionError = err?.message || 'Could not add this contact.';
    } finally {
      savingRole = '';
    }
  }

  async function generate(kind: 'brief' | 'draft') {
    if (!selected || generating) return;
    const id = selected.counterparty_id;
    const composerId = composeRequestId;
    const replyMessageId = kind === 'draft' ? composeReplyMessageId : '';
    generating = kind;
    actionError = '';
    try {
      const result = await generateFundOutput(id, kind,
        kind === 'draft' ? draftInstruction : instruction, groundingDocumentIds, replyMessageId);
      if (contactId !== id || (kind === 'draft' && (!composeOpen || composeRequestId !== composerId))) return;
      const context = await getFundContext(id);
      if (contactId !== id || (kind === 'draft' && (!composeOpen || composeRequestId !== composerId))) return;
      contactContext = context;
      if (kind === 'draft') useDraftResult(result.text, replyMessageId);
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || `Could not generate the ${kind}.`;
    } finally {
      generating = '';
    }
  }

  function parseEmailDraft(text: string): { subject: string; body: string } | null {
    const cleaned = text.trim().replace(/^```(?:text|email)?\s*\n/i, '').replace(/\n```\s*$/, '');
    const match = cleaned.match(/^Subject:\s*([^\r\n]+)\r?\n(?:\r?\n)?([\s\S]+)$/i);
    if (!match || !match[1].trim() || !match[2].trim()) return null;
    return { subject: match[1].trim(), body: match[2].trim() };
  }

  function useDraftResult(text: string, replyMessageId = '') {
    const parsed = parseEmailDraft(text);
    if (!parsed) {
      actionError = 'The generated response did not contain a sendable Subject and body. Read it below; start a new email to write one yourself.';
      detailTab = 'memory';
      return;
    }
    // A reply keeps the thread's subject: the provider threads on it, and an edited
    // one turns a Microsoft reply into an error rather than a new email.
    if (!replyMessageId) composeSubject = parsed.subject;
    composeBody = parsed.body;
    composeReplyMessageId = replyMessageId;
    composeFiles = []; composeDocumentIds = []; scheduleOpen = false;
    composeRequestId = crypto.randomUUID();
    composeOpen = true;
    detailTab = 'emails';
  }

  function startFundEmail() {
    if (composeOpen && (composeSubject.trim() || composeBody.trim())
        && !confirm('Discard the unfinished email and start a new one?')) return;
    composeSubject = ''; composeBody = '';
    composeCc = ''; composeReplyMessageId = '';
    draftInstruction = ''; groundingDocumentIds = [];
    composeFiles = []; composeDocumentIds = []; scheduleOpen = false;
    composeTracking = false; composeMeeting = false;
    meetingSummary = ''; meetingLocation = ''; meetingStart = ''; meetingEnd = '';
    composeRequestId = crypto.randomUUID();
    composeOpen = true; sendNotice = ''; actionError = ''; polishChanges = '';
    // The composer lives in the Emails tab, so opening one goes there. It used to
    // ride on Emails being the landing tab; since Briefing became the default,
    // every caller would otherwise have to remember to switch — and the Today
    // hand-off paths did not.
    detailTab = 'emails';
  }

  function closeFundEmail() {
    if (composeSubject.trim() || composeBody.trim()) {
      if (!confirm('Discard this unfinished email?')) return;
    }
    composeOpen = false;
    draftInstruction = ''; groundingDocumentIds = [];
  }

  function generateComposeDraft() {
    // Replying pre-fills the locked `Re:` subject, so only the body counts as written.
    const written = composeReplyMessageId ? composeBody.trim() : (composeSubject.trim() || composeBody.trim());
    if (written && !confirm(composeReplyMessageId
      ? 'Replace the reply you have written with a new AI draft?'
      : 'Replace the current subject and message with a new AI draft?')) return;
    generate('draft');
  }

  function startReply(message: { message_id: string; subject: string; owner_email: string }): boolean {
    if (message.owner_email.toLowerCase() !== $userEmailStore.toLowerCase()) return false;
    if (composeOpen && (composeSubject.trim() || composeBody.trim())
        && !confirm('Discard the unfinished email and reply in this thread?')) return false;
    composeReplyMessageId = message.message_id;
    composeSubject = /^re:/i.test(message.subject) ? message.subject : `Re: ${message.subject || '(No subject)'}`;
    composeBody = ''; composeCc = '';
    composeFiles = []; composeDocumentIds = []; scheduleOpen = false;
    composeTracking = false; composeMeeting = false;
    meetingSummary = ''; meetingLocation = ''; meetingStart = ''; meetingEnd = '';
    composeRequestId = crypto.randomUUID();
    composeOpen = true; actionError = ''; sendNotice = ''; polishChanges = '';
    detailTab = 'emails';   // same reason as startFundEmail()
    return true;
  }

  function draftReply(message: { message_id: string; subject: string; owner_email: string }) {
    if (!canGenerateDraft || generating || !startReply(message)) return;
    generateComposeDraft();
  }

  function chooseNewEmailRecipient(id: string): boolean {
    const person = institutionBriefMembers.find(member => member.counterparty_id === id);
    if (!person || person.counterparty_id === selected?.counterparty_id) return false;
    if ((composeSubject.trim() || composeBody.trim())
        && !confirm('Discard the unfinished email and write to another contact?')) return false;
    todayComposeRequest = { contactId: person.counterparty_id, subject: '', body: '' };
    openContact(person);
    return true;
  }

  async function editFundEmail(mode: 'polish' | 'refine') {
    if (!selected || polishing || !composeBody.trim()) return;
    const id = selected.counterparty_id;
    polishing = true; actionError = '';
    try {
      const result = await polishFundEmail(id, composeSubject, composeBody, mode);
      if (contactId === id) {
        composeSubject = result.subject; composeBody = result.body;
        polishChanges = result.changes || 'Edited draft. Review it before sending.';
      }
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Could not edit this email.';
    } finally { polishing = false; }
  }

  async function saveReviewDraftInDealton() {
    if (!canUseAs() || !selected || savingReviewDraft || !composeSubject.trim() || !composeBody.trim()) return;
    const id = selected.counterparty_id;
    savingReviewDraft = true;
    actionError = '';
    try {
      await saveFundReviewDraft(id, composeSubject, composeBody);
      if (contactId === id) {
        sendNotice = 'Review-only draft saved in Dealton. Nothing was saved to the mailbox.';
        await reloadContext(id);
      }
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Could not save the review-only draft.';
    } finally { savingReviewDraft = false; }
  }

  async function saveEmailToDrafts() {
    if (!selected?.email || sending || !composeSubject.trim() || !composeBody.trim()) return;
    if (composeMeeting || composeTracking) {
      actionError = 'Mailbox Drafts currently save the message and attachments only. Turn off invite and tracking, or send/schedule this email.';
      return;
    }
    const id = selected.counterparty_id;
    sending = true; actionError = '';
    try {
      const saved = await saveFundProviderDraft(id, {
        request_id: composeRequestId || crypto.randomUUID(), to: selected.email,
        subject: composeSubject, body: composeBody, cc: composeCc,
        reply_message_id: composeReplyMessageId,
        document_ids: composeDocumentIds, files: composeFiles,
      });
      if (contactId === id) {
        sendNotice = `Saved in your ${saved.provider === 'microsoft' ? 'Outlook' : 'Gmail'} Drafts. It has not been sent.`;
        composeOpen = false; composeRequestId = ''; composeFiles = []; composeDocumentIds = [];
      }
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Check provider Drafts before trying again.';
    } finally { sending = false; }
  }

  function checkedMeeting(): { calendar_start: string; calendar_end: string; calendar_summary: string; calendar_location: string } | null {
    if (!composeMeeting) return { calendar_start: '', calendar_end: '', calendar_summary: '', calendar_location: '' };
    const start = new Date(meetingStart);
    const end = new Date(meetingEnd);
    if (!meetingSummary.trim() || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())
        || start.getTime() <= Date.now() || end.getTime() <= start.getTime()) {
      actionError = 'Enter a meeting title and valid future start and end times.';
      return null;
    }
    return {
      calendar_start: start.toISOString(), calendar_end: end.toISOString(),
      calendar_summary: meetingSummary.trim(), calendar_location: meetingLocation.trim(),
    };
  }

  async function saveInstitution(institutionId: string | null) {
    if (!selected || savingInstitution) return;
    savingInstitution = true; actionError = '';
    try {
      await assignFundInstitution(selected.counterparty_id, institutionId);
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not save this institution.';
    } finally { savingInstitution = false; }
  }

  async function createAndAssignInstitution() {
    if (!selected || savingInstitution || !newInstitutionName.trim()) return;
    savingInstitution = true; actionError = '';
    try {
      const result = await createFundInstitution(newInstitutionName.trim());
      await assignFundInstitution(selected.counterparty_id, result.institution_id);
      newInstitutionName = '';
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not create this institution.';
    } finally { savingInstitution = false; }
  }

  async function prepareInstitutionBrief() {
    if (!selectedInstitution || !institutionBriefContacts.length || institutionBriefGenerating) return;
    const id = selectedInstitution.institution_id;
    institutionBriefGenerating = true; institutionBriefError = '';
    try {
      const data = await generateFundInstitutionBrief(id, institutionBriefContacts,
        institutionBriefInstruction, institutionBriefDocuments);
      if (selected?.institution_id === id) institutionBrief = data;
    } catch (err: any) {
      if (selected?.institution_id === id) institutionBriefError = err?.message || 'Could not prepare the institution brief.';
    } finally { institutionBriefGenerating = false; }
  }

  function startNote(note?: { note_id: string; title: string; body: string }) {
    noteEditId = note?.note_id || '';
    noteTitle = note?.title || '';
    noteBody = note?.body || '';
    noteEditorOpen = true;
    actionError = '';
  }

  async function saveNote() {
    if (!selected || noteSaving || !noteTitle.trim() || !noteBody.trim()) return;
    const id = selected.counterparty_id;
    noteSaving = true; actionError = '';
    try {
      if (noteEditId) await editFundNote(id, noteEditId, noteTitle, noteBody);
      else await addFundNote(id, noteTitle, noteBody);
      if (contactId === id) {
        noteEditorOpen = false; noteEditId = ''; noteTitle = ''; noteBody = '';
        await reloadContext(id);
      }
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Could not save this note.';
    } finally { noteSaving = false; }
  }

  async function toggleMessage(personId: string, owner: string, messageId: string) {
    if (!selected) return;
    const id = selected.counterparty_id;
    if (personId !== id && !institutionBriefMembers.some(member => member.counterparty_id === personId)) return;
    const key = `${personId}:${owner}:${messageId}`;
    if (openedMessages.has(key) && !messageErrors[key]) {
      const next = new Set(openedMessages);
      next.delete(key);
      openedMessages = next;
      return;
    }
    openedMessages = new Set(openedMessages).add(key);
    if (messageBodies[key]) return;
    try {
      const result = await getFundMessage(personId, messageId, owner);
      if (contactId !== id) return;
      messageBodies = { ...messageBodies, [key]: result.body || '(No message body)' };
      messageErrors = { ...messageErrors, [key]: '' };
    } catch (err: any) {
      if (contactId !== id) return;
      messageErrors = { ...messageErrors, [key]: err?.message || 'Could not load this email.' };
    }
  }

  async function refreshOlderThreads() {
    if (!selected || refreshingThreads) return;
    const id = selected.counterparty_id;
    refreshingThreads = true; actionError = '';
    try {
      const result = await refreshFundThreads(id);
      if (contactId !== id) return;
      await reloadContext(id);
      if (selected.institution_id) {
        const activity = await getFundInstitutionActivity(selected.institution_id);
        if (contactId === id) institutionActivity = activity.messages;
      }
      if (contactId === id && result.updated) {
        expandedEmailThreads = new Set(); openedMessages = new Set();
      }
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Could not refresh mailbox thread metadata.';
    } finally { refreshingThreads = false; }
  }

  async function sendReviewedEmail() {
    if (!selected?.email || sending || !composeSubject.trim() || !composeBody.trim()) return;
    const id = selected.counterparty_id;
    const to = selected.email;
    const calendar = checkedMeeting();
    if (!calendar) return;
    if (!confirm(`Send this ${composeReplyMessageId ? 'reply' : 'email'} from your connected mailbox to ${to}${composeCc.trim() ? `, CC ${composeCc.trim()}` : ''}${composeMeeting ? ', with a calendar invite' : ''}${composeTracking ? ', with open tracking' : ''} now?`)) return;
    sending = true; actionError = ''; sendNotice = '';
    const requestId = composeRequestId || crypto.randomUUID();
    composeRequestId = requestId;
    try {
      await submitFundEmail(id, {
        request_id: requestId, to, subject: composeSubject, body: composeBody,
        cc: composeCc, reply_message_id: composeReplyMessageId,
        document_ids: composeDocumentIds, files: composeFiles,
        tracking: composeTracking, ...calendar,
      });
      if (contactId === id) {
        sendNotice = `Sent to ${to}. It will appear in your provider Sent folder immediately and in this relationship after the next sync.`;
        composeOpen = false; composeSubject = ''; composeBody = ''; composeCc = ''; composeReplyMessageId = ''; composeRequestId = '';
        composeFiles = []; composeDocumentIds = [];
        composeTracking = false; composeMeeting = false;
      }
      getFundSent(id).then(data => {
        if (contactId === id) sentReceipts = data.messages;
      }).catch(() => { /* The successful provider response is the source of truth. */ });
      getFundEmailTracking(id).then(data => {
        if (contactId === id) emailTracking = data.messages;
      }).catch(() => {});
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Send status is unclear. Check your Sent folder before trying again.';
    } finally { sending = false; }
  }

  async function scheduleReviewedEmail() {
    if (!selected?.email || sending || !composeSubject.trim() || !composeBody.trim()
        || !scheduleDate || !scheduleTime) return;
    const when = new Date(`${scheduleDate}T${scheduleTime}`);
    if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now() + 60000) {
      actionError = 'Choose a time at least one minute in the future.';
      return;
    }
    const calendar = checkedMeeting();
    if (!calendar) return;
    const id = selected.counterparty_id;
    if (!confirm(`Schedule this email to ${selected.email}${composeCc.trim() ? `, CC ${composeCc.trim()}` : ''} for ${when.toLocaleString()}? It will send automatically at that time unless cancelled.`)) return;
    sending = true; actionError = '';
    const requestId = composeRequestId || crypto.randomUUID();
    composeRequestId = requestId;
    try {
      await submitFundEmail(id, {
        request_id: requestId, to: selected.email, subject: composeSubject,
        body: composeBody, cc: composeCc, reply_message_id: composeReplyMessageId,
        document_ids: composeDocumentIds, files: composeFiles, send_at: when.toISOString(),
        tracking: composeTracking, ...calendar,
      });
      if (contactId === id) {
        sendNotice = `Scheduled for ${when.toLocaleString()}. You can cancel it below before dispatch.`;
        composeOpen = false; scheduleOpen = false;
        composeSubject = ''; composeBody = ''; composeCc = ''; composeReplyMessageId = '';
        composeRequestId = ''; composeFiles = []; composeDocumentIds = [];
        composeTracking = false; composeMeeting = false;
        const data = await getFundScheduled(id);
        if (contactId === id) scheduledSends = data.messages;
      }
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Could not schedule this email.';
    } finally { sending = false; }
  }

  async function cancelScheduled(requestId: string) {
    if (!selected || !confirm('Cancel this scheduled email before it sends?')) return;
    const id = selected.counterparty_id;
    try {
      await cancelFundScheduled(requestId);
      const data = await getFundScheduled(id);
      if (contactId === id) scheduledSends = data.messages;
    } catch (err: any) {
      actionError = err?.message || 'Could not cancel this scheduled email.';
    }
  }

  async function cancelFromSchedule(requestId: string) {
    if (!confirm('Cancel this scheduled email before it sends?')) return;
    try {
      await cancelFundScheduled(requestId);
      if (selectedSchedule?.request_id === requestId) selectedSchedule = null;
      await loadAllScheduled();
    } catch (err: any) {
      scheduledError = err?.message || 'Could not cancel this scheduled email.';
    }
  }

  async function openSchedule(requestId: string) {
    scheduledError = '';
    try {
      const detail = await getFundScheduledDetail(requestId);
      selectedSchedule = detail;
      scheduleSubject = detail.subject;
      scheduleBody = detail.body;
      scheduleCc = detail.cc || '';
      const local = new Date(detail.send_at);
      scheduleEditDate = `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, '0')}-${String(local.getDate()).padStart(2, '0')}`;
      scheduleEditTime = `${String(local.getHours()).padStart(2, '0')}:${String(local.getMinutes()).padStart(2, '0')}`;
    } catch (err: any) {
      scheduledError = err?.message || 'Could not open this scheduled email.';
    }
  }

  async function saveScheduleEdit() {
    if (!selectedSchedule || scheduleSaving) return;
    const when = new Date(`${scheduleEditDate}T${scheduleEditTime}`);
    if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now() + 60000) {
      scheduledError = 'Choose a time at least one minute in the future.';
      return;
    }
    scheduleSaving = true; scheduledError = '';
    try {
      await editFundScheduled(selectedSchedule.request_id, {
        subject: scheduleSubject, body: scheduleBody, cc: scheduleCc,
        send_at: when.toISOString(),
      });
      const id = selectedSchedule.request_id;
      await loadAllScheduled();
      await openSchedule(id);
    } catch (err: any) {
      scheduledError = err?.message || 'Could not save this scheduled email.';
    } finally { scheduleSaving = false; }
  }

</script>

<svelte:window onclick={onWindowClick} onkeydown={handleReviewKey} />

{#if loading && !workspace}
  <div class="empty-state" role="status">Loading Investor Relations…</div>
{:else if workspace}
  <!-- A failed REFRESH keeps the page. `load()` runs again on every poll version
       bump, and ordering the error branch ahead of this one replaced a working
       workspace with an error card — the Sales Today shows a retry strip over
       the data instead. Only a first load with nothing to show falls through. -->
  {#if error}
    <div class="empty-state fund-reload-alert" role="status">
      Could not refresh this workspace, so you are seeing the last loaded data. {error}
      <button class="today-mini-btn" type="button" onclick={load}>Retry</button>
    </div>
  {/if}
  {#if view === 'today'}
    <div class="today-topbar"><SyncButton /></div>
    <TodayHero firstName={userName.split(' ')[0] || 'there'} agentName={agent}
      status={todayStatus} />
    {#if todayDecisionsError}<div class="fund-reload-alert" role="status">Mailbox suggestions are unavailable: {todayDecisionsError} <button class="today-mini-btn" onclick={loadTodayDecisions}>Retry</button></div>{/if}
    {#if scheduledError && !pendingSends.length}<div class="fund-reload-alert" role="status">Pending sends are unavailable: {scheduledError} <button class="today-mini-btn" onclick={loadAllScheduled}>Retry</button></div>{/if}
    {#if todayDraftError}<div class="fund-reload-alert" role="alert">{todayDraftError}</div>{/if}
    {#if todayBriefError}<div class="fund-reload-alert" role="alert">{todayBriefError}</div>{/if}
    <TodayNav active={spy.active} onJump={jumpToSection} items={todayNavItems} />
    <section class="today-section" id="fund-briefing" use:spyOn>
      <header class="today-section-header"><h2>Briefing</h2><span class="today-section-count">{todayBriefingTotal} prioritized · {todayFocusGroups.length} confirmed relationship{todayFocusGroups.length === 1 ? '' : 's'}</span></header>
      {#if !todaySignalsLoaded}
        <div class="today-empty" role="status">Checking calls, recorded next steps and synced LP mail…</div>
      {:else if todayBriefingTotal}
        {#each todayHighlights as highlight, i (highlight.key)}
          {@const display = briefingDisplay(highlight)}
          <div class="fund-briefing-tile tone-{display.tone}" class:open={!!todayExpanded[highlight.key]}
            role="button" tabindex="0" aria-expanded={!!todayExpanded[highlight.key]}
            onclick={(event) => { if (!(event.target as HTMLElement).closest('.fund-briefing-detail, button, input, select, [role="textbox"]')) toggleTodayHighlight(highlight); }}
            onkeydown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); toggleTodayHighlight(highlight); } }}>
            <span class="fund-briefing-num">{i + 1}</span>
            <div class="fund-briefing-body">
              <div class="fund-briefing-top"><strong>{highlight.name}</strong><span class="fund-briefing-pill">{display.tag}</span><span class="fund-briefing-chevron" aria-hidden="true">›</span></div>
              <p class="fund-briefing-preview">{highlight.detail}</p>
              {#if todayExpanded[highlight.key]}
                <p class="fund-briefing-source">{highlight.label}{#if highlight.source} · {highlight.source}{/if}</p>
                <div class="fund-briefing-detail" role="group" aria-label="Briefing for {highlight.name}">
                  <strong>Why this is here</strong>
                  <ul>{#each highlight.facts as fact}<li>{fact}</li>{/each}</ul>
                  {#if todayFocusKey === highlight.key && todayFocusGroup}
                    {#if todayFocusGroup.key.startsWith('institution:') && todayFocusMembers.length > 1}
                      <p class="fund-today-workbench-context">A new brief uses up to {todayBriefMemberIds.length} confirmed people from this institution. Review the LP page to choose different evidence or Fund files.</p>
                    {/if}
                    {#if todayFocusMembers.length > 1}
                      <div class="fund-today-workbench-controls">
                        <label for="fund-today-person">Person to email</label>
                        <select id="fund-today-person" value={todayFocusContactId} onchange={(event) => { todayFocusGeneration += 1; todayFocusContactId = event.currentTarget.value; todayFocusDraft = null; todayFocusBusy = ''; todayFocusError = ''; }}>
                          {#each todayFocusMembers as person (person.counterparty_id)}<option value={person.counterparty_id}>{person.display_name}</option>{/each}
                        </select>
                      </div>
                    {/if}
                    <div class="fund-today-workbench-actions">
                      <button type="button" class="today-mini-btn" disabled={!!todayFocusBusy || !todayFocusMembers.length} onclick={() => prepareTodayFocus('brief')}>{todayFocusBusy === 'brief' ? 'Preparing brief…' : todayFocusBrief?.brief ? 'Refresh brief' : 'Prepare LP brief'}</button>
                      <button type="button" class="today-mini-btn ghost" disabled={!!todayFocusBusy || !todayFocusPerson} onclick={() => prepareTodayFocus('draft')}>{todayFocusBusy === 'draft' ? 'Drafting…' : 'Draft email for review'}</button>
                    </div>
                    {#if todayFocusError}<p class="fund-reload-alert" role="alert">{todayFocusError}</p>{/if}
                    {#if todayFocusLoading}<p class="fund-today-workbench-context" role="status">Checking for a saved briefing…</p>{/if}
                    {#if todayFocusBrief?.brief}
                      <div class="fund-today-workbench-result" role="region" aria-label="LP briefing">
                        <div class="fund-today-workbench-result-head"><h4>LP briefing</h4><span>Generated for review · no email sent</span></div>
                        <div class="md-content">{@html renderSafeMarkdown(todayFocusBrief.brief)}</div>
                        {#if todayFocusBrief.source_manifest?.sources?.length}
                          <details class="fund-today-workbench-sources"><summary>{todayFocusBrief.source_manifest.sources.length} source{todayFocusBrief.source_manifest.sources.length === 1 ? '' : 's'} used</summary>
                            <ol>{#each todayFocusBrief.source_manifest.sources as source (source.number)}<li>{source.type === 'team_note' ? 'Team note' : 'Synced email'} · {source.contact_name} · {source.title || source.subject || 'Untitled'}</li>{/each}</ol>
                          </details>
                        {:else}<p class="fund-today-workbench-source-note">Open the LP record to inspect the numbered mail evidence and team notes behind this brief.</p>{/if}
                      </div>
                    {/if}
                    {#if todayFocusDraft}
                      <div class="fund-today-workbench-result" role="region" aria-label="LP email draft">
                        <div class="fund-today-workbench-result-head"><h4>Email draft</h4><span>For {todayFocusPerson?.display_name || 'selected LP'} · review before sending</span></div>
                        <label for="fund-today-draft-subject">Subject</label>
                        <input id="fund-today-draft-subject" bind:value={todayFocusDraft.subject} />
                        <span class="fund-today-workbench-field-label">Message</span>
                        <EmailBodyEditor bind:value={todayFocusDraft.body} rows={9} />
                        <div class="fund-today-workbench-actions"><button type="button" class="today-mini-btn" disabled={!todayFocusDraft.subject.trim() || !todayFocusDraft.body.trim()} onclick={reviewTodayFocusDraft}>Review in email composer →</button></div>
                      </div>
                    {/if}
                  {/if}
                </div>
              {/if}
            </div>
            <button class="fund-briefing-verb" onclick={(event) => { event.stopPropagation(); openTodayHighlight(highlight); }}>{display.verb}</button>
          </div>
        {/each}
        {#if dashboardMailError}<p class="fund-alert" role="status">Mail activity is unavailable: {dashboardMailError} <button class="today-mini-btn" onclick={loadDashboardMailActivity}>Retry</button></p>{/if}
      {:else if !todayDecisionsError && !dashboardMailError && !todayCallsError}
        <!-- The three onboarding states used to live in the "Next steps" section, which
             the spec folds into Chase. They land here instead: Briefing is the first
             section, and an account with no inbox has nothing to chase. -->
        <div class="today-empty">
          {#if !workspace.inbox_connected}
            Connect a Google or Microsoft mailbox to bring real relationships into Today.
            {#if !isSupportView()}<a class="today-mini-btn" href={workspace.provider === 'microsoft' ? '/api/auth/microsoft/login' : '/api/auth/login?reauth=true'}>Connect inbox →</a>{/if}
          {:else if !allContacts.length}
            {$pollingActive ? 'Your relationships will appear here as the mailbox sync progresses.' : 'Your inbox is connected. Sync to discover external contacts and their email history.'}
            <SyncButton />
          {:else if !investors.length}
            {allContacts.length} external contact{allContacts.length === 1 ? '' : 's'} discovered, none confirmed as an LP yet.
          <button class="today-mini-btn" onclick={openInvestorReview}>Review discovered contacts →</button>
          {:else}
            No call, due follow-up or recent LP mail needs a briefing right now. Review all {todayFocusGroups.length} confirmed relationship{todayFocusGroups.length === 1 ? '' : 's'} on the Dashboard.
            <button class="today-mini-btn" onclick={() => $currentView = 'dashboard'}>View relationships →</button>
          {/if}
        </div>
      {:else}
        <div class="today-empty" role="status">Some mailbox context is unavailable. Your recorded relationships are still available in Contacts. <button class="today-mini-btn" onclick={() => { loadTodayDecisions(); loadDashboardMailActivity(); }}>Retry</button></div>
      {/if}
    </section>
    {#if pendingSends.length}
      <section class="today-section" id="fund-sends">
        <header class="today-section-header"><h2>Going out soon</h2><span class="today-section-count">{pendingSends.length} pending</span></header>
        {#if scheduledError}<p class="fund-alert" role="alert">{scheduledError}</p>{/if}
        {#each pendingSends.slice(0, 5) as send (send.request_id)}
          <div class="fund-send-row"><time datetime={send.send_at}>{new Date(send.send_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</time><div><strong>{send.subject}</strong><small>To {send.recipient}</small></div><button class="today-mini-btn" onclick={() => { $currentView = 'scheduled'; openSchedule(send.request_id); }}>Review →</button><button class="today-mini-btn ghost" onclick={() => cancelFromSchedule(send.request_id)}>Cancel</button></div>
        {/each}
        {#if pendingSends.length > 5}<button class="today-mini-btn ghost today-expand-btn" onclick={() => $currentView = 'scheduled'}>See all {pendingSends.length} scheduled emails →</button>{/if}
      </section>
    {/if}
    <section class="today-section" id="fund-calls" use:spyOn>
      <header class="today-section-header"><h2>Calls today</h2><span class="today-section-count">{todayCalls.length} scheduled</span></header>
      {#if todayCallsError}
        <div class="today-empty" role="status">LP calendar calls are unavailable. {todayCallsError} <button class="today-mini-btn" onclick={loadTodayCalls}>Retry</button></div>
      {:else if todayCallsLoading && !todayCalls.length}
        <div class="today-empty">Checking your calendar…</div>
      {:else if todayCalls.length}
        {#each (todayExpanded.calls ? todayCalls : todayCalls.slice(0, 3)) as call (call.id)}
          <article class="fund-call-card"><time datetime={call.start}>{new Date(call.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</time><div class="fund-call-main"><h3>{call.summary}</h3><p>{[...new Set(call.investors.map(person => { const contact = allContacts.find(item => item.counterparty_id === person.counterparty_id); return contact ? (relationshipInstitution(contact)?.name || organizationHint(contact)?.name || person.display_name) : person.display_name; }))].join(', ')}</p></div><div class="fund-today-actions">{#each call.investors as person (person.counterparty_id)}<button class="today-mini-btn" disabled={!!todayBriefContactId} onclick={() => prepareTodayBrief(person.counterparty_id)}>{todayBriefContactId === person.counterparty_id ? 'Preparing…' : `Prepare brief for ${person.display_name} →`}</button>{/each}</div></article>
        {/each}
        {#if todayCalls.length > 3}<button class="today-mini-btn ghost today-expand-btn" onclick={() => todayExpanded.calls = !todayExpanded.calls}>{todayExpanded.calls ? 'Show fewer ↑' : `Show ${todayCalls.length - 3} more ↓`}</button>{/if}
      {:else}<div class="today-empty">No calls with LPs on your calendar today.</div>{/if}
    </section>
    <!-- No "Next steps" section: a recorded follow-up that is DUE is a Chase row, which
         is how sales Today does it. See the Chase section below. -->
    <section class="today-section" id="fund-respond" use:spyOn>
      <header class="today-section-header"><h2>Respond</h2><span class="today-section-count">{respondGroups.length} conversation{respondGroups.length === 1 ? '' : 's'} · {respondDecisions.filter(item => item.ready_draft).length} ready draft{respondDecisions.filter(item => item.ready_draft).length === 1 ? '' : 's'}</span></header>
      {#if respondGroups.length}
        {#each (todayExpanded.respond ? respondGroups : respondGroups.slice(0, 5)) as group, i (group.key)}
          <details class="fund-today-tile"><summary><span class="fund-today-num">{i + 1}</span><span class="fund-today-tile-main"><strong>{group.name}</strong>{#if group.source}<small>{group.source}</small>{/if}<span>{group.decisions[0].subject || '(No subject)'} · {emailListDate(group.decisions[0].sent_at)}</span></span><span class="fund-today-tile-count">{group.decisions.length} message{group.decisions.length === 1 ? '' : 's'}</span><span class="fund-today-chevron" aria-hidden="true">›</span></summary>
            <div class="fund-today-detail">{#each group.decisions as decision (`${decision.counterparty_id}:${decision.message_id}`)}<div class="fund-today-person"><div><strong>{decision.display_name}</strong><p>{decision.subject || '(No subject)'} · Received {emailListDate(decision.sent_at)}</p>{#if decision.ready_draft}<details class="fund-ready-draft"><summary>Draft ready for review · {decision.ready_draft.subject}</summary><p>{decision.ready_draft.body}</p><small>Based on {decision.ready_draft.source_manifest.evidence?.length || 0} messages from your mailbox · never sent automatically</small></details>{/if}</div><div class="fund-today-actions">{#if decision.ready_draft}<button class="today-mini-btn" onclick={() => reviewReadyTodayDraft(decision)}>Review draft →</button>{:else}<button class="today-mini-btn" onclick={() => openTodayReply(decision)}>Reply in thread →</button><button class="today-mini-btn ghost" disabled={!!todayDraftMessageId} onclick={() => prepareTodayDraft(decision)}>{todayDraftMessageId === decision.message_id ? 'Preparing…' : 'Draft with AI'}</button>{/if}<button class="today-mini-btn ghost" onclick={() => actOnTodayDecision(decision, 'snooze')}>Snooze 7d</button><button class="today-mini-btn ghost" onclick={() => actOnTodayDecision(decision, 'dismiss')}>Dismiss</button></div></div>{/each}</div>
          </details>
        {/each}
        {#if respondGroups.length > 5}<button class="today-mini-btn ghost today-expand-btn" onclick={() => todayExpanded.respond = !todayExpanded.respond}>{todayExpanded.respond ? 'Show fewer ↑' : `Show ${respondGroups.length - 5} more ↓`}</button>{/if}
      {:else if !todayDecisionsLoaded}<div class="today-empty" role="status">Checking synced LP mail…</div>
      {:else if !todayDecisionsError}<div class="today-empty">No LP email is waiting on a reply.</div>{/if}
    </section>
    <section class="today-section" id="fund-chase" use:spyOn>
      <header class="today-section-header"><h2>Chase</h2><span class="today-section-count">{chaseTotal} item{chaseTotal === 1 ? '' : 's'} · {dueActions.length} recorded follow-up{dueActions.length === 1 ? '' : 's'} · {chaseDecisions.length} mail timing check{chaseDecisions.length === 1 ? '' : 's'}</span></header>
      {#if chaseItems.length}
        {#each (todayExpanded.chase ? chaseItems : chaseItems.slice(0, 5)) as item, i (item.key)}
          {@const contact = item.contact}
          {@const decision = item.decision}
          {@const organization = contact ? organizationHint(contact) : null}
          {@const pill = contact && !decision ? statusPill(contact) : null}
          <article class="fund-chase-row" class:fund-chase-due={!decision} class:fund-chase-overdue={pill?.label === 'Overdue'}>
            <span class="fund-briefing-num">{i + 1}</span>
            <div class="fund-chase-main">
              <div class="fund-chase-top">
                <strong>{contact?.display_name || decision?.display_name}</strong>
                {#if organization}<span class="fund-chase-organization">{organization.name}</span>{:else}<span class="fund-chase-organization">No institution linked</span>{/if}
                <span class="fund-stage-pill" style="color:{chaseStage(contact).color};border-color:{chaseStage(contact).color};">{chaseStage(contact).label}</span>
                {#if pill}<span class="status-pill {pill.cls}">{pill.label}</span>{/if}
              </div>
              {#if decision}
                <p class="fund-chase-context">Mail timing check · “{decision.subject || '(No subject)'}” · sent {emailListDate(decision.sent_at)} ({decision.age_days}d ago)</p>
                {#if decision.ready_draft}<details class="fund-ready-draft"><summary>Draft ready for review · {decision.ready_draft.subject}</summary><p>{decision.ready_draft.body}</p><small>Based on {decision.ready_draft.source_manifest.evidence?.length || 0} messages from your mailbox · never sent automatically</small></details>{/if}
              {:else if contact}
                <p class="fund-chase-context">Recorded next step · {relationshipNextStep(contact)}{#if relationshipDue(contact)} · due {emailListDate(relationshipDue(contact))}{/if}</p>
              {/if}
            </div>
            <div class="fund-chase-actions">
              {#if contact}<button class="today-mini-btn" onclick={() => openFollowUp(contact)}>Follow up</button>{/if}
              {#if decision}
                {#if decision.ready_draft}<button class="today-mini-btn" onclick={() => reviewReadyTodayDraft(decision)}>Review draft →</button>{:else}<button class="today-mini-btn ghost" disabled={!!todayDraftMessageId} onclick={() => prepareTodayDraft(decision)}>{todayDraftMessageId === decision.message_id ? 'Preparing…' : 'Draft with AI'}</button>{/if}
                <button class="today-mini-btn ghost" onclick={() => actOnTodayDecision(decision, 'snooze')}>Snooze 7d</button>
                <button class="today-mini-btn ghost" onclick={() => actOnTodayDecision(decision, 'dismiss')}>Dismiss</button>
              {:else if contact}
                <button class="today-mini-btn ghost" disabled={!!todayBriefContactId} onclick={() => prepareTodayBrief(contact.counterparty_id)}>{todayBriefContactId === contact.counterparty_id ? 'Preparing…' : 'Prepare brief'}</button>
              {/if}
            </div>
          </article>
        {/each}
        {#if chaseItems.length > 5}<button class="today-mini-btn ghost today-expand-btn" onclick={() => todayExpanded.chase = !todayExpanded.chase}>{todayExpanded.chase ? 'Show fewer ↑' : `Show ${chaseItems.length - 5} more ↓`}</button>{/if}
      {:else if !todayDecisionsLoaded}<div class="today-empty" role="status">Checking synced LP mail…</div>
      {:else if !todayDecisionsError}<div class="today-empty">Nothing to chase: no recorded follow-up is due and no sent email needs a timing review.</div>{/if}
    </section>
    <section class="today-section" id="fund-new-lps" use:spyOn>
      <header class="today-section-header"><h2>Possible LPs</h2><span class="today-section-count">{newLpGroups.length} with an investment signal</span></header>
      {#if newLpGroups.length}
        {#each (todayExpanded.newLps ? newLpGroups : newLpGroups.slice(0, 5)) as group, i (group.key)}
          <details class="fund-today-tile"><summary><span class="fund-today-num">{i + 1}</span><span class="fund-today-tile-main"><strong>{group.name}</strong>{#if group.source}<small>{group.source}</small>{/if}<span>{group.contacts.length} suggested person{group.contacts.length === 1 ? '' : 's'} to review</span></span><span class="fund-today-tile-count">Unconfirmed</span><span class="fund-today-chevron" aria-hidden="true">›</span></summary>
            <div class="fund-today-detail">{#each group.contacts as contact (contact.counterparty_id)}<div class="fund-today-person"><div><strong>{contact.display_name}</strong><p>{contact.suggested_reason || 'Suggested from limited direct mail'}</p></div><div class="fund-today-actions"><button class="today-mini-btn" onclick={() => openContact(contact)}>Review relationship →</button><button class="today-mini-btn ghost" disabled={!!savingRole} onclick={() => setRole(contact, 'investor')}>Confirm LP</button></div></div>{/each}</div>
          </details>
        {/each}
        {#if newLpGroups.length > 5}<button class="today-mini-btn ghost today-expand-btn" onclick={() => todayExpanded.newLps = !todayExpanded.newLps}>{todayExpanded.newLps ? 'Show fewer ↑' : `Show ${newLpGroups.length - 5} more ↓`}</button>{/if}
        <!-- The section's one way out, per spec r6: the Review LPs page is the
             dashboard's sorting tab, which is titled exactly that. -->
        <div class="fund-plist-foot"><button class="today-mini-btn" onclick={() => { dashTab = 'sorting'; $currentView = 'dashboard'; }}>Review LPs →</button></div>
      {:else}<div class="today-empty">No contact in synced mail carries an investment signal yet.</div>{/if}
    </section>
    <section class="today-section" id="fund-quiet" use:spyOn>
      <header class="today-section-header"><h2>Quiet</h2><span class="today-section-count">{quietGroups.length} with no email for 30+ days</span></header>
      {#if quietGroups.length}
        {#each (todayExpanded.quiet ? quietGroups : quietGroups.slice(0, 3)) as group, i (group.key)}
          {@const latest = groupLatestMail(group.contacts)}
          <details class="fund-today-tile"><summary><span class="fund-today-num">{i + 1}</span><span class="fund-today-tile-main"><strong>{group.name}</strong>{#if group.source}<small>{group.source}</small>{/if}<span>Last synced email {emailListDate(latest?.last_seen)}</span></span><span class="fund-today-tile-count">Review context</span><span class="fund-today-chevron" aria-hidden="true">›</span></summary>
            <div class="fund-today-detail"><p class="fund-today-note">Check calls and notes before deciding whether to follow up.</p><div class="fund-today-actions"><button class="today-mini-btn" onclick={() => group.organization ? openOrganization(group.key) : openContact(group.contacts[0])}>Review relationship →</button>{#if !groupNextAction(group.contacts)}<button class="today-mini-btn ghost" onclick={() => openFollowUp(group.contacts[0])}>Add follow-up</button>{/if}</div></div>
          </details>
        {/each}
        {#if quietGroups.length > 3}<button class="today-mini-btn ghost today-expand-btn" onclick={() => todayExpanded.quiet = !todayExpanded.quiet}>{todayExpanded.quiet ? 'Show fewer ↑' : `Show ${quietGroups.length - 3} more ↓`}</button>{/if}
      {:else}<div class="today-empty">No confirmed LP has gone 30+ days without an email.</div>{/if}
    </section>
  {:else if view === 'dashboard'}
    <div class="fund-page-heading"><div class="page-title">{dashTab === 'sorting' ? 'Review' : dashTab === 'portfolio' ? 'Portfolio context' : 'LP Dashboard'}</div><SyncButton /></div>
    {#if actionError}<p class="fund-alert" role="alert">{actionError}</p>{/if}
    <!-- Named for the management company, not for one vehicle: a single org-level IR
         workspace covers every fund the team raises for. -->
    <div class="page-subtitle">{dashTab === 'sorting' ? 'Suggestions from synced mail' : workspace.profile.manager_name} · {new Date().toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
    <div class="tabs fund-dashboard-tabs" role="tablist" aria-label="Dashboard relationships">
      <button type="button" class="tab" role="tab" aria-selected={dashTab === 'ir'} class:active={dashTab === 'ir'} onclick={() => applySavedView(ALL_INVESTORS_KEY)}>LPs <span class="tab-count">{investors.length}</span></button>
      <button type="button" class="tab" role="tab" aria-selected={dashTab === 'portfolio'} class:active={dashTab === 'portfolio'} onclick={() => applySavedView(PORTFOLIO_VIEW_KEY)}>Portfolio <span class="tab-count">{portfolio.length}</span></button>
      <button type="button" class="tab" role="tab" aria-selected={dashTab === 'sorting'} class:active={dashTab === 'sorting'} onclick={openInvestorReview}>Needs review <span class="tab-count">{unreviewedPeople.length}</span></button>
    </div>
    {#if dashTab === 'ir' && unreviewedPeople.length}
      <div class="fund-review-strip">
        <span class="status-pill status-pill-amber">Needs review</span>
        <span><b>{unreviewedPeople.length}</b> contact{unreviewedPeople.length === 1 ? '' : 's'} to review</span>
        <button class="navlink" onclick={openInvestorReview}>Review LPs →</button>
      </div>
    {/if}
    {#if dashTab === 'sorting'}
      <button class="fund-row-link fund-ir-back" onclick={() => dashTab = 'ir'}>← LP Dashboard</button>
      <p class="fund-review-intro">Only uncertain relationship suggestions need your call. Company investment does not establish a Fund LP relationship.</p>
      <div class="fund-review-progress" aria-label={`${reviewDoneCount} of ${reviewRows.length} reviewed`}>
        <span class="fund-review-progress-track"><span style={`width:${reviewRows.length ? reviewDoneCount / reviewRows.length * 100 : 0}%`}></span></span>
        <span>{reviewDoneCount} of {reviewRows.length} reviewed</span>
        <span class="fund-review-keys"><kbd>↑</kbd><kbd>↓</kbd> move · <kbd>I</kbd> <kbd>P</kbd> <kbd>N</kbd> set</span>
      </div>
    {/if}
    <!-- The Sales dashboard's saved-view bar, same classes, same control: the trigger
         names the active view, `· modified` appears when the chips have drifted off the
         preset, and the menu renames / deletes / bookmarks in place. `.dash-view-bar`
         and friends live in app.css. -->
    {#if dashTab !== 'sorting'}
    <div class="dash-view-bar">
      <div class="vdt" role="button" tabindex="0" aria-haspopup="menu" aria-expanded={viewDropdownOpen}
        onclick={() => viewDropdownOpen = !viewDropdownOpen}
        onkeydown={(event) => { if (event.key === 'Enter' || event.key === ' ') viewDropdownOpen = !viewDropdownOpen; }}>
        {#if activeFundView.bookmarked}<span class="vdt-bm" aria-label="Bookmarked"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg></span>{/if}
        <span class="vdt-name">{activeFundView.name}</span>
        <span class="vdt-arrow">▼</span>
      </div>
      {#if dashTab === 'ir' && fundViewDirty}<span class="vdt-dirty">· modified</span>{/if}
      <span class="count-label"><b>{dashTab === 'portfolio' ? portfolioRows.length : irEntities.length}</b> {dashTab === 'portfolio' ? 'portfolio contacts' : 'LPs'}</span>
      <div class="dash-view-spacer"></div>
      <!-- The name box appears only while naming, so the resting state of this row is
           the trigger alone — not four controls. -->
      {#if dashTab === 'ir' && viewNaming}
        <input aria-label="Name for new relationship view" bind:value={newViewName} maxlength="60" placeholder="Name this view" />
        <button class="dash-save-btn" disabled={viewSaving || !newViewName.trim()} onclick={saveCurrentView}>Save</button>
        <button class="fund-row-link" onclick={() => { viewNaming = false; newViewName = ''; }}>Cancel</button>
      {:else if dashTab === 'ir' && fundViewDirty}
        {#if !activeFundView.builtin}
          <button class="dash-save-btn" disabled={viewSaving} onclick={updateActiveFundView}
            title={`Overwrite “${activeFundView.name}” with the current filters + sort`}>Update view</button>
        {/if}
        {#if irFiltersSet}<button class="dash-save-btn ghost" onclick={openSaveAsNew}>Save as new</button>{/if}
      {:else if dashTab === 'ir'}
        <button class="dash-save-btn ghost" disabled>+ Save current view</button>
      {/if}
      {#if viewDropdownOpen}
        <div class="vdm" role="menu">
          <div class="vdm-section-label">Bookmarked</div>
          {#each allFundViews.filter(v => v.bookmarked) as v (v.key)}
            {@render fundViewRow(v)}
          {/each}
          {#if allFundViews.some(v => !v.bookmarked)}
            <div class="vdm-divider"></div>
            <div class="vdm-section-label">All views</div>
            {#each allFundViews.filter(v => !v.bookmarked) as v (v.key)}
              {@render fundViewRow(v)}
            {/each}
          {/if}
        </div>
      {/if}
      {#if viewError}<span class="fund-alert" role="alert">{viewError}</span>{/if}
    </div>
    {/if}
    <!-- One dropdown row: bookmark toggle · name (inline-editable) · count · hover
         rename/delete. "All LPs" is the builtin and owns none of those edits. -->
    {#snippet fundViewRow(v: FundSavedView)}
      <div class="vdm-item" class:active={activeViewKey === v.key} class:editing={editingViewKey === v.key}
        role="menuitem" tabindex="0"
        onclick={() => { if (editingViewKey !== v.key) applyFundView(v); }}
        onkeydown={(event) => { if (editingViewKey !== v.key && (event.key === 'Enter' || event.key === ' ')) applyFundView(v); }}>
        <button class="vdm-bm" class:on={v.bookmarked} type="button" aria-label="Toggle bookmark"
          disabled={v.builtin}
          title={v.builtin ? 'The default view is always bookmarked'
            : v.bookmarked ? 'Remove bookmark' : 'Bookmark (show in shortcuts)'}
          onclick={(event) => toggleFundViewBookmark(v, event)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
        </button>
        {#if editingViewKey === v.key}
          <input class="vdm-edit" value={v.name} aria-label="Rename view" use:focusSelect
            onclick={(event) => event.stopPropagation()}
            onkeydown={(event) => {
              event.stopPropagation();
              if (event.key === 'Enter') { renameFundView(v, event.currentTarget.value); editingViewKey = null; }
              else if (event.key === 'Escape') editingViewKey = null;
            }}
            onblur={(event) => { renameFundView(v, event.currentTarget.value); editingViewKey = null; }} />
        {:else}
          <span class="vdm-name">{v.name}</span>
          {#if !v.builtin}
            <button class="vdm-act vdm-rename" title="Rename view" aria-label="Rename view"
              onclick={(event) => { event.stopPropagation(); editingViewKey = v.key; }}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
            </button>
            <button class="vdm-act vdm-del" title="Delete view" aria-label="Delete view"
              onclick={(event) => deleteFundView(v, event)}>✕</button>
          {/if}
          <span class="vdm-count">{fundViewCount(v.preset)}</span>
        {/if}
      </div>
    {/snippet}
    <div class="page-toolbar">
      <input class="page-toolbar-search" bind:value={search} placeholder="Search name or email…" aria-label="Search relationships" />
      {#if dashTab === 'ir'}
        <!-- Same chip grammar as the Sales dashboard — `.fchip*` lives in app.css and
             is shared, so this adds no styling of its own. Counts are over the search
             result, so ticking one chip never renumbers its siblings. -->
        <div class="fchip-wrap">
          <button class="fchip" class:set={irStageFilter.length > 0} aria-haspopup="menu"
                  aria-expanded={openFilterDD === 'stage'}
                  onclick={() => openFilterDD = openFilterDD === 'stage' ? null : 'stage'}>
            Stage <span class="fchip-v">{irStageLbl}</span> <span class="fchip-ar">▾</span>
          </button>
          {#if openFilterDD === 'stage'}
            <div class="fchip-dd" role="menu">
              {#each IR_STAGE_OPTIONS as option}
                {@const n = irSearched.filter(c => (relationshipStage(c) || '') === option.key).length}
                <button class="fchip-opt" class:on={irStageFilter.includes(option.key)} class:empty={n === 0}
                        onclick={() => irStageFilter = toggleIrFilter(irStageFilter, option.key)}>
                  <span class="fchip-check">✓</span>{option.label}<span class="fchip-count">{n}</span>
                </button>
              {/each}
            </div>
          {/if}
        </div>
        <div class="fchip-wrap">
          <button class="fchip" class:set={irStatusFilter.length > 0} aria-haspopup="menu"
                  aria-expanded={openFilterDD === 'status'}
                  onclick={() => openFilterDD = openFilterDD === 'status' ? null : 'status'}>
            Status <span class="fchip-v">{irStatusLbl}</span> <span class="fchip-ar">▾</span>
          </button>
          {#if openFilterDD === 'status'}
            <div class="fchip-dd" role="menu">
              {#each IR_STATUS_OPTIONS as option}
                {@const n = irSearched.filter(c => followUpStatus(c) === option.key).length}
                <button class="fchip-opt" class:on={irStatusFilter.includes(option.key)} class:empty={n === 0}
                        onclick={() => irStatusFilter = toggleIrFilter(irStatusFilter, option.key)}>
                  <span class="fchip-check">✓</span>{option.label}<span class="fchip-count">{n}</span>
                </button>
              {/each}
            </div>
          {/if}
        </div>
        <!-- Review belongs to its own page, never in the filed LP table. -->
        {#if (workspace.institutions || []).length}
          <div class="fchip-wrap">
            <button class="fchip" class:set={!!irInstitutionFilter} aria-haspopup="menu"
                    aria-expanded={openFilterDD === 'institution'}
                    onclick={() => openFilterDD = openFilterDD === 'institution' ? null : 'institution'}>
              LP institution <span class="fchip-v">{irInstitutionLbl}</span> <span class="fchip-ar">▾</span>
            </button>
            {#if openFilterDD === 'institution'}
              <div class="fchip-dd" role="menu">
                {#each workspace.institutions || [] as institution}
                  {@const n = irSearched.filter(c => c.institution_id === institution.institution_id).length}
                  <button class="fchip-opt" class:on={irInstitutionFilter === institution.institution_id} class:empty={n === 0}
                          onclick={() => { irInstitutionFilter = irInstitutionFilter === institution.institution_id ? '' : institution.institution_id; openFilterDD = null; }}>
                    <span class="fchip-check">✓</span>{institution.name}<span class="fchip-count">{n}</span>
                  </button>
                {/each}
              </div>
            {/if}
          </div>
        {/if}
        <button class="fchip" class:set={irPinnedOnly} aria-pressed={irPinnedOnly}
                onclick={() => irPinnedOnly = !irPinnedOnly}>
          Pinned <span class="fchip-v">{irPinnedOnly ? 'Only' : 'All'}</span>
        </button>
        <!-- No Sort select: the column headers sort, the way they do on the Sales
             dashboard and in the spec. -->
        {#if irFiltersSet}<button class="fchip-clear" onclick={clearIrFilters}>Clear ✕</button>{/if}
      {/if}
    </div>
    {#if !workspace.inbox_connected && !allContacts.length && dashTab !== 'portfolio'}
      <div class="empty-state">Connect your inbox in Settings to discover contacts.</div>
    {:else if dashTab === 'sorting'}
      {#if !reviewRows.length}
        <div class="empty-state">{unreviewedPeople.length ? 'No review candidates match this search.' : 'Nothing needs confirmation. Mike files clear Fund relationships directly into LPs or Portfolio.'}</div>
      {:else}
        <div class="data-table-wrap"><table class="data-table fund-review-table">
          <colgroup><col class="fund-review-gutter" /><col class="fund-review-who" /><col class="fund-review-from" /><col class="fund-review-action-col" /></colgroup>
          <thead><tr><th></th><th>Who</th><th>From</th><th>What is this?</th></tr></thead>
          <tbody>
            {#each reviewGroups as group (group.key)}
              {@const pending = group.rows.filter(c => !reviewDone[c.counterparty_id])}
              {@const visible = group.rows.slice(0, reviewMore[group.key] ? undefined : REVIEW_INITIAL_ROWS)}
              <tr class="fund-review-group"><td></td><td colspan="3"><div class="fund-review-group-content">
                <strong>{group.label}</strong><span class="fund-review-count">{pending.length}</span>
              </div></td></tr>
              {#each visible as contact (contact.counterparty_id)}
                {@const done = reviewDone[contact.counterparty_id]}
                <tr id={`fund-review-${contact.counterparty_id}`} class="fund-review-row" class:done={!!done} class:current={reviewCursor === contact.counterparty_id}>
                  <td class="fund-review-marker">{#if !done && reviewCursor === contact.counterparty_id}<span aria-hidden="true"></span>{/if}</td>
                  <td><div class="fund-review-person"><button type="button" onclick={() => openContact(contact)}>{contact.display_name}</button><span>{contact.email || 'No email address'}</span>{#if organizationHint(contact)}<span>Suggested organization: {organizationHint(contact)?.name}</span>{/if}</div></td>
                  <td><div class="fund-review-mail"><button type="button" onclick={() => openContact(contact)}>{contact.sample_subjects?.[0] || 'View email context →'}</button><span>{mailDate(contact.last_seen, 'Date unknown')}</span></div></td>
                  <td class="fund-review-action">{#if done}
                    <span>{done.role === 'investor' ? 'LP · on dashboard' : done.role === 'portfolio' ? 'Portfolio · on dashboard' : 'Neither · in Contacts'}</span>
                    <button type="button" class="fund-review-undo" disabled={!!savingRole} onclick={() => undoReviewRole(contact)}>Undo</button>
                  {:else}
                    <div class="fund-review-segment" role="group" aria-label={`What is ${contact.display_name}?`}>
                      {#each ([['investor', 'LP'], ['portfolio', 'Portfolio'], ['neither', 'Neither']] as const) as [role, label]}
                        <button type="button" class:suggested={contact.suggested_role === role} disabled={!contact.email || !!savingRole}
                          aria-label={`${label} for ${contact.display_name}`} onclick={() => setReviewRole(contact, role)}>{#if contact.suggested_role === role}<span aria-hidden="true">✦ </span>{/if}{label}</button>
                      {/each}
                    </div>
                  {/if}</td>
                </tr>
              {/each}
              {#if group.rows.length > visible.length}
                <tr class="fund-review-more"><td></td><td colspan="3"><button type="button" onclick={() => reviewMore = { ...reviewMore, [group.key]: true }}>Show {group.rows.length - visible.length} more</button></td></tr>
              {/if}
            {/each}
          </tbody>
        </table></div>
        {#if reviewDoneCount === reviewRows.length}
          <div class="fund-review-finish"><strong>All {reviewRows.length} reviewed.</strong> Confirmed LPs and portfolio contacts are in their dashboard tabs; Neither contacts are in Contacts.
            <button type="button" class="navlink" onclick={() => dashTab = 'ir'}>Back to LP Dashboard →</button></div>
        {/if}
      {/if}
      {#if autoFiledInvestors.length}
        <div class="fund-review-summary"><span class="fund-ir-sparkle" aria-hidden="true">✦</span>
          <span>Mike filed <strong>{autoFiledInvestors.length} LP{autoFiledInvestors.length === 1 ? '' : 's'}</strong> from direct investment evidence. Review them on the dashboard.</span>
          <button type="button" class="fund-row-link" onclick={() => applySavedView(ALL_INVESTORS_KEY)}>See them →</button>
        </div>
      {/if}
      {#if filteredAutomated.length}
        <div class="fund-review-summary"><span class="fund-ir-sparkle" aria-hidden="true">✦</span>
          <span><strong>{filteredAutomated.length} automated address{filteredAutomated.length === 1 ? '' : 'es'}</strong> kept out of relationship review.</span>
          <button type="button" class="fund-row-link" onclick={() => { roleFilter = 'all'; search = ''; $currentView = 'contacts'; }}>View in Contacts →</button>
        </div>
      {/if}
      <div class="fund-review-scope" role="note">
        <strong>Why this number?</strong>
        <p>Showing {reviewVisibleCount} of {reviewRows.length} {searchTerm ? 'matching ' : ''}Review row{reviewRows.length === 1 ? '' : 's'}. {unreviewedPeople.length} contact{unreviewedPeople.length === 1 ? '' : 's'} need final confirmation across the full queue. Plausible company investment discussions also appear here for your decision; automated contacts stay out.</p>
        <p>Initial mailbox sync checks the last 90 days. Later syncs add new mail. {workspace.preferences.include_personal_emails ? 'Personal addresses are included.' : 'Personal addresses are excluded by your mailbox setting.'}{#if $fundSyncFilterSummary} Last sync: {$fundSyncFilterSummary}.{/if}</p>
      </div>
    {:else if dashTab === 'ir'}
        {#if pinError}<p class="fund-alert" role="alert">{pinError}</p>{/if}
        {#if dashboardMailError}<p class="fund-alert" role="alert">Mail activity is unavailable: {dashboardMailError}</p>{/if}
        <!-- The six-column Sales skeleton. The first column is the pin/caret edge;
             names and all expanded content begin at the second column's edge. -->
        <div class="data-table-wrap" onscroll={() => openStageDD = null}><table class="data-table fund-ir-table"><thead><tr>
          <th></th>
          <th class="th-sort" onclick={() => sortIr('name')}>LP<span class="sort-ind" class:active={irSort === 'name'}>{irSortIndicator('name')}</span></th>
          <th class="th-sort" onclick={() => sortIr('status')}>Status<span class="sort-ind" class:active={irSort === 'status'}>{irSortIndicator('status')}</span></th>
          <th class="th-sort" onclick={() => sortIr('due')}>Follow-up<span class="sort-ind" class:active={irSort === 'due'}>{irSortIndicator('due')}</span></th>
          <th class="num th-sort" onclick={() => sortIr('inbound')}>Inbound<span class="sort-ind" class:active={irSort === 'inbound'}>{irSortIndicator('inbound')}</span></th>
          <th class="num th-sort" onclick={() => sortIr('outbound')}>Outbound<span class="sort-ind" class:active={irSort === 'outbound'}>{irSortIndicator('outbound')}</span></th>
        </tr></thead><tbody>
          {#if !irRows.length}
            <tr><td colspan="6" class="empty-state">{investors.length ? 'No LPs match this view.' : unreviewedPeople.length ? `No LPs filed yet. ${unreviewedPeople.length} contact${unreviewedPeople.length === 1 ? '' : 's'} need final confirmation in Review.` : 'No LPs filed yet. Sync your inbox or add a relationship in Contacts.'}</td></tr>
          {:else}
          {#each irGroups as group (group.key)}
            {@const open = expandedIrStages.has(group.key)}
            <tr class="group-row" role="button" tabindex="0"
                onclick={() => toggleIrStageGroup(group.key)}
                onkeydown={(event) => (event.key === 'Enter' || event.key === ' ') && toggleIrStageGroup(group.key)}>
              <td class="fund-ir-group-gutter"><span class="group-arrow">{open ? '▼' : '▶'}</span><span class="stage-dot" class:fund-ir-former-dot={group.key === 'former_lp'} style="background:{group.key === 'former_lp' ? 'transparent' : group.color};"></span></td>
              <td colspan="5" class="fund-ir-group-name"><span class="group-name">{group.label}</span><span class="group-meta">{group.rows.length} LP{group.rows.length === 1 ? '' : 's'}</span></td>
            </tr>
            {#if open}
              {#each group.rows as organization (organization.key)}
                {@const orgOpen = expandedFundOrganizations.has(organization.key)}
                {@const action = organization.organization && !organization.key.startsWith('institution:')
                  ? null : groupNextAction(organization.contacts)}
                {@const pill = action ? statusPill(action) : null}
                {@const inbound = groupMailActivity(organization.contacts, 'inbound')}
                {@const outbound = groupMailActivity(organization.contacts, 'outbound')}
                <!-- The whole row expands. Navigation is the single action in its rail. -->
                <tr class="data-row fund-ir-entity" class:open={orgOpen}
                    role="button" tabindex="0" aria-expanded={orgOpen}
                    onclick={() => toggleFundOrganization(organization.key)}
                    onkeydown={(event) => (event.key === 'Enter' || event.key === ' ') && toggleFundOrganization(organization.key)}>
                  <td class="fund-ir-pin">{#if entityPinnable(organization)}<button class="star-btn" class:on={entityPinned(organization)} disabled={savingPin} aria-label={`${entityPinned(organization) ? 'Unpin' : 'Pin'} ${organization.name}`} onclick={(event) => { event.stopPropagation(); toggleEntityPin(organization); }}>{entityPinned(organization) ? '★' : '☆'}</button>{/if}</td>
              <td class="fund-ir-main"><span class="fund-ir-entity-name">{organization.name}</span>{#if organization.contacts.some(c => c.set_by === 'model')}<span class="fund-ir-secondary" title="Filed from direct synced-mail evidence. Open to correct the role."> ✦ Filed by Mike</span>{/if}</td>
                  <td>{#if pill}<span class="status-pill {pill.cls}" title={`Recorded follow-up for ${action?.display_name}`}>{pill.label}</span>{:else}<span class="fund-ir-secondary">—</span>{/if}</td>
                  <td>{action && relationshipDue(action) ? mailDate(relationshipDue(action)) : '—'}</td>
                  <td class="num" title={inbound ? mailDate(inbound) : 'No synced inbound mail'}>{activityAgo(inbound)}</td>
                  <td class="num" title={outbound ? mailDate(outbound) : 'No synced outbound mail'}>{activityAgo(outbound)}</td>
                </tr>
                {#if orgOpen}
                  {@const stageContact = entityStageContact(organization)}
                  <!-- The spec's two-column panel: what the records say on the left, the
                       controls that change them on the right. Clicks inside must not
                       reach the row, or reading the panel would collapse it. -->
                  <tr class="fund-ir-detail-row"><td class="fund-ir-detail-gutter"></td><td colspan="5">
                   <!-- svelte-ignore a11y_click_events_have_key_events -->
                   <!-- svelte-ignore a11y_no_static_element_interactions -->
                   <div class="fund-ir-panel" onclick={(event) => event.stopPropagation()}>
                    <div class="fund-ir-panel-main">
                      <div class="fund-record-subhead">Mike's summary</div>
                      <p class="fund-ir-summary">{organization.contacts.find(c => c.suggested_reason)?.suggested_reason || 'No relationship summary is available yet. Open this LP to review the evidence or prepare a brief.'}</p>
                      {#if organization.contacts.some(c => c.suggested_reason)}<p class="fund-ir-source">✦ Limited synced-mail suggestion · review before relying on it</p>{/if}
                      <div class="fund-ir-section">
                        <div class="fund-record-subhead">Update opens ({(dashboardTracking[organization.key] || []).reduce((total, item) => total + item.opens, 0)})</div>
                        {#if dashboardTrackingLoading.has(organization.key)}
                          <p class="fund-ir-muted-note">Loading your tracked updates…</p>
                        {:else if dashboardTrackingErrors[organization.key]}
                          <p class="fund-alert" role="alert">{dashboardTrackingErrors[organization.key]} <button class="fund-row-link" onclick={() => { const { [organization.key]: _old, ...remaining } = dashboardTracking; dashboardTracking = remaining; void loadEntityTracking(organization); }}>Retry</button></p>
                        {:else if dashboardTracking[organization.key]?.length}
                          {#each dashboardTracking[organization.key].slice(0, 5) as tracked (tracked.tracking_id)}
                            <div class="fund-ir-open"><strong>{tracked.opens}×</strong><span>{tracked.subject}</span><time>{tracked.last_opened_at ? `Last opened ${mailDate(tracked.last_opened_at)}` : 'Not opened'}</time></div>
                          {/each}
                          <p class="fund-ir-source">Your tracked sends only. Mail privacy settings may block opens.</p>
                        {:else}
                          <p class="fund-ir-muted-note">No tracked LP updates sent from your mailbox yet.</p>
                        {/if}
                      </div>
                      <div class="fund-ir-section">
                        <div class="fund-record-subhead">Stage reasoning</div>
                        <p class="fund-ir-summary">{stageContact && relationshipStage(stageContact)
                          ? relationshipStageSource(stageContact) === 'ai'
                            ? relationshipStageReason(stageContact) || 'Mike inferred this stage from synced mail.'
                            : 'Set by your team. Manual stage updates are preserved.'
                          : stageContact && relationshipStageSource(stageContact) === 'ai'
                            ? relationshipStageReason(stageContact) || 'Mike has not found enough synced-mail evidence to infer a stage.'
                            : 'No stage is recorded for this LP yet.'}</p>
                      </div>
                    </div>
                    <!-- Stage and manual override use the same pattern as the Sales expand. -->
                    <div class="fund-ir-rail">
                      <span class="fund-ir-rail-label">Stage</span>
                      {#if stageContact}
                        <div class="fchip-wrap fund-ir-stage-dd">
                          <button class="fund-ir-stage-btn" aria-haspopup="menu"
                                  aria-expanded={openStageDD === organization.key} disabled={savingStage}
                                  onclick={(event) => toggleStageMenu(organization.key, event.currentTarget)}>
                            {fundStageLabel(relationshipStage(stageContact))}
                            <span class="fchip-ar">▾</span>
                          </button>
                        </div>
                      {:else}
                        <p class="fund-ir-secondary">Confirm this organization as an LP institution to set a stage.</p>
                      {/if}
                      {#if stageContact}
                        <p class="fund-ir-rail-help">{relationshipStageSource(stageContact) === 'ai'
                          ? 'Set by Mike · updates after sync' : relationshipStage(stageContact) ? 'Manual stage' : 'Stage pending synced-mail evidence'}</p>
                        {#if relationshipStageSource(stageContact) === 'manual_override'
                            || (relationshipStage(stageContact) && !relationshipStageSource(stageContact))}
                          <button class="fund-row-link" disabled={savingStage}
                                  onclick={() => resumeEntityAutoStage(organization)}>Resume automatic stage updates</button>
                        {/if}
                      {/if}
                      <label class="fund-ir-rail-label" for={`due-${organization.key}`}>Follow-up</label>
                      {#if stageContact}
                        <input class="fund-ir-rail-input" id={`due-${organization.key}`} type="date"
                               value={relationshipDue(stageContact) || ''} disabled={savingFollowUp}
                               onchange={(event) => setEntityFollowUpDate(organization, event.currentTarget.value)} />
                        <!-- Status is derived from the next step, not the date, so a date on
                             its own would save and still show no status. Say that here rather
                             than let the row look broken. -->
                        {#if !relationshipNextStep(stageContact)}
                          <button class="fund-row-link fund-ir-secondary" onclick={() => organization.organization
                            ? openOrganization(organization.key) : openFollowUp(organization.contacts[0])}>
                            Add a next step for this date to show a status →
                          </button>
                        {/if}
                      {:else}
                        <p class="fund-ir-secondary">Available once the organization is confirmed.</p>
                      {/if}
                      <span class="fund-ir-rail-label">LP name</span>
                      <div class="fund-ir-rail-name">{organization.name}</div>
                      <button class="fund-row-link fund-ir-rail-open" onclick={() => organization.organization
                        ? openOrganization(organization.key) : openContact(organization.contacts[0])}>
                        Open LP →
                      </button>
                    </div>
                  </div></td></tr>
                {/if}
              {/each}
            {/if}
          {/each}
          {/if}
        </tbody></table></div>
        {#if openStageDD}
          {@const menuEntity = irSortedEntities.find(entity => entity.key === openStageDD)}
          {#if menuEntity}
            {@const menuContact = entityStageContact(menuEntity)}
            {#if menuContact}
              <div class="fchip-dd fund-ir-stage-dd fund-ir-stage-menu" role="menu"
                   style={`top:${stageMenuPosition.top}px;left:${stageMenuPosition.left}px`}>
                {#each FUND_STAGES as option}
                  <button class="fchip-opt" class:on={relationshipStage(menuContact) === option.key}
                          onclick={() => setEntityStage(menuEntity, option.key)}>
                    <span class="fchip-check">✓</span>{option.label}
                  </button>
                {/each}
                {#if menuEntity.contacts.length === 1}
                  <div class="fund-ir-dd-sep"></div>
                  <div class="fund-ir-dd-label">Role</div>
                  <div class="fund-ir-dd-toggle">
                    {#each [['investor', 'LP'], ['portfolio', 'Portfolio'], ['neither', 'Neither']] as [value, label]}
                      <button class:on={menuEntity.contacts[0].role === value}
                              disabled={!!savingRole}
                              onclick={() => { openStageDD = null; setRole(menuEntity.contacts[0], value as FundRole); }}>{label}</button>
                    {/each}
                  </div>
                {/if}
              </div>
            {/if}
          {/if}
        {/if}
        <div class="fund-ir-table-footer">
          <p class="fund-ir-scan-note">{irScanNote}</p>
          {#if unreviewedPeople.length}
            <button type="button" class="fund-row-link" onclick={openInvestorReview}>Needs review: {unreviewedPeople.length} contact{unreviewedPeople.length === 1 ? '' : 's'} →</button>
          {/if}
        </div>
    {:else}
      {#if portfolioRows.length}
        <div class="data-table-wrap"><table class="data-table fund-portfolio-table">
          <thead><tr><th>Portfolio contact</th><th>Organization</th><th>Latest email</th><th>Last seen</th><th></th></tr></thead>
          <tbody>{#each portfolioRows as contact (contact.counterparty_id)}
            <tr class="data-row"><td><button type="button" class="fund-row-link" onclick={() => openContact(contact)}>{contact.display_name}</button><div class="fund-hint">{contact.email || 'No email address'}</div></td>
              <td>{organizationHint(contact)?.name || '—'}</td>
              <td>{contact.sample_subjects?.[0] || '—'}</td>
              <td>{mailDate(contact.last_seen, '—')}</td>
              <td><button type="button" class="fund-row-link" onclick={() => openContact(contact)}>Open →</button></td></tr>
          {/each}</tbody>
        </table></div>
      {:else}
        <div class="empty-state">{searchTerm ? 'No portfolio contacts match this search.' : 'No confirmed portfolio contacts yet.'} {#if !searchTerm && unreviewedPeople.length}<button type="button" class="fund-row-link" onclick={openInvestorReview}>Review suggested contacts →</button>{/if}</div>
      {/if}
    {/if}
  {:else if view === 'scheduled'}
    <div class="fund-page-heading"><div class="page-title">Scheduled Emails</div><button class="today-mini-btn" onclick={loadAllScheduled} disabled={scheduledLoading}>Refresh</button></div>
    <div class="page-subtitle">Emails queued from your connected mailbox. A pending email can be cancelled before dispatch.</div>
    <div class="page-toolbar">
      {#each [['all', 'All'], ['pending', 'Pending'], ['sent', 'Sent'], ['attention', 'Needs review']] as [key, label]}
        <button class="fchip" class:set={scheduledFilter === key} onclick={() => scheduledFilter = key as typeof scheduledFilter}>{label}</button>
      {/each}
      <span class="count-label"><b>{shownScheduled.length}</b> emails</span>
    </div>
    {#if scheduledError}<p class="fund-alert" role="alert">{scheduledError}</p>{/if}
    {#if scheduledLoading && !allScheduled.length}<div class="empty-state" role="status">Loading scheduled emails…</div>
    {:else if !shownScheduled.length}<div class="empty-state">No {scheduledFilter === 'all' ? '' : scheduledFilter + ' '}scheduled emails from your mailbox.</div>
    {:else}
      <div class="data-table-wrap"><table class="data-table"><thead><tr><th>LP</th><th>Subject</th><th>To / CC</th><th>Send at</th><th>Status</th><th></th></tr></thead><tbody>
        {#each shownScheduled as item (item.request_id)}
          {@const contact = allContacts.find(c => c.counterparty_id === item.counterparty_id)}
          <tr class="data-row">
            <td>{#if contact}<button class="fund-row-link" onclick={() => openContact(contact)}>{contact.display_name}</button>{:else}{item.recipient}{/if}</td>
            <td><button class="fund-row-link" onclick={() => openSchedule(item.request_id)}>{item.subject}</button></td><td>{item.recipient}{#if item.cc}<div class="fund-hint">CC {item.cc}</div>{/if}</td>
            <td>{new Date(item.send_at).toLocaleString()}</td>
            <td>{item.status === 'uncertain' || item.status === 'claimed' ? 'Check Sent folder' : item.status}</td>
            <td>{#if item.status === 'pending'}<button class="today-mini-btn" onclick={() => cancelFromSchedule(item.request_id)}>Cancel</button>{/if}</td>
          </tr>
        {/each}
      </tbody></table></div>
      {#if selectedSchedule}
        <section class="card fund-schedule-editor" aria-label="Scheduled email details">
          <div class="fund-compose-heading"><h2>Review scheduled email</h2><button class="today-mini-btn" onclick={() => selectedSchedule = null}>Close</button></div>
          <p class="fund-muted">From your mailbox · To {selectedSchedule.recipient} · {selectedSchedule.status}</p>
          {#if selectedSchedule.calendar_invite}<p class="fund-muted">Calendar invite: {selectedSchedule.calendar_invite.summary} · {new Date(selectedSchedule.calendar_invite.start).toLocaleString()}. To change the meeting, cancel and compose again.</p>{/if}
          {#if selectedSchedule.tracking_enabled}<p class="fund-muted">Open tracking is enabled for this email.</p>{/if}
          {#if selectedSchedule.status === 'pending'}
            <label class="fund-field">Subject<input bind:value={scheduleSubject} maxlength="250" /></label>
            <label class="fund-field">CC (optional)<input bind:value={scheduleCc} maxlength="1000" /></label>
            <label class="fund-field">Message<EmailBodyEditor bind:value={scheduleBody} rows={10} /></label>
            <div class="fund-compose-actions"><label class="fund-field">Date<input type="date" bind:value={scheduleEditDate} /></label><label class="fund-field">Time<input type="time" bind:value={scheduleEditTime} /></label></div>
            {#if selectedSchedule.attachment_names.length}<p class="fund-muted">Attachments held for this email: {selectedSchedule.attachment_names.join(', ')}. To change files, cancel and compose again.</p>{/if}
            <div class="fund-compose-actions"><button class="btn-primary" onclick={saveScheduleEdit} disabled={scheduleSaving || !scheduleSubject.trim() || !scheduleBody.trim()}>{scheduleSaving ? 'Saving…' : 'Save changes'}</button><button class="today-mini-btn" onclick={() => cancelFromSchedule(selectedSchedule!.request_id)} disabled={scheduleSaving}>Cancel send</button></div>
          {:else}<p class="fund-muted">This email is no longer pending. Check your provider Sent folder if the status is uncertain.</p>{/if}
        </section>
      {/if}
    {/if}
  {:else if view === 'contacts'}
    <div class="page-title">Contacts</div>
    <div class="page-subtitle">People appear once: under a confirmed institution or suggested business domain, as an individual with a personal email, or as an ungrouped contact. Domain groups do not confirm an institution.</div>
    <div class="tabs fund-dashboard-tabs" role="tablist" aria-label="Contact groups">
      <button type="button" class="tab" role="tab" aria-selected={activeContactTab === 'organizations'} class:active={activeContactTab === 'organizations'} onclick={() => contactTab = 'organizations'}>Organizations <span class="tab-count">{contactOrganizations.length}</span></button>
      <button type="button" class="tab" role="tab" aria-selected={activeContactTab === 'individuals'} class:active={activeContactTab === 'individuals'} onclick={() => contactTab = 'individuals'}>Individuals <span class="tab-count">{individualContacts.length}</span></button>
      <button type="button" class="tab" role="tab" aria-selected={activeContactTab === 'ungrouped'} class:active={activeContactTab === 'ungrouped'} onclick={() => contactTab = 'ungrouped'}>Ungrouped <span class="tab-count">{otherContacts.length}</span></button>
    </div>
    <div class="page-toolbar">
      <input class="page-toolbar-search" bind:value={search} oninput={() => { collapsedContactSearchGroups = new Set(); contactTab = 'auto'; }} placeholder="Search name, email or organization…" aria-label="Search contacts" />
      {#each [['all', 'All'], ['investor', 'LPs'], ['portfolio', 'Portfolio'], ['neither', 'Neither'], ['unclassified', 'Unclassified']] as [key, label]}
        <button class="fchip" class:set={roleFilter === key} onclick={() => roleFilter = key as typeof roleFilter}>{label}</button>
      {/each}
      <span class="count-label"><b>{visibleContactCount}</b> contact{visibleContactCount === 1 ? '' : 's'} in this view</span>
      <div class="page-toolbar-spacer"></div>
      <div class="page-toolbar-actions"><button class="action-btn primary-action" onclick={() => { actionError = ''; addOpen = !addOpen; }}>+ Add contact</button></div>
    </div>
    {#if actionError}<p class="fund-alert" role="alert">{actionError}</p>{/if}
    {#if addOpen}
      <form class="card mb-4" onsubmit={(event) => { event.preventDefault(); addContact(); }}>
        <div class="font-bold mb-3">Add contact</div>
        <p class="fund-muted">Add someone you already know. Mailbox sync will add their real email history when available.</p>
        <div class="grid-3 mb-3">
          <div><label class="text-xs text-dim" for="fund-new-email">Email *</label><input id="fund-new-email" type="email" bind:value={newEmail} required placeholder="person@example.com" /></div>
          <div><label class="text-xs text-dim" for="fund-new-name">Name</label><input id="fund-new-name" bind:value={newName} placeholder="Full name" /></div>
          <div><label class="text-xs text-dim" for="fund-new-role">Relationship *</label><select id="fund-new-role" bind:value={newRole}><option value="investor">LP</option><option value="portfolio">Portfolio</option><option value="neither">Neither</option></select></div>
        </div>
        <div class="row"><button class="primary" type="submit" disabled={!!savingRole}>{savingRole ? 'Saving…' : 'Save'}</button><button type="button" onclick={() => addOpen = false} disabled={!!savingRole}>Cancel</button></div>
      </form>
    {/if}
    {#if !workspace.inbox_connected && !allContacts.length}
      <div class="empty-state">Connect your inbox in Settings to discover contacts.</div>
    {:else if !shown.length}
      <div class="empty-state">{allContacts.length ? 'No contacts match these filters.' : 'No contacts synced yet. Start a mailbox sync.'}</div>
    {:else if !visibleContactCount}
      <div class="empty-state">{activeContactTab === 'individuals' && !workspace.preferences.include_personal_emails
        ? 'Personal email contacts are excluded by your mailbox setting. You can include them in Settings.'
        : `No ${activeContactTab === 'organizations' ? 'organizations' : activeContactTab === 'individuals' ? 'individuals with personal email addresses' : 'ungrouped contacts'} match these filters. Check the other tabs.`}</div>
    {:else}
      <div class="data-table-wrap"><table class="data-table fund-contacts-table"><thead><tr><th class="fund-contacts-gutter"></th><th>Contact</th><th class="fund-contacts-email">Email</th><th>Relationship</th><th>Last seen</th><th></th></tr></thead><tbody>
        {#if activeContactTab === 'organizations'}
          {#each contactOrganizations as organization (organization.key)}
            {@const open = search.trim() ? !collapsedContactSearchGroups.has(organization.key) : expandedContactOrganizations.has(organization.key)}
            {@const latest = groupLatestMail(organization.contacts)}
            <tr class="subgroup-row">
              <td colspan="6"><div class="subgroup-header"><button class="fund-row-link subgroup-arrow" aria-label={`${open ? 'Collapse' : 'Expand'} ${organization.name}`} aria-expanded={open} onclick={() => toggleContactOrganization(organization.key)}>{open ? '▾' : '▸'}</button><button class="fund-row-link subgroup-name" onclick={() => openOrganization(organization.key)}>{organization.name}</button><span class="subgroup-meta">{organization.contacts.length} contact{organization.contacts.length === 1 ? '' : 's'} · {organization.source}{#if latest} · last seen {mailDate(latest.last_seen)}{/if}</span></div></td>
            </tr>
            {#if open}
              {#each organization.contacts as contact (contact.counterparty_id)}
                {@render fundContactRow(contact, true)}
              {/each}
            {/if}
          {/each}
        {:else if activeContactTab === 'individuals'}
          {#each individualContacts as contact (contact.counterparty_id)}
            {@render fundContactRow(contact, false)}
          {/each}
        {:else}
          {#each otherContacts as contact (contact.counterparty_id)}
            {@render fundContactRow(contact, false)}
          {/each}
        {/if}
      </tbody></table></div>
    {/if}
  {:else if view === 'detail' && selectedOrganization}
    <div class="client-header-wrap">
      <button class="back-crumb" onclick={() => $currentView = 'dashboard'}>← Back to Dashboard</button>
      <div class="lp-hdr">
        <div class="lp-hdr-text">
          <div class="page-title">{selectedOrganization.name}</div>
          <div class="lp-meta">
            <span>{selectedOrganizationRecord ? 'Confirmed LP institution' : 'Suggested from mail · institution not confirmed'}</span>
            <span>{selectedOrganization.contacts.length} contact{selectedOrganization.contacts.length === 1 ? '' : 's'}</span>
          </div>
        </div>
        <div class="lp-hdr-actions">
          <button class="today-mini-btn" disabled={savingPin} aria-label={isOrganizationPinned(selectedOrganization.key) ? `Unpin ${selectedOrganization.name}` : `Pin ${selectedOrganization.name}`}
            onclick={() => toggleOrganizationPin(selectedOrganization.key)}>{isOrganizationPinned(selectedOrganization.key) ? '★ Pinned' : '☆ Pin'}</button>
          {#if selectedOrganizationRecord}
            <button class="today-mini-btn" onclick={openOrganizationFollowUp}>{selectedOrganizationRecord.next_step ? 'Edit follow-up' : 'Add follow-up'}</button>
          {/if}
        </div>
      </div>
      {#if pinError}<p class="fund-alert" role="alert">{pinError}</p>{/if}
      {#if actionError}<p class="fund-alert" role="alert">{actionError}</p>{/if}
      {#if selectedOrganizationRecord}
        <div class="fund-compose-actions">
          <label class="fund-field">Relationship stage
            <select class="fund-inline-role" value={selectedOrganizationRecord.stage || ''} disabled={savingStage}
              onchange={async (event) => {
                const control = event.currentTarget;
                savingStage = true; actionError = '';
                try { await setFundInstitutionStage(selectedOrganizationRecord.institution_id, control.value || null); await load(); }
                catch (err: any) { control.value = selectedOrganizationRecord.stage || ''; actionError = err?.message || 'Could not save institution stage.'; }
                finally { savingStage = false; }
              }}>
              <option value="">Not set</option>
              {#each FUND_STAGES as stage}<option value={stage.key}>{stage.label}</option>{/each}
            </select>
          </label>
          {#if selectedOrganizationRecord.next_step}<div class="lp-nextstep"><span class="lbl">Next step</span><span class="val">{selectedOrganizationRecord.next_step}</span>{#if selectedOrganizationRecord.follow_up_on}<span class="due">by {mailDate(selectedOrganizationRecord.follow_up_on)}</span>{/if}</div>{/if}
        </div>
        {#if followUpEditing}
          <div class="fund-action-card">
            <div class="fund-compose-heading"><h3>Follow-up · {selectedOrganization.name}</h3><button class="today-mini-btn" onclick={() => followUpEditing = false}>Close</button></div>
            <label class="fund-field">Action<textarea bind:value={nextStepDraft} maxlength="500" rows="2" placeholder="An action for this institution"></textarea></label>
            <label class="fund-field">Follow up by (optional)<input type="date" bind:value={followUpDateDraft} /></label>
            <button class="btn-primary" onclick={saveOrganizationFollowUp} disabled={savingFollowUp || (!!followUpDateDraft && !nextStepDraft.trim())}>{savingFollowUp ? 'Saving…' : 'Save follow-up'}</button>
          </div>
        {/if}
      {:else}
        <p class="fund-hint">This organization name comes from synced mail. Pinning or opening it does not confirm anyone as an LP. Review the people below and link them to a confirmed institution before using institution-wide briefs or follow-ups.</p>
        {#if selectedOrganization.contacts[0]}<button class="today-mini-btn" onclick={() => { openContact(selectedOrganization.contacts[0]); detailTab = 'contacts'; }}>Review and link people →</button>{/if}
      {/if}
    </div>
    <div class="fund-record-subhead">People at {selectedOrganization.name}</div>
    <div class="data-table-wrap"><table class="data-table"><thead><tr><th>Contact</th><th>Relationship</th><th>Inbound</th><th>Outbound</th><th></th></tr></thead><tbody>
      {#each selectedOrganization.contacts as member (member.counterparty_id)}
        <tr class="data-row"><td><button class="fund-row-link" onclick={() => openContact(member)}>{member.display_name}</button><div class="fund-ir-secondary">{member.email || 'No email address'}</div></td>
          <td>{member.role ? `${roleLabel(member.role)}${member.set_by === 'model' ? ' · filed by Mike' : ''}` : member.suggested_source === 'model_review' ? `Suggested ${roleLabel(member.suggested_role)}` : 'Unclassified'}</td>
          <td>{activityAgo(dashboardMailActivity[member.counterparty_id]?.inbound || null)}</td>
          <td>{activityAgo(dashboardMailActivity[member.counterparty_id]?.outbound || null)}</td>
          <td><button class="fund-row-link" onclick={() => openContact(member)}>Open person →</button></td></tr>
      {/each}
    </tbody></table></div>
    <div class="fund-record-subhead">Recent email context</div>
    {#if selectedOrganizationRecord}
      {#if institutionActivityLoading}<p class="fund-muted">Loading institution mail…</p>
      {:else if institutionActivityError}<p class="fund-alert" role="alert">{institutionActivityError}</p>
      {:else if institutionActivity.length}
        {#each institutionActivity as item (`${item.counterparty_id}:${item.owner_email}:${item.message_id}`)}
          <div class="fund-person-row"><div><button class="fund-row-link" onclick={() => { const person = allContacts.find(c => c.counterparty_id === item.counterparty_id); if (person) openContact(person); }}>{item.subject || '(No subject)'}</button><p>{item.display_name} · {mailDate(item.sent_at)} · synced by {item.owner_email}</p></div></div>
        {/each}
      {:else}<p class="fund-muted">No synced mail for confirmed members of this institution.</p>{/if}
    {:else}
      <p class="fund-muted">Subject samples from mail, grouped by business email domain. These do not confirm an LP relationship.</p>
      {#each selectedOrganization.contacts as member (member.counterparty_id)}
        {#each member.sample_subjects || [] as subject, index (`${member.counterparty_id}:${index}`)}
          <div class="fund-person-row"><div><button class="fund-row-link" onclick={() => openContact(member)}>{subject}</button><p>{member.display_name} · open their synced thread for the full context</p></div></div>
        {/each}
      {/each}
    {/if}
    <p class="fund-hint">Mail and drafts belong to a specific person and connected mailbox. Open a person to read their threads or prepare a message; the institution relationship stays here.</p>
  {:else if view === 'detail'}
    <div class="client-header-wrap" class:rail-open={showMikeRail} style="--fund-mike-w: {mikeW}px">
      <button class="back-crumb" onclick={() => $currentView = 'dashboard'}>← Back to Dashboard</button>
      {#if selected}
        {@const selectedOrgHint = organizationHint(selected)}
        <!-- Thin header per designs/lp-relationship-v1.html: title, a meta line of SET
             facts only, the next step when there is one, and just Pin + ⋯. Role, Stage
             and grouping moved into ⋯ — they are settings, not status. -->
        <div class="lp-hdr">
          <div class="lp-hdr-text">
            <div class="page-title">{selectedInstitution?.name || selectedOrgHint?.name || selected.display_name}</div>
            <div class="lp-meta">
              {#if selectedInstitution}<span>Main contact: {selected.display_name}</span>
              {:else if selectedOrgHint}<span>Contact: {selected.display_name}</span>{/if}
              {#if selected.email && !selectedInstitution}<span>{selected.email}</span>{/if}
              {#if selected.last_seen}<span>Last email {mailDate(selected.last_seen)}</span>{/if}
            </div>
            {#if (!entityKeyForContact(selected) || selectedInstitution) && relationshipNextStep(selected)}
              {@const pill = statusPill(selected)}
              <div class="lp-nextstep">
                <span class="lbl">Next step</span>
                <span class="val">{relationshipNextStep(selected)}</span>
                {#if relationshipDue(selected)}<span class="due">by {mailDate(relationshipDue(selected))}</span>{/if}
                {#if pill}<span class="status-pill {pill.cls}">{pill.label}</span>{/if}
              </div>
            {/if}
          </div>
          <div class="lp-hdr-actions">
            {#if selected.role === 'investor' && !selected.automated_address}
              {#if entityKeyForContact(selected)}
                <button class="today-mini-btn" onclick={() => toggleOrganizationPin(entityKeyForContact(selected)!)} disabled={savingPin} aria-label={isOrganizationPinned(entityKeyForContact(selected)!) ? 'Unpin institution' : 'Pin institution'}>{isOrganizationPinned(entityKeyForContact(selected)!) ? '★ Pinned' : '☆ Pin institution'}</button>
              {:else}
                <button class="today-mini-btn" onclick={() => togglePin(selected.counterparty_id)} disabled={savingPin} aria-label={isPinned(selected.counterparty_id) ? 'Unpin LP' : 'Pin LP'}>{isPinned(selected.counterparty_id) ? '★ Pinned' : '☆ Pin'}</button>
              {/if}
            {/if}
            <div class="lp-more">
              <button class="today-mini-btn" aria-haspopup="menu" aria-expanded={moreOpen}
                      aria-label="More relationship settings"
                      onclick={() => moreOpen = !moreOpen}>⋯</button>
              {#if moreOpen}
                <div class="lp-menu" role="menu">
                  <label class="lp-mi">Role
                    <select class="fund-inline-role" aria-label="Relationship type" value={displayRole(selected) || ''}
                            disabled={!!savingRole || !selected.email}
                            onchange={(event) => setRole(selected, event.currentTarget.value as FundRole, event.currentTarget)}>
                      <option value="" disabled>Unclassified</option>
                      <option value="investor" disabled={selected.automated_address}>LP</option>
                      <option value="portfolio" disabled={selected.automated_address}>Portfolio</option>
                      <option value="neither">Neither</option>
                    </select>
                  </label>
                  {#if selected.role === 'investor' && !selected.automated_address && (!entityKeyForContact(selected) || selectedInstitution)}
                    <label class="lp-mi">Stage
                      <select class="fund-inline-role" aria-label="LP relationship stage"
                              value={relationshipStage(selected) || ''} disabled={savingStage}
                              onchange={(event) => saveStage(selected, event.currentTarget.value, event.currentTarget)}>
                        <option value="">Not set</option>
                        {#each FUND_STAGES as stage}<option value={stage.key}>{stage.label}</option>{/each}
                      </select>
                    </label>
                    <button class="lp-mi" onclick={() => { moreOpen = false; openFollowUpEditor(); }}>{relationshipNextStep(selected) ? 'Edit next step' : 'Add next step'}</button>
                  {/if}
                  {#if selected.role === 'investor' && !selected.automated_address}
                    <button class="lp-mi" onclick={() => { moreOpen = false; detailTab = 'contacts'; }}>Group under institution</button>
                  {/if}
                </div>
              {/if}
            </div>
          </div>
        </div>
        {#if !selected.role && selected.suggested_source === 'model_review'}
          <!-- Unconfirmed is a blocking state, not a setting, so it stays on the page
               rather than hiding in ⋯: brief, draft and send are locked until it clears. -->
          <div class="fund-review-strip">
            <span class="fund-ir-sparkle" aria-hidden="true">✦</span>
            <span>Suggested {roleLabel(displayRole(selected))}{selected.suggested_reason ? ` · ${selected.suggested_reason}` : ''}</span>
            {#if displayRole(selected) === 'investor' && !selected.automated_address}
              <button class="today-mini-btn" disabled={!!savingRole} onclick={() => setRole(selected, 'investor')}>Confirm LP</button>
            {/if}
          </div>
        {/if}
        {#if !selected.role && selected.suggested_source === 'company_investor'}
          <div class="fund-review-strip">
            <span>Company investor signal · Fund LP not verified{selected.suggested_reason ? ` · ${selected.suggested_reason}` : ''}</span>
          </div>
        {/if}
        {#if selected.suggested_organization_domain && !selectedInstitution}
          <p class="fund-hint">{selectedOrgHint?.name || selected.suggested_organization_domain} is an unconfirmed organization suggestion. Review the people in Contacts and confirm the institution before recording an institution stage or follow-up.</p>
        {/if}
        {#if followUpEditing}
          <div class="fund-action-card">
            <div class="fund-compose-heading"><h3>{relationshipNextStep(selected) ? 'Edit next step' : 'Add next step'}{selectedInstitution ? ` · ${selectedInstitution.name}` : ''}</h3><button class="today-mini-btn" onclick={() => followUpEditing = false}>Close</button></div>
            <label class="fund-field">Action<textarea bind:value={nextStepDraft} maxlength="500" rows="2" placeholder="An action you intend to take"></textarea></label>
            <label class="fund-field">Follow up by (optional)<input type="date" bind:value={followUpDateDraft} /></label>
            <button class="btn-primary" onclick={saveFollowUp} disabled={savingFollowUp || (!!followUpDateDraft && !nextStepDraft.trim())}>{savingFollowUp ? 'Saving…' : 'Save next step'}</button>
          </div>
        {/if}
      {/if}
    </div>
    {#if selected}
      {#if selected.automated_address}
        <p class="fund-alert" role="alert">This address sends automated notifications. It is not this person's direct email. Mark it Neither under Relationship, or add the person's real email address in Contacts. Brief, draft, and assistant are unavailable for this address.</p>
      {/if}
      <div class="split-shell fund-detail-shell" bind:this={shellEl}
           class:dragging={mikeDragging} class:collapsed={!showMikeRail}
           style="--fund-mike-w: {mikeW}px">
        <aside class="split-rail fund-mike-rail" aria-label="{agent} chat">
          {#if showMikeRail}
            <div class="fund-mike-head">
              <div class="fund-mike-title"><span aria-hidden="true">✦</span> {agent}</div>
              <div class="fund-mike-actions">
                <button type="button" title="Open {agent} as a tab" aria-label="Open {agent} as a tab" onclick={() => setMikeMode('tab')}>⛶</button>
                <button type="button" title="Minimise {agent} to bubble" aria-label="Minimise {agent} to bubble" onclick={() => setMikeMode('bubble')}>−</button>
              </div>
              <div class="fund-mike-reading">{mikeContextLabel}</div>
            </div>
            {@render assistantRail()}
          {/if}
        </aside>
        <div class="split-divider fund-mike-divider">
          {#if showMikeRail}
            <button type="button" class="split-drag"
                    aria-label="Resize {agent} panel — arrow keys adjust"
                    onpointerdown={resizeMike} onkeydown={resizeMikeKey}></button>
          {/if}
        </div>
        <!-- The briefing and relationship records are the centre of the LP page. -->
        <section class="split-main fund-detail-main">
          <div class="tabs" role="tablist" aria-label="Relationship details">
            <!-- First and default, but only for a confirmed LP: there is nothing
                 to brief on otherwise, and the review strip above says why. -->
            {#if briefingAllowed}
              <button class="tab" class:active={detailTab === 'briefing'} role="tab"
                      aria-selected={detailTab === 'briefing'}
                      onclick={() => detailTab = 'briefing'}>Briefing</button>
            {/if}
            {#if mikeMode === 'tab'}
              <button class="tab tab-assist" class:active={detailTab === 'assistant'} role="tab"
                      aria-selected={detailTab === 'assistant'} onclick={() => detailTab = 'assistant'}>
                <span class="spark">✦</span> {agent}
              </button>
            {/if}
            <!-- Counts only where the number is a real record count; Memory and Files
                 carry no badge because a zero there is an empty state, not a total. -->
            <button class="tab" class:active={detailTab === 'emails'} role="tab" aria-selected={detailTab === 'emails'} onclick={() => detailTab = 'emails'}>Emails{#if emailThreads.length} <span class="tab-count">{emailThreads.length}</span>{/if}</button>
            <button class="tab" class:active={detailTab === 'contacts'} role="tab" aria-selected={detailTab === 'contacts'} onclick={() => detailTab = 'contacts'}>Contacts <span class="tab-count">{1 + lpColleagues.length}</span></button>
            <button class="tab" class:active={detailTab === 'memory'} role="tab" aria-selected={detailTab === 'memory'} onclick={() => detailTab = 'memory'}>Memory</button>
            <button class="tab" class:active={detailTab === 'files'} role="tab" aria-selected={detailTab === 'files'} onclick={() => detailTab = 'files'}>Files</button>
          </div>
          {#if actionError}<p class="fund-alert" role="alert">{actionError}</p>{/if}
          {#if detailTab === 'briefing'}
            {#if briefingNotice}<p class="fund-hint">{briefingNotice}</p>{/if}
            {#if sandboxDemoAvailable && visibleBriefingFixture && !briefing.briefing}
              <p class="fund-hint">{briefingFixtureNotice}</p>
            {/if}
            <BriefingView
              briefing={briefing.briefing ?? visibleBriefingFixture}
              lpName={selectedInstitution?.name || selected.display_name}
              gpName={workspace?.profile.manager_name || ''}
              {agent}
              bind:view={briefingView}
              loading={briefing.loading}
              generating={briefing.generating}
              error={briefing.error}
              lockedBy={briefing.lockedBy}
              readOnly={(isSupportView() && !canUseAs()) || (!!visibleBriefingFixture && !briefing.briefing)}
              saveState={briefing.saveState}
              onReadingChange={(label) => readingLabel = label}
              onGenerate={isSupportView() && !canUseAs() ? undefined : () => briefing.generate()}
              onSaveSection={(id, content, briefingId) => briefing.save(id, content, briefingId)}
              onRevertSection={(id) => briefing.revert(id)}
              onOpenSource={openBriefingSource}
              onCompare={(id) => { briefingNotice =
                `${agent} has a newer draft for section ${id.toUpperCase()}. Comparing drafts is not built yet — Revert that section to read it.`; }}
              onEnterEdit={() => briefing.enterEdit()}
              onLeaveEdit={() => briefing.leaveEdit()} />
          {:else if detailTab === 'assistant'}
            <div class="seg-assist fund-mike-tab-chat">
              <div class="fund-mike-tab-head">
                <span><span aria-hidden="true">✦</span> {agent}</span>
                <button type="button" class="today-mini-btn" onclick={() => setMikeMode('rail')}>Restore to rail</button>
              </div>
              {@render assistantRail()}
            </div>
          {:else if detailTab === 'emails'}
            {#if selected.role === 'investor' && !selected.automated_address}
              <div class="fund-email-toolbar">
                {#if relationshipEmails.length}
                  <div class="esearch">
                    <svg class="esearch-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>
                    <input aria-label="Search email threads" placeholder="Search email threads…" bind:value={emailQuery} />
                    {#if emailQuery}<button type="button" class="eq-x" aria-label="Clear email search" onclick={() => emailQuery = ''}>×</button>{/if}
                  </div>
                {/if}
                {#if refreshingThreads || contactContext?.messages.some(m => !m.thread_id && m.owner_email.toLowerCase() === $userEmailStore.toLowerCase())}
                  <button type="button" class="today-mini-btn" disabled={refreshingThreads}
                    onclick={refreshOlderThreads}
                    title="Group older messages from your mailbox into their original conversations">
                    {refreshingThreads ? 'Grouping emails…' : 'Group emails'}
                  </button>
                {/if}
                {#if !composeOpen}<button class="btn-newemail" onclick={startFundEmail}>+ New email</button>{/if}
              </div>
              {#if selectedInstitution && relationshipEmails.length}
                <p class="fund-email-scope">Showing {institutionBriefMembers.length} contact{institutionBriefMembers.length === 1 ? '' : 's'} at {selectedInstitution.name}</p>
              {/if}
              {#if sendNotice}<div class="fund-send-notice" role="status">{sendNotice}</div>{/if}
              {#if composeOpen && !replyThreadAvailable}{@render fundComposeArea()}{/if}
              {#if selectedInstitution && institutionActivityLoading}<div class="segment-loading">Loading institution emails…</div>
              {:else if selectedInstitution && institutionActivityError}<div class="empty-state">Could not load institution emails: {institutionActivityError}</div>
              {:else if contextLoading && !selectedInstitution}<div class="segment-loading">Loading synced emails…</div>
              {:else if relationshipEmails.length}
                <div class="fund-message-list">
                  {#each orderedEmailThreads as thread, index (thread.key)}
                    {#if index === 0 || (!!replyDecisionForThread(thread) !== !!replyDecisionForThread(orderedEmailThreads[index - 1]))}
                      <h3 class="fund-email-group-heading">{replyDecisionForThread(thread) ? 'Needs reply' : 'All threads'}</h3>
                    {/if}
                    <article class="ethread" class:open={expandedEmailThreads.has(thread.key)}>
                      <div class="et-head">
                        <button class="et-toggle" onclick={() => toggleEmailThread(thread)}>
                        <span class="et-caret" class:open={expandedEmailThreads.has(thread.key)}>▸</span>
                        <span class="et-main"><span class="et-subj" title={thread.subject}>{#if replyDecisionForThread(thread)}<span class="fund-reply-dot" aria-hidden="true"></span>{/if}<span class="fund-thread-subject-text">{thread.subject}</span></span><span class="et-meta">{#if replyDecisionForThread(thread)}<span class="fund-reply-owed">Reply owed · {replyDecisionForThread(thread)?.age_days} day{replyDecisionForThread(thread)?.age_days === 1 ? '' : 's'}</span> · {/if}{thread.messages.map(m => m.display_name).filter((name, index, names) => names.indexOf(name) === index).join(', ')} · {thread.messages.length} message{thread.messages.length === 1 ? '' : 's'}</span></span>
                        <span class="et-date">{emailListDate(thread.sent_at)}</span>
                        </button>
                      </div>
                      {#if expandedEmailThreads.has(thread.key)}
                        {@const replyTarget = threadReplyTarget(thread)}
                        <div class="et-body">
                          {#each thread.messages as message (`${message.counterparty_id}:${message.owner_email}:${message.message_id}`)}
                            {@const key = `${message.counterparty_id}:${message.owner_email}:${message.message_id}`}
                            {@const direction = messageDirection(message)}
                            <div class="emsg" id="fund-msg-{key}" class:out={direction === 'outbound'} class:mopen={openedMessages.has(key)}>
                              <button class="em-h" onclick={() => toggleMessage(message.counterparty_id, message.owner_email, message.message_id)}>
                                <span class="em-caret" class:open={openedMessages.has(key)}>▸</span>
<b title={message.sender}>{senderName(message.sender)}</b>
                                <!-- In the institution view a thread can belong to another member.
                                     Name them only then; on their own page it is the page's title. -->
                                {#if message.counterparty_id !== selected.counterparty_id}<span class="em-who">on {message.display_name}&rsquo;s thread</span>{/if}
                                <span>{emailListDate(message.sent_at)}{direction ? ` · ${direction}` : ''}</span>
                              </button>
                              {#if openedMessages.has(key)}
                                {#if messageErrors[key]}<p class="fund-alert">{messageErrors[key]} <button onclick={() => toggleMessage(message.counterparty_id, message.owner_email, message.message_id)}>Retry</button></p>
                                {:else if messageBodies[key]}<div class="em-b md-content">{@html renderSafeMarkdown(unwrapHardBreaks(repairLostApostrophes(stripCids(messageBodies[key]))))}</div>
                                {:else}<span class="spinner"></span> Loading full message…{/if}
                                {#if message.counterparty_id !== selected.counterparty_id}
                                  <button class="today-mini-btn" onclick={() => { const person = allContacts.find(c => c.counterparty_id === message.counterparty_id); if (person) openContact(person); }}>Open {message.display_name} to reply</button>
                                {/if}
                              {:else if messageSnippet(message.excerpt)}
                                <div class="em-snip">{messageSnippet(message.excerpt)}</div>
                              {/if}
                            </div>
                          {/each}
                          {#if replyTarget && !(composeOpen && composeReplyMessageId === replyTarget.message_id)}
                            <div class="fund-thread-actions">
                              <button type="button" class="btn-newemail" disabled={!canGenerateDraft || !!generating} onclick={() => draftReply(replyTarget)}>✦ Draft reply</button>
                              <button type="button" class="today-mini-btn" onclick={() => startReply(replyTarget)}>Reply</button>
                            </div>
                          {/if}
                        </div>
                      {/if}
                      {#if composeOpen && composeReplyMessageId && thread.messages.some(message => message.message_id === composeReplyMessageId && message.owner_email.toLowerCase() === $userEmailStore.toLowerCase())}
                        {@render fundComposeArea()}
                      {/if}
                    </article>
                  {/each}
                </div>
                {#if !visibleEmailThreads.length}<div class="eno">No threads match “{emailQuery}”.</div>{/if}
              {:else if contextError && !contactContext}<div class="empty-state">Could not load synced email: {contextError} <button class="today-mini-btn" onclick={() => reloadContext(selected.counterparty_id)}>Retry</button></div>
              {:else}<div class="empty-state">No synced email for this {selectedInstitution ? 'LP institution' : 'LP'} yet. <SyncButton /></div>{/if}
              {#if sentReceipts.length}
                <div class="fund-record-subhead">Sent from your mailbox</div>
                {#each sentReceipts as receipt}<div class="fund-sent-row"><b>{receipt.subject}</b><span>{receipt.status === 'sent' ? 'Sent' : 'Check provider Sent folder'} · {mailDate(receipt.created_at)}</span></div>{/each}
              {/if}
              {#if emailTracking.length}
                <div class="fund-record-subhead">Open tracking</div>
                {#each emailTracking as item}
                  <div class="fund-sent-row"><b>{item.subject}</b><span>{item.opens} image loads · {item.last_opened_at ? mailDate(item.last_opened_at) : 'No load recorded'}</span></div>
                {/each}
              {/if}
              {#if scheduledSends.length}
                <div class="fund-record-subhead">Scheduled from your mailbox</div>
                {#each scheduledSends as scheduled}
                  <div class="fund-sent-row"><b>{scheduled.subject}</b><span>{scheduled.status} · {new Date(scheduled.send_at).toLocaleString()}</span>{#if scheduled.status === 'pending'}<button class="today-mini-btn" onclick={() => cancelScheduled(scheduled.request_id)}>Cancel</button>{/if}</div>
                {/each}
              {/if}
            {:else if selected.automated_address}<div class="empty-state">This is an automated notification address. Use the person's direct email address instead.</div>
            {:else}<div class="fund-unclassified"><h3>Review this relationship</h3><p>Confirm the type in the header to use synced LP email. Classification can be corrected later and does not send mail.</p>{#if selected.sample_subjects?.length}<div class="fund-record-subhead">Recent email subjects</div><ul>{#each selected.sample_subjects as subject}<li>{subject}</li>{/each}</ul>{/if}{#if selected.suggested_role}<p>Mailbox keywords suggest {selected.suggested_role}; this is unconfirmed.</p>{/if}</div>{/if}
          {:else if detailTab === 'contacts'}
            <div class="fund-contacts-heading">People</div>
            {#if lpIsIndividual}
              <p class="fund-muted">A personal email address does not tell us which institution this person represents. Link an institution when your team can confirm it.</p>
            {/if}
            <div class="fund-person-row fund-contact-person"><div class="fund-person-avatar">{selected.display_name.slice(0, 1).toUpperCase()}</div><div class="fund-person-info"><b>{selected.display_name}</b><p>{selected.email || 'No email address'} <span class="fund-contact-date">Last email {mailDate(selected.last_seen, 'not recorded')}</span></p></div></div>
            {#each lpColleagues as { contact: member } (member.counterparty_id)}
              <div class="fund-person-row fund-contact-person"><div class="fund-person-avatar">{member.display_name.slice(0, 1).toUpperCase()}</div><div class="fund-person-info"><button class="fund-row-link" onclick={() => openContact(member)}>{member.display_name}</button><p>{member.email}{#if !member.institution_id && !selected.institution_id} · Same email domain{/if} <span class="fund-contact-date">Last email {mailDate(member.last_seen, 'not recorded')}</span></p></div></div>
            {/each}
            {#if teamOnLp.length > 1}
              <div class="fund-contacts-heading fund-team-heading">Your team</div>
              {#each teamOnLp as person (person.email)}
                <div class="fund-person-row fund-contact-person"><div class="fund-person-avatar">{person.email.slice(0, 1).toUpperCase()}</div><div class="fund-person-info"><b>{person.email}</b><p>{person.notes}</p></div></div>
              {/each}
            {/if}
            {#if selected.role === 'investor'}
              <details class="fund-institution-tools"><summary>Institution and brief tools</summary>
              <div class="fund-record-subhead">LP institution</div>
              <p class="fund-muted">Group people only after confirming they work with the same LP institution. Their email evidence remains attributed to each address.</p>
              <label class="fund-field">Institution
                <select class="fund-inline-role" value={selected.institution_id || ''} disabled={savingInstitution}
                        onchange={(event) => saveInstitution(event.currentTarget.value || null)}>
                  <option value="">Not grouped</option>
                  {#each workspace.institutions || [] as institution}
                    <option value={institution.institution_id}>{institution.name}</option>
                  {/each}
                </select>
              </label>
              <div class="fund-compose-actions">
                <input aria-label="New LP institution name" bind:value={newInstitutionName} maxlength="160" placeholder="New LP institution" />
                <button class="today-mini-btn" disabled={savingInstitution || !newInstitutionName.trim()} onclick={createAndAssignInstitution}>Create and group</button>
              </div>
              {#if selectedInstitution}
                <!-- The members themselves are listed once, under "People on this LP". -->
                <div class="fund-record-subhead">Institution brief</div>
                <p class="fund-muted">Choose the confirmed LP contacts to include. Their bounded email evidence and team notes will be sent to Anthropic only when you click Prepare. Grouping alone does not prove an investment.</p>
                {#each institutionBriefMembers as member (member.counterparty_id)}
                  <label class="fund-toolbar-filter"><input type="checkbox" value={member.counterparty_id}
                    bind:group={institutionBriefContacts} disabled={institutionBriefGenerating || (!institutionBriefContacts.includes(member.counterparty_id) && institutionBriefContacts.length >= 8)} /> {member.display_name} · {member.email}</label>
                {/each}
                {#if groundableDocs.length}
                  <div class="fund-record-subhead">Optional firm documents</div>
                  {#each groundableDocs as doc (doc.document_id)}
                    <label class="fund-toolbar-filter"><input type="checkbox" value={doc.document_id}
                      bind:group={institutionBriefDocuments} disabled={institutionBriefGenerating || (!institutionBriefDocuments.includes(doc.document_id) && institutionBriefDocuments.length >= 3)} /> {doc.filename}</label>
                  {/each}
                {/if}
                <label class="fund-field">Brief focus (optional)<textarea bind:value={institutionBriefInstruction} rows="2" maxlength="1000" placeholder="Questions to resolve or open points to review"></textarea></label>
                <button class="btn-primary" disabled={institutionBriefGenerating || !institutionBriefContacts.length} onclick={prepareInstitutionBrief}>{institutionBriefGenerating ? 'Preparing…' : 'Prepare institution brief'}</button>
                {#if institutionBriefError}<p class="fund-alert" role="alert">{institutionBriefError}</p>{/if}
                {#if institutionBrief?.brief}
                  <section class="fund-output-card"><header class="fund-output-header"><h3>Saved institution brief</h3></header>
                    <div class="fund-output-body md-content">{@html renderSafeMarkdown(institutionBrief.brief)}</div>
                    <div class="fund-output-footer"><span class="fund-muted">Review only · {institutionBrief.source_manifest?.sources?.length || 0} cited mail/note sources{institutionBrief.source_manifest?.documents?.length ? ` · ${institutionBrief.source_manifest.documents.length} selected files` : ''}</span></div>
                    {#if institutionBrief.source_manifest?.sources?.length}
                      <div class="fund-output-body"><b>Sources</b><ul>
                        {#each institutionBrief.source_manifest.sources as source (source.number)}
                          <li>{source.number}. {source.contact_name} · {source.type === 'email' ? source.subject || 'Email' : source.title || 'Team note'}</li>
                        {/each}
                      </ul></div>
                    {/if}
                    {#if institutionBrief.source_manifest?.documents?.length}
                      <div class="fund-output-body"><b>Selected files</b><ul>
                        {#each institutionBrief.source_manifest.documents as doc (doc.document_id)}<li>{doc.filename}</li>{/each}
                      </ul></div>
                    {/if}
                  </section>
                {/if}
                <div class="fund-record-subhead">Recent institution email activity</div>
                <p class="fund-muted">Mail stays attributed to each person and mailbox. This is a bounded list of recent messages, not a complete institution history.</p>
                {#if institutionActivityLoading}<div class="segment-loading">Loading institution activity…</div>
                {:else if institutionActivityError}<p class="fund-alert" role="alert">{institutionActivityError}</p>
                {:else if institutionActivity.length}
                  {#each institutionActivity as item (`${item.counterparty_id}:${item.owner_email}:${item.message_id}`)}
                    <div class="fund-person-row"><div><button class="fund-row-link" onclick={() => { const person = allContacts.find(c => c.counterparty_id === item.counterparty_id); if (person) openContact(person); }}>{item.subject || '(No subject)'}</button><p>{item.display_name} · {mailDate(item.sent_at)} · synced by {item.owner_email}</p></div></div>
                  {/each}
                {:else}<p class="fund-muted">No synced mail for the confirmed members of this institution.</p>{/if}
              {/if}
              </details>
            {/if}
          {:else if detailTab === 'memory'}
            <!-- Known vs unknown is the LP-native structure (designs/lp-relationship-v1.html).
                 Both halves are read off records, so they are honest before any brief exists. -->
            <div class="fund-record-subhead"><span class="fund-ir-sparkle" aria-hidden="true">✦</span> What {agent} knows <span class="tab-count">{lpKnown.length}</span></div>
            {#if lpKnown.length}
              {#each lpKnown as fact}
                <div class="lp-fact">
                  <div>{fact.text}</div>
                  <span class="fund-muted">{fact.src}</span>
                  {#if fact.fix}<button class="fund-row-link lp-fix" onclick={fact.fix.run}>{fact.fix.label}</button>{:else}<span class="lp-fix-gap"></span>{/if}
                </div>
              {/each}
            {:else}
              <p class="fund-muted">Nothing recorded for this LP yet.</p>
            {/if}
            {#if lpUnknown.length}
              <div class="fund-record-subhead" style="margin-top:26px">Still unknown <span class="tab-count">{lpUnknown.length}</span></div>
              <ul class="lp-unknown">{#each lpUnknown as gap}<li>{gap}</li>{/each}</ul>
              <p class="fund-muted">{agent} fills these in as email and notes come in, and asks you when it cannot.</p>
            {/if}
            {#if selected.role === 'investor' && !selected.automated_address}
              <div class="fund-record-toolbar"><button class="btn-primary" disabled={!!generating || !(contactContext?.messages.some(m => m.evidence_number) || contactContext?.notes?.length)} onclick={() => generate('brief')}>{generating === 'brief' ? 'Preparing…' : '✦ Prepare LP brief'}</button></div>
              {@render generationSources()}
              <label class="fund-field">Focus for the next brief<textarea bind:value={instruction} rows="2" placeholder="Optional: questions or open points to focus on"></textarea></label>
              <div class="fund-compose-heading"><h3>Team notes</h3><button class="today-mini-btn" onclick={() => startNote()}>+ Add note</button></div>
              <p class="fund-muted">Saved context for this LP. The author can edit their note; your IR team can read it.</p>
              {#if noteEditorOpen}
                <div class="card fund-note-editor">
                  <label class="fund-field">Title<input bind:value={noteTitle} maxlength="160" placeholder="Question, commitment, or meeting context" /></label>
                  <label class="fund-field">Note<textarea bind:value={noteBody} rows="5" maxlength="10000" placeholder="Record what you know, and where it came from."></textarea></label>
                  <div class="fund-compose-actions"><button class="btn-primary" onclick={saveNote} disabled={noteSaving || !noteTitle.trim() || !noteBody.trim()}>{noteSaving ? 'Saving…' : 'Save note'}</button><button class="today-mini-btn" onclick={() => noteEditorOpen = false} disabled={noteSaving}>Cancel</button></div>
                </div>
              {/if}
              {#if contactContext?.notes?.length}
                {#each contactContext.notes as note (note.note_id)}
                  <section class="fund-output-card"><header class="fund-output-header"><div><h3>{note.title}</h3><p>{note.author_email} · Updated {mailDate(note.updated_at)}</p></div></header><div class="fund-output-body md-content">{@html renderSafeMarkdown(note.body)}</div>{#if note.author_email.toLowerCase() === $userEmailStore.toLowerCase()}<div class="fund-output-footer"><button class="today-mini-btn" onclick={() => startNote(note)}>Edit note</button></div>{/if}</section>
                {/each}
              {:else}<div class="empty-state">No team notes for this LP yet.</div>{/if}
              {#if contactContext?.outputs.brief}<section class="fund-output-card"><header class="fund-output-header"><span class="fund-output-mark">✦</span><div><h3>Relationship brief</h3><p>Saved for your review · Based on cited LP email, team notes or selected Fund files</p></div></header><div class="fund-output-body md-content">{@html renderSafeMarkdown(contactContext.outputs.brief)}</div>{@render outputSources('brief')}</section>
              {:else}<div class="empty-state">No relationship brief saved yet.</div>{/if}
              {#if contactContext?.outputs.draft}<section class="fund-output-card"><header class="fund-output-header"><span class="fund-output-mark">✦</span><div><h3>Last generated email response</h3><p>Review the model's answer before using it</p></div></header><div class="fund-output-body md-content">{@html renderSafeMarkdown(contactContext.outputs.draft)}</div>{@render outputSources('draft')}<div class="fund-output-footer"><button class="today-mini-btn" onclick={() => useDraftResult(contactContext?.outputs.draft || '')}>Use as editable email</button></div></section>{/if}
            {:else}<div class="empty-state">Confirm this contact as an LP to prepare relationship memory.</div>{/if}
          {:else if detailTab === 'files'}
            <!-- The spec's "Firm materials sent" needs per-LP attachment tracking, which
                 nothing records yet (open question 2 in lp-relationship-v1.html). An
                 honest empty state beats a list that silently means something else. -->
            <div class="fund-record-subhead">Firm materials sent <span class="tab-count">0</span></div>
            <p class="fund-muted">Which firm files this LP has already received is not recorded yet. Attachments are chosen per email in the composer and are not tracked against the relationship, so this stays empty until that lands — the Emails tab is the reliable record meanwhile.</p>
            <div class="fund-record-subhead" style="margin-top:26px">Firm library <span class="tab-count">{workspace.documents.length}</span></div>
            <p class="fund-muted">Shared across this IR workspace, not specific to this LP. For a brief or draft, explicitly choose text-readable files in Emails or Memory; only your selection is sent to Anthropic for that generation.</p>
            {#if workspace.documents.length}{#each workspace.documents as doc}<div class="fund-file-row"><div><b>{doc.title}</b><span>{doc.filename} · {doc.category === 'fund' ? 'Fund' : 'Collateral'}</span></div><a class="today-mini-btn" href={fundDocumentUrl(doc.document_id)} download={doc.filename} onclick={(e) => void downloadSupportFile(e, fundDocumentUrl(doc.document_id), doc.filename).catch((err) => alert(err.message))}>Download</a></div>{/each}
            {:else}<div class="empty-state">No Fund files uploaded yet. Add them in Teach {agent.split(' ')[0]}.</div>{/if}
          {/if}
        </section>
      </div>
      {#if showMikeBubble}
        <button type="button" class="fund-mike-bubble" aria-label="Open {agent} chat"
                title="Open {agent} chat" onclick={openMikeBubble}>✦</button>
      {/if}
      {#if mikeOverlayOpen && mikeNarrow}
        <div class="fund-mike-overlay-backdrop" onclick={() => mikeOverlayOpen = false} role="presentation"></div>
        <aside class="fund-mike-overlay" aria-label="{agent} chat">
          <div class="fund-mike-head">
            <div class="fund-mike-title"><span aria-hidden="true">✦</span> {agent}</div>
            <div class="fund-mike-actions">
              <button type="button" title="Open {agent} as a tab" aria-label="Open {agent} as a tab" onclick={() => setMikeMode('tab')}>⛶</button>
              <button type="button" title="Close {agent} overlay" aria-label="Close {agent} overlay" onclick={() => mikeOverlayOpen = false}>×</button>
            </div>
            <div class="fund-mike-reading">{mikeContextLabel}</div>
          </div>
          {@render assistantRail()}
        </aside>
      {/if}
    {:else}<div class="empty-state">This contact is unavailable.</div>{/if}
  {/if}
{:else if error}
  <div class="empty-state" role="alert">Could not load your workspace. {error} <button type="button" onclick={load}>Retry</button></div>
{/if}

<!-- One LP conversation, rendered in the right rail, full tab or narrow overlay. -->
{#snippet outputSources(kind: 'brief' | 'draft')}
  {#if contactContext?.output_provenance?.[kind]}
    {@const record = contactContext.output_provenance[kind]}
    <details class="fund-output-footer">
      <summary>Sources used for this generation</summary>
      <p class="fund-muted">{record.generated_at ? mailDate(record.generated_at) : 'Date unavailable'} · Requested model {record.model_id} · {record.prompt_version}</p>
      {#each record.evidence || [] as source}
        <div><button class="fund-row-link" onclick={() => { detailTab = 'emails'; if (selected) toggleMessage(selected.counterparty_id, source.owner_email, source.message_id); }}>Email {source.number}: {source.subject || '(No subject)'}</button><span class="fund-hint"> · {mailDate(source.sent_at)} · {source.owner_email}</span></div>
      {/each}
      {#each record.notes || [] as source}<div class="fund-muted">Team note {source.number}: {source.title} · Updated {mailDate(source.updated_at)}</div>{/each}
      {#each contactContext?.output_sources?.[kind] || [] as source}<div><a href={fundDocumentUrl(source.document_id)} download={source.filename} onclick={(e) => void downloadSupportFile(e, fundDocumentUrl(source.document_id), source.filename).catch((err) => alert(err.message))}>Document {source.number}: {source.title} ({source.filename})</a></div>{/each}
    </details>
  {:else if contactContext?.output_sources?.[kind]?.length}
    <div class="fund-output-footer"><span class="fund-muted">Files provided for generation:</span>{#each contactContext.output_sources[kind] || [] as source}<a href={fundDocumentUrl(source.document_id)} download={source.filename} onclick={(e) => void downloadSupportFile(e, fundDocumentUrl(source.document_id), source.filename).catch((err) => alert(err.message))}>Document {source.number}: {source.title} ({source.filename})</a>{/each}</div>
  {/if}
{/snippet}

{#snippet generationSources()}
  {#if workspace?.documents.length}
    <details class="fund-generation-sources">
      <summary>Fund files for AI generation {groundingDocumentIds.length ? `(${groundingDocumentIds.length} selected)` : '(optional)'}</summary>
      <p class="fund-muted">When you prepare a brief or draft, up to 6,000 characters from each selected PDF, DOCX, TXT, MD or CSV file (5 MB maximum) are sent to Anthropic. Files you leave unselected are not read for generation. A confirmed LP still needs synced mail or a team note. This does not attach a file to an email.</p>
      {#if groundableDocs.length}
        {#each groundableDocs as doc (doc.document_id)}
          <label class="fund-field"><input type="checkbox" value={doc.document_id} bind:group={groundingDocumentIds}
            disabled={groundingDocumentIds.length >= 3 && !groundingDocumentIds.includes(doc.document_id)} />
            {doc.title} · {doc.filename}</label>
        {/each}
      {:else}
        <p class="fund-muted">No text-readable files under 5 MB. PDF, DOCX, TXT, MD and CSV are supported.</p>
      {/if}
    </details>
  {/if}
{/snippet}

{#snippet assistantRail()}
  {#if messages.length > 0}
    <div class="rail-util">
      <button type="button" class="rail-restart" onclick={restartConversation} title="Clear this conversation and start over">↺ Restart</button>
    </div>
  {/if}

  <div class="chat-messages">
    {#if selected?.automated_address}
      <div class="rail-starter">
        <div class="rail-starter-av">✦</div>
        <div class="rail-starter-t">A direct email address is needed</div>
        <div class="rail-starter-s">This address sends automated notifications. Add the person's real email address in Contacts before asking about the relationship.</div>
      </div>
    {:else if !canAskAssistant}
      <div class="rail-starter">
        <div class="rail-starter-av">✦</div>
        <div class="rail-starter-t">Confirm the relationship first</div>
        <div class="rail-starter-s">{agent} answers from this contact's synced mail, so it waits until you choose LP under Relationship.</div>
      </div>
    {:else if messages.length === 0 && !chatLoading}
      <div class="rail-starter">
        <div class="rail-starter-av">✦</div>
        <div class="rail-starter-t">Ask {agent} about {selectedName}</div>
        <div class="rail-starter-s">Ask about this relationship or plan the next move. Nothing is sent.</div>
      </div>
    {/if}
    {#each messages as msg}
      {#if msg.role === 'assistant'}
        <div class="gmsg">
          <div class="gmsg-body">
            <div class="md-content">{@html renderSafeMarkdown(msg.content)}</div>
          </div>
        </div>
      {:else}
        <div class="chat-msg user">{msg.content}</div>
      {/if}
    {/each}
    {#if chatLoading}
      <div class="chat-msg assistant" style="display:flex; align-items:center; gap:8px;">
        <span class="spinner"></span> <span class="text-muted">Thinking…</span>
      </div>
    {/if}
    {#if chatError}<p class="fund-alert" role="alert">{chatError}</p>{/if}
  </div>

  {#if canAskAssistant}
    <div class="ask-suggest">
      <button class="sug-chip" onclick={() => quickAsk('What should my next touchpoint with this LP be?')}>Plan the next move</button>
      <button class="sug-chip" onclick={() => quickAsk('What has this LP asked for that is still open?')}>What is still open?</button>
      <button class="sug-chip" onclick={() => quickAsk('What changed in this relationship recently?')}>What changed recently?</button>
    </div>
    <div class="strat-ask">
      <textarea
        bind:value={chatInput}
        placeholder="Ask about {selectedName}, or plan the next move…"
        class="flex-1"
        rows="2"
        style="resize: vertical; min-height: 2.4em; max-height: 12em; font-family: inherit;"
        onkeydown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleChat();
          }
        }}
      ></textarea>
      <button class="primary" onclick={handleChat} disabled={!chatInput.trim() || chatLoading}>Send</button>
    </div>
  {/if}
{/snippet}

{#snippet fundContactRow(contact: FundContact, grouped: boolean)}
  <tr class="data-row fund-contact-row" class:fund-contact-member={grouped}>
    <td></td>
    <td><div class="fund-contact-cell">
      <span class="fund-contact-avatar" style:background={avatarColor(contact.email || contact.display_name)}>{initialsFor(contact.display_name, contact.email || '')}</span>
      <div class="fund-contact-meta"><button class="fund-row-link" onclick={() => openContact(contact)}>{contact.display_name}</button>
        {#if contact.automated_address}<div class="fund-hint">Automated notification address</div>{/if}
      </div>
    </div></td>
    <td class="fund-contacts-email">{contact.email || '—'}</td>
    <td><select class="fund-inline-role" aria-label={`Relationship for ${contact.display_name}`} value={displayRole(contact) || ''} disabled={!!savingRole || !contact.email} onchange={(event) => setRole(contact, event.currentTarget.value as FundRole, event.currentTarget)}>
      <option value="" disabled>Unclassified</option><option value="investor" disabled={contact.automated_address}>LP</option><option value="portfolio" disabled={contact.automated_address}>Portfolio</option><option value="neither">Neither</option>
    </select>
      {#if !contact.role && contact.suggested_source === 'company_investor'}<div class="fund-hint" title={contact.suggested_reason || ''}>Company investor signal; Fund LP not verified</div>
      {:else if !contact.role && contact.suggested_source === 'model_review'}<div class="fund-hint" title={contact.suggested_reason || ''}>Needs final confirmation</div>
      {:else if contact.set_by === 'model'}<div class="fund-hint" title={contact.suggested_reason || ''}>Filed by Mike from mail · role can be corrected</div>
      {:else if !contact.role && contact.suggested_role}<div class="fund-hint">Subject hint only</div>{/if}
      {#if !contact.role && displayRole(contact) === 'investor'}<button class="today-mini-btn" disabled={!!savingRole} onclick={() => setRole(contact, 'investor')}>Confirm LP</button>{/if}
    </td>
    <td>{mailDate(contact.last_seen)}</td>
    <td><button class="fund-row-link" onclick={() => openContact(contact)}>Open →</button></td>
  </tr>
{/snippet}

{#snippet fundComposeArea()}
              {#if isSupportView() && !canUseAs()}
                <p class="fund-hint">This account has not accepted staff Use as actions.</p>
              {:else}
                <div class="compose-area fund-compose">
                  <div class="fund-compose-heading"><b>{isSupportView() ? 'Review-only draft' : composeReplyMessageId ? 'Reply in thread' : 'New email'}</b><button class="today-mini-btn" onclick={closeFundEmail} disabled={sending}>Close</button></div>
                  {#if !composeReplyMessageId && selectedInstitution && institutionBriefMembers.length > 1}
                    <label class="fund-field fund-email-recipient">To
                      <select aria-label="New email recipient" value={selected?.counterparty_id || ''}
                        onchange={(event) => { if (!chooseNewEmailRecipient(event.currentTarget.value)) event.currentTarget.value = selected?.counterparty_id || ''; }}>
                        {#each institutionBriefMembers as member (member.counterparty_id)}
                          <option value={member.counterparty_id}>{member.display_name} · {member.email}</option>
                        {/each}
                      </select>
                    </label>
                  {/if}
                  <p class="fund-muted">{isSupportView() ? 'Saved only in Dealton' : 'From your connected mailbox'}{#if composeReplyMessageId || !selectedInstitution || institutionBriefMembers.length <= 1} · To <b>{selected?.email || '—'}</b>{/if}{isSupportView() ? '.' : '. Read and edit before sending.'}</p>
                  <div class="fund-compose-generate">
                    <input aria-label="Draft instructions (optional)" bind:value={draftInstruction} maxlength="1000" placeholder="Any extra instructions (optional)" />
                    <button class="primary" disabled={!!generating || !canGenerateDraft} onclick={generateComposeDraft}>{generating === 'draft' ? 'Generating…' : composeReplyMessageId ? 'Generate reply' : 'Generate draft'}</button>
                  </div>
                  {#if !canGenerateDraft && !contextLoading}<p class="fund-hint">AI drafting needs a synced email or team note. You can still write this email yourself.</p>{/if}
                  {@render generationSources()}
                  <!-- Replying keeps the subject the provider threads on. Microsoft reply
                       uses strict threading and fails rather than silently opening a new
                       email, so an edited subject turns a reply into an error. Same lock
                       the Sales composer applies when it is on an existing thread. -->
                  <!-- The space lives inside the span; Svelte drops it at the start of an if-block. -->
                  <label class="fund-field">Subject{#if composeReplyMessageId}<span class="fund-muted">&nbsp;(locked to this thread)</span>{/if}
                    <input bind:value={composeSubject} maxlength="250" placeholder="Email subject"
                           readonly={!!composeReplyMessageId}
                           title={composeReplyMessageId ? 'Subject stays fixed while replying in an existing thread' : ''} /></label>
                  {#if !isSupportView()}
                    <label class="fund-field">CC (optional)<input bind:value={composeCc} maxlength="1000" placeholder="name@example.com, colleague@example.com" /></label>
                  {#if ccSuggestions.length}
                    <!-- Known addresses for this LP and the colleagues your team already has
                         mail with. Appends to the CC field; it never replaces what you typed. -->
                    {#if ccLpContacts.length}
                      <div class="fund-cc-picks">
                        <span class="fund-cc-label">CC &mdash; LP contacts</span>
                        {#each ccLpContacts as address}
                          <button type="button" class="fchip" disabled={ccAlreadyIncluded(address)}
                                  onclick={() => addCc(address)}>{address}</button>
                        {/each}
                      </div>
                    {/if}
                    {#if ccTeam.length}
                      <div class="fund-cc-picks">
                        <span class="fund-cc-label">CC &mdash; your team</span>
                        {#each ccTeam as address}
                          <button type="button" class="fchip" disabled={ccAlreadyIncluded(address)}
                                  onclick={() => addCc(address)}>{address}</button>
                        {/each}
                      </div>
                    {/if}
                  {/if}
                  {/if}
                  <label class="fund-field">Message<EmailBodyEditor bind:value={composeBody} rows={12} placeholder="Write your email…" /></label>
                  {#if polishChanges}
                    <div class="polish-changes mb-3" role="status">
                      <div class="text-xs font-bold mb-1">Changes made:</div>
                      <pre class="text-xs">{polishChanges}</pre>
                    </div>
                  {/if}
                  {#if !isSupportView()}
                  {#if workspace?.documents.length}
                    <div class="text-xs text-dim">Attachments &mdash; firm library</div>
                    <div class="chip-row mb-2">
                      {#each workspace?.documents || [] as doc (doc.document_id)}
                        <button type="button" class="chip attach" class:selected={composeDocumentIds.includes(doc.document_id)}
                                title={doc.title} onclick={() => toggleComposeDocument(doc.document_id)}>
                          <svg class="clip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>{doc.filename}
                        </button>
                      {/each}
                    </div>
                  {/if}
                  <div class="text-xs text-dim">Files for this email only</div>
                  <div class="chip-row mb-2">
                    {#each composeFiles as file, index (`${file.name}:${file.size}:${index}`)}
                      <span class="chip selected fund-file-chip">{file.name}
                        <button type="button" aria-label="Remove {file.name}" onclick={() => removeComposeFile(index)}>&times;</button>
                      </span>
                    {/each}
                    <label class="today-mini-btn fund-attach-btn">+ Attach file
                      <input type="file" multiple class="sr-only" onchange={addComposeFiles} />
                    </label>
                  </div>
                  <p class="fund-hint">Up to five files and 10 MB total. Selected files are sent or held with the scheduled email; they are not added to the shared document library.</p>
                  <label class="fund-field"><input type="checkbox" bind:checked={composeTracking} /> Track email opens (optional)</label>
                  <p class="fund-hint">Open counts are approximate: mail clients may load or block images without the recipient opening the message.</p>
                  <label class="fund-field"><input type="checkbox" bind:checked={composeMeeting} /> Include a calendar invite</label>
                  {#if composeMeeting}
                    <label class="fund-field">Meeting title<input bind:value={meetingSummary} maxlength="160" /></label>
                    <label class="fund-field">Start<input type="datetime-local" bind:value={meetingStart} /></label>
                    <label class="fund-field">End<input type="datetime-local" bind:value={meetingEnd} /></label>
                    <label class="fund-field">Location (optional)<input bind:value={meetingLocation} maxlength="200" /></label>
                  {/if}
                  {/if}
                  <div class="draft-actions fund-compose-commit">
                    {#if !isSupportView()}
                      <button class="btn-primary" onclick={sendReviewedEmail} disabled={sending || !composeSubject.trim() || !composeBody.trim()}>{sending ? 'Working…' : 'Send email'}</button>
                      <button class="today-mini-btn" onclick={saveEmailToDrafts} disabled={sending || !composeSubject.trim() || !composeBody.trim() || composeMeeting || composeTracking} title={composeMeeting || composeTracking ? 'Drafts support message and attachments; send or schedule to include invite or tracking' : ''}>Save to mailbox Drafts</button>
                      <button class="today-mini-btn" onclick={() => scheduleOpen = !scheduleOpen} disabled={sending}>Schedule send</button>
                    {:else if canUseAs()}
                      <button class="btn-primary" onclick={saveReviewDraftInDealton} disabled={savingReviewDraft || !composeSubject.trim() || !composeBody.trim()}>{savingReviewDraft ? 'Saving…' : 'Save review-only draft'}</button>
                    {/if}
                    <span class="act-sep" aria-hidden="true"></span>
                    <button class="today-mini-btn draft-ai-btn" disabled={polishing || !composeBody.trim()} onclick={() => editFundEmail('polish')}><span class="draft-spark">&#10022;</span> {polishing ? '…' : 'Polish wording'}</button>
                    <button class="today-mini-btn draft-ai-btn" disabled={polishing || !composeBody.trim()} onclick={() => editFundEmail('refine')}><span class="draft-spark">&#10022;</span> {polishing ? '…' : 'Refine content'}</button>
                  </div>
                  <p class="fund-hint fund-compose-note">{isSupportView() ? 'This draft stays in Dealton. Polish and Refine send the text to Anthropic; no email is sent or changed.' : 'You confirm the recipients before it sends or is scheduled. Polish and Refine send this draft text to Anthropic — review every change.'}</p>
                  {#if scheduleOpen && !isSupportView()}
                    <div class="fund-compose-actions">
                      <label class="fund-field">Date<input type="date" bind:value={scheduleDate} /></label>
                      <label class="fund-field">Time<input type="time" bind:value={scheduleTime} /></label>
                      <button class="btn-primary" onclick={scheduleReviewedEmail} disabled={sending || !scheduleDate || !scheduleTime}>Confirm schedule</button>
                    </div>
                  {/if}
                </div>
              {/if}
{/snippet}

<style>
  .fund-ready-draft { margin-top: 10px; padding: 10px 12px; border: 1px solid var(--border); border-radius: var(--radius-sm); background: var(--bg-hover); max-width: 72ch; }
  .fund-ready-draft summary { cursor: pointer; color: var(--text); font-size: 12px; font-weight: 600; }
  .fund-ready-draft p { white-space: pre-wrap; overflow-wrap: anywhere; color: var(--text-muted); font-size: 12px; line-height: 1.55; }
  .fund-ready-draft small { color: var(--text-dim); font-size: 11px; }
  .today-topbar { display: flex; align-items: center; justify-content: flex-end; margin-bottom: 14px; }
  .fund-reload-alert { margin-bottom: 14px; }
  .today-section { margin-bottom: 28px; scroll-margin-top: 56px; }
  .today-section-header { display: flex; align-items: baseline; gap: 12px; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid var(--border); }
  .today-section-header h2 { font-family: inherit; font-size: 15px; font-weight: 500; letter-spacing: -0.01em; color: var(--text); }
  .today-section-count { font-size: 11px; color: var(--text-dim); font-family: var(--font-mono); }
  /* Only a recorded due date can color a next-step status. Mail timing and
     suggested relationships keep neutral bars. */
  #fund-briefing { --fund-today-accent: var(--accent); }
  #fund-calls { --fund-today-accent: var(--blue); }
  .fund-action-card { border: 1px solid var(--border); border-radius: var(--radius); background: var(--bg-surface); padding: 18px; box-shadow: var(--shadow-sm); }
  .fund-action-card h3 { margin: 0 0 7px; font-size: 15px; }
  .fund-action-card p,.fund-muted { color: var(--text-muted); font-size: 13px; line-height: 1.6; }
  .fund-action-card p { margin: 0 0 14px; }
  /* `.today-empty`, `.today-expand-btn`, `.action-btn` and `.segment-loading` are Sales
     classes whose rules live in Today / Contacts / ClientView's SCOPED style blocks, so a
     page wearing the name gets nothing from them. Each is reproduced verbatim below —
     change one, change the other. (Promotion into app.css is the real fix: see
     `todos/fund-relationships-design.md`.) */
  .today-empty { font-size: 12.5px; color: var(--text-dim); font-style: italic; padding: 14px 16px; background: var(--bg-surface); border: 1px dashed var(--border); border-radius: 8px; }
  /* A Retry button inside the box inherits `font-style` — buttons only reset family and size. */
  .today-empty .today-mini-btn { font-style: normal; }
  .segment-loading { display: flex; align-items: center; gap: 8px; padding: 24px 12px; color: var(--text-dim); font-size: 13px; }
  /* Toolbar pill: `.fchip` geometry, because it shares that row with the filter chips. */
  .action-btn { display: inline-flex; align-items: center; background: transparent; color: var(--text-muted); border: 1px solid var(--border); padding: 7px 13px; border-radius: 20px; font-size: 12px; font-weight: 500; cursor: pointer; font-family: inherit; text-decoration: none; white-space: nowrap; transition: all 0.1s; }
  .action-btn:hover { color: var(--text); border-color: var(--border-strong); background: var(--bg-hover); }
  .action-btn.primary-action { background: var(--accent); color: #fff; border-color: var(--accent); }
  .action-btn.primary-action:hover { background: color-mix(in srgb, var(--accent) 88%, black); border-color: color-mix(in srgb, var(--accent) 88%, black); }
  .fund-today-workbench-result-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
  .fund-today-workbench-context, .fund-today-workbench-source-note { margin: 10px 0 0; color: var(--text-muted); font-size: 12.5px; line-height: 1.6; }
  .fund-today-workbench-controls { display: grid; gap: 7px; margin-top: 16px; }
  .fund-today-workbench-controls label, .fund-today-workbench-result label, .fund-today-workbench-field-label { color: var(--text-muted); font-size: 12px; font-weight: 600; }
  /* Only the width is ours — the box comes from app.css's shared input/select rules. A
     padding override on a `select` puts the value under our arrow, which occupies the
     12–20px band inside the right edge (see the `select` rule's note). */
  .fund-today-workbench-controls select, .fund-today-workbench-result input { width: 100%; max-width: 640px; }
  .fund-today-workbench-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 16px; }
  .fund-today-workbench-result { display: grid; gap: 10px; margin-top: 18px; padding: 18px; border: 1px solid var(--border); border-radius: var(--radius); background: var(--bg); }
  .fund-today-workbench-result-head h4 { margin: 0; color: var(--text); font-size: 14px; }
  .fund-today-workbench-result-head span { color: var(--text-dim); font-size: 11px; }
  .fund-today-workbench-result .md-content { max-height: 520px; overflow-y: auto; color: var(--text-muted); font-size: 13px; line-height: 1.65; }
  .fund-today-workbench-sources { border-top: 1px solid var(--border); padding-top: 10px; color: var(--text-muted); font-size: 12px; }
  .fund-today-workbench-sources summary { cursor: pointer; }
  .fund-today-workbench-sources ol { margin: 9px 0 0; padding-left: 20px; }
  .fund-today-workbench-sources li { margin: 5px 0; }
  .fund-briefing-tile { --brief-accent: var(--text-dim); display: flex; align-items: center; gap: 16px; position: relative; overflow: hidden; min-height: 84px; margin-bottom: 10px; padding: 15px 18px; border: 1px solid var(--border); border-radius: 12px; background: var(--bg-surface); cursor: pointer; transition: border-color 0.12s, background 0.12s; }
  /* Overlaid strip, not `border-left`: a 3px border sits outside the radius and squares the corner off. */
  .fund-briefing-tile::before { content: ''; position: absolute; top: 0; bottom: 0; left: 0; width: 3px; background: var(--brief-accent); }
  .fund-briefing-tile:hover, .fund-briefing-tile:focus-visible { border-color: var(--border-strong); }
  /* Background lift only while collapsed — an open tile has its own `--bg-hover` boxes inside. */
  .fund-briefing-tile:not(.open):hover, .fund-briefing-tile:not(.open):focus-visible { background: var(--bg-hover); }
  .fund-briefing-tile.tone-red { --brief-accent: var(--red); }
  .fund-briefing-tile.tone-amber { --brief-accent: var(--orange); }
  .fund-briefing-tile.tone-blue { --brief-accent: var(--blue); }
  .fund-briefing-num { flex: none; width: 22px; text-align: center; color: var(--text-dim); font: 500 22px 'Fraunces', Georgia, serif; }
  .fund-briefing-body { flex: 1; min-width: 0; padding-right: 150px; }
  .fund-briefing-top { display: flex; align-items: center; gap: 9px; min-width: 0; }
  .fund-briefing-top strong { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text); font-size: 15px; }
  /* `.ctask-pill` in Today.svelte — same tint, border fraction, size and tracking. */
  .fund-briefing-pill { flex: none; border: 1px solid color-mix(in srgb, var(--brief-accent) 32%, transparent); border-radius: 5px; padding: 2px 7px; background: color-mix(in srgb, var(--brief-accent) 12%, transparent); color: var(--brief-accent); font: 600 9.5px var(--font-mono); letter-spacing: .04em; text-transform: uppercase; }
  .fund-briefing-chevron { color: var(--text-dim); font-size: 15px; transition: transform .12s; }
  .fund-briefing-tile.open .fund-briefing-chevron { transform: rotate(90deg); }
  .fund-briefing-preview { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; margin: 5px 0 0; color: var(--text-muted); font-size: 12.5px; line-height: 1.5; }
  .fund-briefing-tile.open .fund-briefing-preview { display: block; }
  .fund-briefing-source { margin: 8px 0 0; color: var(--text-dim); font-size: 11px; }
  .fund-briefing-detail { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--border); color: var(--text-muted); font-size: 12.5px; line-height: 1.6; cursor: default; }
  .fund-briefing-detail > strong { color: var(--text); font-size: 12px; }
  .fund-briefing-detail ul { margin: 8px 0 0; padding-left: 18px; }
  .fund-briefing-detail li { margin: 5px 0; overflow-wrap: anywhere; }
  .fund-briefing-tile.open .fund-briefing-body { padding-right: 0; }
  .fund-briefing-tile.open .fund-briefing-top { padding-right: 150px; }
  .fund-briefing-verb { position: absolute; right: 18px; top: 50%; transform: translateY(-50%); padding: 4px; border: 0; background: none; color: var(--accent-ink); font-size: 12px; font-weight: 600; white-space: nowrap; cursor: pointer; }
  .fund-briefing-tile.open .fund-briefing-verb { top: 14px; transform: none; }
  .fund-briefing-verb:hover { text-decoration: underline; text-underline-offset: 3px; }
  @media (max-width: 700px) { .fund-briefing-tile { align-items: flex-start; flex-wrap: wrap; } .fund-briefing-body { padding-right: 0; } .fund-briefing-tile.open .fund-briefing-top { padding-right: 0; } .fund-briefing-verb, .fund-briefing-tile.open .fund-briefing-verb { position: static; transform: none; margin-left: 38px; } }
  .fund-send-row, .fund-call-card { display: flex; align-items: center; gap: 14px; padding: 11px 14px; margin-bottom: 8px; border: 1px solid var(--border); border-left: 3px solid var(--blue); border-radius: var(--radius); background: var(--bg-surface); }
  .fund-send-row time, .fund-call-card time { min-width: 76px; flex: none; color: var(--blue); font: 500 12px var(--font-mono); }
  .fund-send-row > div, .fund-call-main { min-width: 0; flex: 1; }
  .fund-send-row strong, .fund-call-main h3 { display: block; color: var(--text); font-size: 13px; font-weight: 600; overflow-wrap: anywhere; }
  .fund-send-row small, .fund-call-main p { display: block; margin: 3px 0 0; color: var(--text-dim); font-size: 11.5px; overflow-wrap: anywhere; }
  .fund-action-card.fund-action-list { padding: 0; border: 0; background: none; box-shadow: none; }
  .fund-today-tile { position: relative; overflow: hidden; margin-bottom: 10px; border: 1px solid var(--border); border-radius: 12px; background: var(--bg-surface); transition: border-color 0.12s, background 0.12s; }
  .fund-today-tile::before { content: ''; position: absolute; top: 0; bottom: 0; left: 0; width: 3px; background: var(--fund-today-accent, var(--border-strong)); }
  .fund-today-tile.fund-today-overdue::before { background: var(--red); }
  .fund-today-tile:hover { border-color: var(--border-strong); }
  .fund-today-tile:not([open]):hover { background: var(--bg-hover); }
  .fund-today-tile summary { display: flex; align-items: center; gap: 14px; min-height: 66px; padding: 11px 18px; cursor: pointer; list-style: none; }
  .fund-today-tile summary::-webkit-details-marker { display: none; }
  .fund-today-tile summary:focus-visible { outline: 2px solid var(--accent); outline-offset: -3px; }
  .fund-today-num { width: 22px; flex: none; color: var(--text-dim); font: 500 22px 'Fraunces', Georgia, serif; text-align: center; }
  .fund-today-tile-main { display: flex; flex: 1; flex-direction: column; min-width: 0; gap: 2px; }
  .fund-today-tile-main strong { color: var(--text); font-size: 15px; font-weight: 600; overflow-wrap: anywhere; }
  .fund-today-tile-main small, .fund-today-tile-main > span { color: var(--text-muted); font-size: 11.5px; overflow-wrap: anywhere; }
  .fund-today-tile-main small { color: var(--text-dim); }
  .fund-today-tile-count { flex: none; color: var(--text-dim); font: 11px var(--font-mono); }
  .fund-today-chevron { flex: none; color: var(--text-dim); font-size: 17px; transition: transform 0.15s; }
  .fund-today-tile[open] .fund-today-chevron { transform: rotate(90deg); }
  .fund-today-detail { padding: 2px 18px 12px 54px; border-top: 1px solid var(--border); }
  .fund-today-person { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 12px 0; border-bottom: 1px solid var(--border); }
  .fund-today-person:last-child { border-bottom: 0; }
  .fund-today-person strong { color: var(--text); font-size: 12.5px; font-weight: 600; }
  /* `.fund-today-identity` and `.fund-today-clear` are gone with their markup: Chase
     recipients are `.fund-rec` rows now, and there is no "All clear" block — every
     section keeps its own header and says it is empty in its own place. */
  .fund-today-person p, .fund-today-note { margin: 3px 0 0; color: var(--text-muted); font-size: 12px; line-height: 1.5; overflow-wrap: anywhere; }
  .fund-today-note { margin: 12px 0 8px; }
  .fund-today-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 6px; flex: none; }
  /* Today's "Show N more": a plain left-aligned text affordance, never a bordered control. */
  .today-expand-btn { display: block; margin: 8px 0 0; padding: 2px 0; background: transparent; border: none; color: var(--text-dim); text-align: left; }
  .today-expand-btn:hover { background: transparent; border: none; color: var(--text-muted); }
  @media (max-width: 760px) { .fund-briefing-links { grid-template-columns: 1fr; } .fund-send-row, .fund-call-card, .fund-today-person { align-items: flex-start; flex-wrap: wrap; } .fund-today-person { flex-direction: column; } .fund-today-detail { padding-left: 18px; } .fund-today-actions { justify-content: flex-start; } .fund-today-tile-count { display: none; } }
  .fund-toolbar-filter { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-muted); }
  .fund-institution-strip { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0 16px; }
  .fund-institution-card { display: flex; flex-direction: column; gap: 4px; min-width: 180px; text-align: left; border: 1px solid var(--border); border-radius: var(--radius); background: var(--bg-surface); color: var(--text); padding: 10px 12px; cursor: pointer; }
  .fund-institution-card.active { border-color: var(--accent); }
  .fund-institution-card span { color: var(--text-muted); font-size: 11px; }
  .fund-page-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  /* The one "needs you" signal on Relationships. Built from the shared surface/border
     tokens and the shared `.status-pill-amber` — no colour of its own. */
  .fund-review-strip {
    display: flex; align-items: center; gap: 11px; flex-wrap: wrap;
    background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 10px 14px; margin-bottom: 14px;
    font-size: 13px; color: var(--text-muted);
  }
  .fund-review-strip b { color: var(--text); font-weight: 600; }
  .fund-review-strip .navlink { margin-left: auto; }
  .fund-row-link { border: 0; background: none; color: var(--text); font: inherit; font-weight: 600; padding: 0; cursor: pointer; text-align: left; }
  .fund-row-link:hover { color: var(--accent-ink); text-decoration: underline; }
  .fund-contacts-table .fund-contacts-gutter { width: 32px; }
  .fund-contacts-table .subgroup-row { cursor: pointer; border-top: 1px solid var(--border); }
  .fund-contacts-table .subgroup-row:hover { background: var(--bg-hover); }
  .fund-contacts-table .subgroup-row td { padding: 4px 14px 4px 32px; border-bottom: none; }
  .fund-contacts-table .subgroup-header { display: flex; align-items: center; gap: 10px; min-width: 0; font-size: 12.5px; }
  .fund-contacts-table .subgroup-arrow { color: var(--text-dim); font-size: 9px; width: 10px; flex: none; }
  .fund-contacts-table .subgroup-name { color: var(--text); font-weight: 600; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fund-contacts-table .subgroup-meta { color: var(--text-dim); font: 11px var(--font-mono); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fund-contact-row.fund-contact-member td:nth-child(2) { padding-left: 32px; }
  .fund-contact-cell { display: flex; align-items: center; gap: 10px; min-width: 0; }
  /* `.contact-avatar` on Contacts: a circle, white initials. The fill comes from
     `avatarColor()`, which returns a mid-tone gradient — `--text` on it is near-black in
     the light themes and near-white in dark, so it must not inherit. */
  .fund-contact-avatar { width: 26px; height: 26px; flex: none; border-radius: 50%; display: grid; place-items: center; color: #fff; font-size: 10.5px; font-weight: 600; letter-spacing: 0.02em; }
  .fund-contact-meta { min-width: 0; }
  .fund-contacts-email { overflow-wrap: anywhere; }
  .fund-hint { color: var(--text-dim); font-size: 11px; }
  /* The bar itself is the shared `.tabs`/`.tab`/`.tab-count` vocabulary in app.css
     (DECISIONS 2026-08-12: one tab-bar language app-wide, no underline). Only the
     top margin is local — this bar follows a page subtitle, not a toolbar. */
  .fund-dashboard-tabs { margin-top: 18px; }
  .fund-ir-back { margin: 0 0 16px; color: var(--accent-ink); }
  .fund-review-intro { max-width: 640px; margin: -5px 0 14px; color: var(--text-muted); font-size: 13.5px; line-height: 1.6; }
  .fund-review-progress { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; color: var(--text-dim); font: 11.5px var(--font-mono); }
  .fund-review-progress-track { flex: 0 0 220px; height: 4px; overflow: hidden; border-radius: 3px; background: var(--bg-hover); }
  .fund-review-progress-track > span { display: block; height: 100%; background: var(--text-muted); transition: width .2s; }
  .fund-review-keys { margin-left: auto; white-space: nowrap; font-size: 10.5px; }
  .fund-review-keys kbd { margin: 0 2px; padding: 0 4px; border: 1px solid var(--border); border-radius: 4px; color: var(--text-muted); font: inherit; }
  .fund-review-table { table-layout: fixed; min-width: 760px; }
  .fund-review-table col.fund-review-gutter { width: 36px; }
  .fund-review-table col.fund-review-who { width: 245px; }
  .fund-review-table col.fund-review-from { width: 225px; }
  .fund-review-table col.fund-review-action-col { width: 250px; }
  .fund-review-table th { cursor: default; }
  .fund-review-table th:last-child { text-align: right; }
  .fund-review-table td { vertical-align: middle; padding: 11px 12px; border-bottom: 1px solid var(--border); }
  .fund-review-table td.fund-review-marker { padding: 11px 0 11px 16px; }
  .fund-review-row.current td { background: var(--bg-hover); }
  .fund-review-row.current td.fund-review-marker { box-shadow: inset 2px 0 0 var(--text-muted); }
  .fund-review-row.done td { color: var(--text-dim); }
  .fund-review-table td.fund-review-marker span { display: block; width: 6px; height: 6px; border-radius: 50%; background: var(--accent); }
  .fund-review-group td { background: var(--bg-surface); border-bottom: 1px solid var(--border); }
  .fund-review-group-content { display: flex; align-items: center; gap: 10px; min-height: 28px; }
  .fund-review-group-content strong { color: var(--text); font-size: 13.5px; }
  .fund-review-count, .fund-review-call { color: var(--text-dim); font: 11.5px var(--font-mono); }
  .fund-review-call:first-letter { color: var(--accent); }
  /* `.today-mini-btn`'s metrics. `font: 12px inherit` was invalid — the shorthand takes no
     CSS-wide keyword for the family, so the whole declaration was dropped and the size with it. */
  .fund-review-confirm-all { margin-left: auto; padding: 5px 11px; border: 1px solid var(--border); border-radius: 5px; background: transparent; color: var(--text-muted); font-family: inherit; font-size: 12px; cursor: pointer; }
  .fund-review-confirm-all:hover { border-color: var(--border-strong); color: var(--text); }
  .fund-review-person, .fund-review-mail { display: flex; flex-direction: column; min-width: 0; }
  .fund-review-person button, .fund-review-mail button { overflow: hidden; border: 0; padding: 0; background: none; color: var(--text); text-align: left; text-overflow: ellipsis; white-space: nowrap; cursor: pointer; font: inherit; }
  .fund-review-person button { font-weight: 600; }
  .fund-review-mail button { font-size: 13px; }
  .fund-review-person button:hover, .fund-review-mail button:hover { color: var(--accent-ink); text-decoration: underline; }
  .fund-review-person span, .fund-review-mail span { overflow: hidden; color: var(--text-muted); text-overflow: ellipsis; white-space: nowrap; font-size: 12px; }
  .fund-review-mail span { color: var(--text-dim); font: 11px var(--font-mono); }
  .fund-review-action { text-align: right; white-space: nowrap; }
  .fund-review-action > span { color: var(--text-muted); font-size: 12px; }
  /* Segment metrics are `.seg`'s in app.css (and the spec's own `.seg`): 6px/11px at 11px. */
  .fund-review-segment { display: inline-flex; overflow: hidden; border: 1px solid var(--border); border-radius: var(--radius); }
  .fund-review-segment button { padding: 6px 11px; border: 0; border-left: 1px solid var(--border); background: transparent; color: var(--text-muted); font-size: 11px; cursor: pointer; }
  .fund-review-segment button:first-child { border-left: 0; }
  .fund-review-segment button:hover { background: var(--bg-hover); color: var(--text); }
  .fund-review-segment button.suggested { background: var(--bg-hover); color: var(--text); box-shadow: inset 0 0 0 1px var(--accent); }
  .fund-review-segment button.suggested span { color: var(--accent); }
  .fund-review-segment button:disabled, .fund-review-confirm-all:disabled { opacity: .5; cursor: default; }
  .fund-review-undo, .fund-review-more button { border: 0; padding: 0; background: none; color: var(--accent-ink); font-size: 12px; font-weight: 600; cursor: pointer; }
  .fund-review-undo { margin-left: 8px; }
  .fund-review-undo:hover, .fund-review-more button:hover { text-decoration: underline; }
  .fund-review-finish { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 18px; padding: 12px 14px; border: 1px solid var(--border); border-radius: 10px; color: var(--text-muted); font-size: 13px; }
  .fund-review-finish strong { color: var(--text); }
  .fund-review-finish .navlink { margin-left: auto; }
  .fund-review-summary { display: flex; align-items: center; gap: 14px; margin-top: 14px; padding: 15px 18px; border: 1px dashed var(--border-strong); border-radius: 10px; color: var(--text-muted); font-size: 13px; }
  .fund-review-summary strong { color: var(--text); }
  .fund-review-summary .fund-row-link { margin-left: auto; color: var(--accent-ink); white-space: nowrap; }
  @media (max-width: 760px) { .fund-review-summary { flex-wrap: wrap; } .fund-review-summary .fund-row-link { margin-left: 30px; } }
  .fund-review-scope { margin-top: 14px; padding: 12px 14px; border: 1px solid var(--border); border-radius: 10px; background: var(--bg-surface); color: var(--text-muted); font-size: 12px; line-height: 1.55; }
  .fund-review-scope strong { color: var(--text); font-size: 13px; }
  .fund-review-scope p { margin: 5px 0 0; }
  @media (max-width: 760px) { .fund-review-progress-track { flex-basis: 120px; } .fund-review-keys { display: none; } }
  .fund-portfolio-table { min-width: 650px; }
  .fund-portfolio-table th:last-child { width: 85px; }
  /* The mockup uses two left edges: pin/caret and LP name. Group rows and
     expanded rows are real table cells so their content follows the same grid. */
  .fund-ir-table { table-layout: fixed; min-width: 800px; }
  .fund-ir-table th:nth-child(1) { width: 56px; }
  .fund-ir-table th:nth-child(2) { width: auto; }
  .fund-ir-table th:nth-child(3) { width: 110px; }
  .fund-ir-table th:nth-child(4) { width: 118px; }
  .fund-ir-table th:nth-child(5), .fund-ir-table th:nth-child(6) { width: 100px; }
  .fund-ir-table td.fund-ir-pin { text-align: left; padding-left: 16px; padding-right: 0; }
  .fund-ir-table :global(.star-btn.on) { color: var(--orange); }
  .fund-ir-table :global(.group-row td) { background: var(--bg-surface); }
  .fund-ir-table td.fund-ir-group-gutter { padding: 8px 0 8px 16px; white-space: nowrap; }
  .fund-ir-group-gutter .group-arrow { margin-right: 7px; }
  .fund-ir-group-gutter .stage-dot { display: inline-block; width: 10px; height: 10px; vertical-align: middle; }
  .fund-ir-group-gutter .fund-ir-former-dot { border: 1.5px solid var(--text-dim); }
  .fund-ir-table td.fund-ir-group-name { padding: 8px 12px; }
  .fund-ir-group-name .group-meta { margin-left: 10px; }
  .fund-ir-entity td { vertical-align: middle; }
  .fund-ir-entity-name { color: var(--text); font-size: 13.5px; font-weight: 600; line-height: 1.35; overflow-wrap: anywhere; }
  /* Filled in this table, outline everywhere else — the Sales Dashboard's Status column does
     the same (`.status-badge-new` + the 15% tints of `.status-green`/`-orange`/`-red`). One
     tint strength for all three: a per-hue percentage makes them read as different weights. */
  .fund-ir-table :global(.status-pill) { border: 0; padding: 3px 9px; font-weight: 500; }
  .fund-ir-table :global(.status-pill-green) { background: color-mix(in srgb, var(--green) 15%, transparent); }
  .fund-ir-table :global(.status-pill-amber) { background: color-mix(in srgb, var(--orange) 15%, transparent); }
  .fund-ir-table :global(.fund-ir-overdue) { background: color-mix(in srgb, var(--red) 15%, transparent); }
  .fund-ir-table td.num { color: var(--text-muted); font: 12px var(--font-mono); white-space: nowrap; }
  .fund-ir-panel { display: grid; grid-template-columns: minmax(0, 1fr) 230px; gap: 32px; align-items: start; }
  .fund-ir-panel-main { min-width: 0; }
  .fund-ir-panel-main .fund-record-subhead { margin: 5px 0 7px; color: var(--text-muted); font-size: 12px; font-family: inherit; text-transform: none; letter-spacing: 0; }
  .fund-ir-section { border-top: 1px solid var(--border); padding-top: 12px; margin-top: 14px; }
  .fund-ir-summary { margin: 0; color: var(--text); font-size: 13px; line-height: 1.7; }
  .fund-ir-source { margin: 5px 0 0; color: var(--text-dim); font-size: 11px; }
  .fund-ir-open { display: grid; grid-template-columns: 34px minmax(0, 1fr) auto; gap: 12px; padding: 4px 0; color: var(--text); font-size: 12px; line-height: 1.7; }
  .fund-ir-open time { color: var(--text-dim); font: 11px var(--font-mono); white-space: nowrap; }
  .fund-ir-table-footer { display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: 8px 20px; margin-top: 12px; }
  .fund-ir-scan-note { margin: 0; color: var(--text-dim); font: 11px var(--font-mono); }
  .fund-ir-table-footer .fund-row-link { color: var(--accent-ink); font-size: 12px; }
  /* Stage menu. Trigger matches `.fund-inline-role`'s box so the rail's three fields line
     up; the open list is the page's own `.fchip-dd` vocabulary, not a new one. */
  .fund-ir-stage-dd { width: 100%; }
  .fund-ir-stage-menu { position: fixed; right: auto; z-index: 100; width: 240px; max-width: calc(100vw - 16px); max-height: calc(100vh - 16px); overflow-y: auto; }
  .fund-ir-stage-btn { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; min-height: 30px; padding: 2px 8px; background: var(--bg-surface); color: var(--text); border: 1px solid var(--border); border-radius: var(--radius-sm); font: inherit; font-size: 12px; cursor: pointer; text-align: left; }
  .fund-ir-stage-btn:hover:not(:disabled) { border-color: var(--border-strong); }
  .fund-ir-stage-btn:disabled { opacity: 0.5; cursor: default; }
  .fund-ir-dd-note { margin: 2px 8px 6px; font-size: 11px; line-height: 1.45; color: var(--text-muted); }
  .fund-ir-dd-sep { height: 1px; margin: 5px 0; background: var(--border); }
  .fund-ir-dd-label { padding: 2px 10px 4px; color: var(--text-dim); font: 10px var(--font-mono); text-transform: uppercase; letter-spacing: .08em; }
  .fund-ir-dd-toggle { display: flex; margin: 0 8px 6px; border: 1px solid var(--border); border-radius: var(--radius-sm); overflow: hidden; }
  .fund-ir-dd-toggle button { flex: 1; padding: 5px 4px; background: none; border: none; border-right: 1px solid var(--border); color: var(--text-muted); font: inherit; font-size: 11px; cursor: pointer; }
  .fund-ir-dd-toggle button:last-child { border-right: none; }
  .fund-ir-dd-toggle button.on { background: var(--bg-hover); color: var(--text); }
  .fund-ir-dd-toggle button:disabled { opacity: 0.5; cursor: default; }
  .fund-ir-rail-input { width: 100%; min-height: 30px; padding: 2px 8px; background: var(--bg-surface); color: var(--text); border: 1px solid var(--border); border-radius: var(--radius-sm); font: inherit; font-size: 12px; }
  .fund-ir-rail-input:disabled { opacity: 0.55; cursor: not-allowed; }
  /* Neutral label for an unavailable supporting feature. */
  .fund-stub-tag { color: var(--text-dim); font: 9.5px var(--font-mono); text-transform: none; letter-spacing: 0; border: 1px solid var(--border); border-radius: 4px; padding: 0 4px; vertical-align: 1px; }
  /* Briefing and Sales Chase both scan as one relationship per card. Keep the
     recipient's reason and actions together, even when several got one email. */
  .fund-chase-row { --chase-accent: var(--text-dim); position: relative; display: flex; flex-wrap: wrap; align-items: center; gap: 14px; margin-bottom: 10px; padding: 14px 16px; overflow: hidden; border: 1px solid var(--border); border-radius: 12px; background: var(--bg-surface); }
  .fund-chase-row::before { content: ''; position: absolute; inset: 0 auto 0 0; width: 3px; background: var(--chase-accent); }
  .fund-chase-row.fund-chase-due { --chase-accent: var(--orange); }
  .fund-chase-row.fund-chase-overdue { --chase-accent: var(--red); }
  .fund-chase-main { flex: 1 1 240px; min-width: 0; }
  .fund-chase-top { display: flex; flex-wrap: wrap; align-items: center; gap: 7px; }
  .fund-chase-top strong { color: var(--text); font-size: 14px; overflow-wrap: anywhere; }
  .fund-chase-organization { color: var(--text-dim); font-size: 11.5px; overflow-wrap: anywhere; }
  .fund-chase-context { margin: 5px 0 0; color: var(--text-muted); font-size: 12px; line-height: 1.5; overflow-wrap: anywhere; }
  .fund-chase-actions { display: flex; flex: 0 1 auto; flex-wrap: wrap; gap: 6px; justify-content: flex-end; }
  /* Outline only, pointer-events off: a stage is a read-only status here (§4). The hue
     comes from the one FUND_STAGES table the dashboard dots use, so the two agree.
     Geometry is Today's `.draft-stage-pill` — the same pill on a Sales Respond card. */
  .fund-stage-pill { border: 1px solid; border-radius: 4px; padding: 2px 6px; background: transparent; font-size: 10.5px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; white-space: nowrap; pointer-events: none; }
  .fund-plist-foot { margin-top: 10px; }
  @media (max-width: 760px) { .fund-chase-actions { width: 100%; padding-left: 38px; justify-content: flex-start; } }
  .fund-ir-rail { display: flex; flex-direction: column; align-items: stretch; gap: 6px; }
  .fund-ir-rail-label { color: var(--text-dim); font: 11px var(--font-mono); text-transform: uppercase; letter-spacing: .08em; margin-top: 8px; }
  .fund-ir-rail-help { margin: 0; color: var(--text-dim); font-size: 11px; }
  .fund-ir-rail-name { overflow-wrap: anywhere; padding: 8px 10px; border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-size: 12.5px; }
  /* Coral rail CTA — `.sug-chip.primary`'s treatment (the Sales rail's primary chip): the
     fill carries the commit, so the label is 600, not the button default. */
  .fund-ir-rail-open { margin-top: 12px; padding: 9px 12px; border-radius: var(--radius); background: var(--accent); color: #fff; font-size: 12.5px; font-weight: 600; text-align: left; }
  .fund-ir-rail-open:hover { color: #fff; background: var(--accent-hover); text-decoration: none; }
  @media (max-width: 1100px) {
    .fund-ir-panel { grid-template-columns: minmax(0, 1fr); gap: 16px; }
  }
  .fund-ir-secondary { color: var(--text-muted); font-size: 11px; line-height: 1.5; overflow-wrap: anywhere; }
  .fund-ir-detail-row td { background: var(--bg-surface); }
  .fund-ir-detail-row td.fund-ir-detail-gutter { padding: 0; }
  .fund-ir-detail-row td:not(.fund-ir-detail-gutter) { padding: 4px 12px 20px; }
  .fund-ir-add { color: var(--accent-ink); font-weight: 500; }
  .fund-ir-overdue { color: var(--red-ink); border-color: color-mix(in srgb, var(--red) 40%, transparent); }
  /* George's authorship mark, not a status (DESIGN-SYSTEM §1/§5) — coral is correct here. */
  .fund-ir-sparkle { color: var(--accent-ink); margin-right: 5px; }
  .fund-ir-muted-note { color: var(--text-dim); font-style: italic; }
  @media (max-width: 820px) {
    .fund-ir-panel { grid-template-columns: minmax(0, 1fr); gap: 16px; }
    .fund-ir-open { grid-template-columns: 34px minmax(0, 1fr); }
    .fund-ir-open time { grid-column: 2; }
  }
  /* The × lives inside the chip, so it takes the chip's colour rather than a button's. */
  .fund-file-chip { display: inline-flex; align-items: center; gap: 5px; }
  .fund-file-chip button { background: none; border: none; padding: 0; min-height: 0; color: inherit; font: inherit; line-height: 1; cursor: pointer; }
  /* A `<label>` wearing a button class still needs the button's own inline-flex box. */
  .fund-attach-btn { display: inline-flex; align-items: center; gap: 4px; cursor: pointer; }
  .fund-cc-picks { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin: -4px 0 8px; font-size: 11px; }
  /* Same label vocabulary as the Sales composer's two CC groups — the two sets are
     different decisions, so a flat row of addresses hid which was which. */
  .fund-cc-label { flex: none; width: 128px; color: var(--text-dim); }
  .fund-type-pill { border: 1px solid var(--border); border-radius: 12px; padding: 3px 9px; color: var(--text-muted); font-size: 11px; }
  /* LP relationship header (designs/lp-relationship-v1.html): title + SET facts only,
     actions collapsed to Pin + ⋯. */
  .lp-hdr { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
  .lp-hdr-text { min-width: 0; }
  /* The fixed chat rail starts alongside the header and editor. Keep both clear
     of it, including when Mike's saved width is restored on a different screen. */
  .client-header-wrap.rail-open {
    --fund-readable-w: calc(100% - var(--fund-mike-w) - clamp(16px, 3vw, 36px) - 16px);
  }
  .client-header-wrap.rail-open .lp-hdr-text,
  .client-header-wrap.rail-open .fund-action-card { max-width: var(--fund-readable-w); }
  @media (max-width: 1100px) and (min-width: 901px) {
    .client-header-wrap.rail-open {
      --fund-readable-w: calc(100% - min(250px, var(--fund-mike-w)) - clamp(16px, 3vw, 36px) - 16px);
    }
  }
  .lp-hdr-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
  .lp-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin-top: 3px; color: var(--text-muted); font-size: 12px; }
  .lp-meta span + span::before { content: '·'; margin-right: 10px; color: var(--text-dim); }
  /* A separator at the start of a wrapped line looks like stray punctuation. */
  @media (max-width: 1260px) {
    .client-header-wrap.rail-open .lp-meta span + span::before { content: none; }
  }
  .lp-nextstep { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 9px; padding: 5px 9px 5px 0;
                 color: var(--text); }
  .lp-nextstep .lbl { color: var(--text-dim); font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; }
  .lp-nextstep .due { color: var(--text-muted); font-size: 12px; }
  .lp-fact { display: flex; align-items: baseline; justify-content: space-between; gap: 16px;
             padding: 9px 0; border-bottom: 1px solid var(--border); font-size: 13px; }
  .lp-fact > span { flex-shrink: 0; font-size: 11px; }
  /* Source and fix sit in fixed columns so the facts read as a list, not a ragged edge. */
  .lp-fact > .fund-muted { width: 150px; text-align: right; }
  .lp-fix, .lp-fix-gap { flex-shrink: 0; width: 72px; text-align: right; font-size: 11px; }
  .lp-unknown { margin: 6px 0 8px; padding-left: 18px; color: var(--text-muted); font-size: 13px; line-height: 1.8; }
  .lp-more { position: relative; }
  /* Box, border and shadow are `.fchip-dd`'s in app.css — the app's other right-anchored
     menu. A lighter shadow made this one float differently from every dropdown beside it. */
  .lp-menu { position: absolute; right: 0; top: calc(100% + 4px); z-index: 40; min-width: 220px;
             display: flex; flex-direction: column; gap: 2px; padding: 5px;
             background: var(--bg-surface); border: 1px solid var(--border-strong); border-radius: var(--radius);
             box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28); }
  /* Rows are `.fchip-opt`: 7px/10px, 6px radius, weight 500 — buttons and labels mixed in
     one menu render at two weights unless the weight is stated. */
  .lp-mi { display: flex; align-items: center; justify-content: space-between; gap: 9px;
           width: 100%; padding: 7px 10px; border: 0; border-radius: var(--radius-sm); background: none;
           color: var(--text-muted); font-size: 12px; font-weight: 500; text-align: left; cursor: pointer; }
  .lp-mi:hover { background: var(--bg-hover); color: var(--text); }
  .client-header-wrap { margin-bottom: 12px; }
  /* Chrome above the shell: the back-crumb row plus the title row and its margin.
     ⚠️ Carried over from `.cv-shell` in ClientView, whose header block is the same
     construction — NOT measured on this page. Re-measure if this header changes. */
  .fund-detail-shell { --split-chrome: 118px; }
  .fund-detail-main { min-width: 0; padding-top: 0; }
  .fund-record-toolbar { display: flex; align-items: center; justify-content: flex-end; gap: 9px; padding: 14px 0; }
  .fund-record-subhead { margin: 20px 0 10px; color: var(--text-dim); font: 11px var(--font-mono); text-transform: uppercase; letter-spacing: .08em; }
  /* Which member's thread this is — only rendered in the institution view, where a
     thread can belong to someone other than the open contact. */
  .em-who { color: var(--text-dim); font-size: 11px; }
  /* Neutral, not green: this sits in the compose/respond area, where DESIGN-SYSTEM §1 keeps
     green out — green is on-track deal status only. The sentence says what happened. */
  .fund-send-notice { padding: 11px 14px; border: 1px solid var(--border); border-radius: var(--radius); background: var(--bg-surface); margin-bottom: 12px; color: var(--text); font-size: 13px; }
  .fund-sent-row,.fund-file-row,.fund-person-row { display: flex; align-items: center; gap: 12px; padding: 15px; border-bottom: 1px solid var(--border); }
  .fund-sent-row { justify-content: space-between; font-size: 12px; }
  .fund-sent-row span,.fund-file-row span,.fund-person-row p { color: var(--text-muted); font-size: 12px; }
  .fund-file-row { justify-content: space-between; }
  .fund-file-row span { display: block; margin-top: 3px; }
  /* `.ppl-av` on the Sales client page: a 34px circle, 11px/700 initials. The fill stays
     neutral here — these rows have no `avatarColor()` seed to key a gradient off. */
  .fund-person-avatar { width: 34px; height: 34px; flex: none; border-radius: 50%; background: var(--bg-surface); display: grid; place-items: center; font-size: 11px; font-weight: 700; }
  .fund-person-row p { margin: 3px 0; }
  .fund-contacts-heading { margin: 25px 0 12px; color: var(--text-muted); font-size: 14px; }
  .fund-team-heading { margin-top: 34px; }
  .fund-contact-person { padding: 15px 0; gap: 16px; }
  .fund-contact-person .fund-person-avatar { width: 38px; height: 38px; font-size: 13px; }
  .fund-person-info { min-width: 0; }
  .fund-person-info b, .fund-person-info .fund-row-link { font-size: 15px; }
  .fund-person-info p { margin: 4px 0 0; color: var(--text-muted); font-size: 13px; line-height: 1.5; overflow-wrap: anywhere; }
  .fund-contact-date { margin-left: 9px; color: var(--text-dim); font: 11px var(--font-mono); white-space: nowrap; }
  .fund-institution-tools { margin: 28px 0 0; border-top: 1px solid var(--border); padding-top: 12px; }
  .fund-institution-tools > summary { width: fit-content; color: var(--text-muted); font-size: 12px; cursor: pointer; }
  .fund-institution-tools[open] > summary { color: var(--text); }
  @media (max-width: 760px) { .fund-contact-date { display: block; margin-left: 0; white-space: normal; } }
  .fund-unclassified { padding: 24px; max-width: 65ch; }
  .fund-unclassified p,.fund-unclassified li { color: var(--text-muted); font-size: 13px; line-height: 1.6; }
  /* Same box as `.compose-area` in app.css — the Sales composer surface. */
  .fund-compose { margin: 0 0 16px; padding: 18px; border: 1px solid var(--border); border-radius: var(--radius); background: var(--bg-surface); box-shadow: var(--shadow-sm); }
  .ethread .fund-compose { margin: 0 14px 14px; }
  .fund-compose-commit { margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--border); }
  .fund-compose-note { margin-top: 8px; }
  .fund-compose-generate { display: flex; gap: 8px; margin: 14px 0 8px; }
  .fund-compose-generate input { flex: 1; min-width: 0; }
  .fund-compose-generate button { flex: none; white-space: nowrap; }
  @media (max-width: 600px) { .fund-compose-generate { flex-wrap: wrap; } .fund-compose-generate input { flex-basis: 100%; } }
  .fund-compose-heading,.fund-compose-actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .fund-compose-actions { justify-content: flex-start; margin-top: 14px; }
  .fund-field { display: block; margin: 12px 0; font-size: 12px; color: var(--text-muted); }
  /* A recipient is a short value; full width made it read as the page's main form. */
  .fund-email-recipient { max-width: 360px; margin-bottom: 6px; }
  .fund-field input:not([type="checkbox"]):not([type="radio"]),
  .fund-field textarea { display: block; width: 100%; margin-top: 5px; }
  .fund-field:has(> input[type="checkbox"]) { display: flex; align-items: center; gap: 7px; }
  .fund-field > input[type="checkbox"] { flex: none; margin: 0; }
  .fund-output-card { margin-top: 18px; border: 1px solid var(--border); border-radius: var(--radius); background: var(--bg-surface); overflow: hidden; }
  .fund-output-header { display: flex; align-items: flex-start; gap: 10px; padding: 15px 18px; border-bottom: 1px solid var(--border); }
  .fund-output-mark { display: grid; place-items: center; width: 26px; height: 26px; flex: none; border-radius: 50%; background: color-mix(in srgb, var(--accent) 14%, transparent); color: var(--accent-ink); }
  .fund-output-header h3 { margin: 1px 0 3px; color: var(--text); font-size: 14px; font-weight: 700; }
  .fund-output-header p { margin: 0; color: var(--text-muted); font-size: 12px; }
  .fund-output-body.md-content { max-width: 78ch; padding: 18px 20px 22px; font-size: 14px; line-height: 1.75; }
  .fund-output-body :global(:first-child) { margin-top: 0; }
  .fund-output-body :global(:last-child) { margin-bottom: 0; }
  .fund-output-footer { padding: 0 18px 16px; }
  .fund-inline-role { max-width: 160px; min-height: 30px; background: var(--bg-surface); color: var(--text); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 2px 6px; font: inherit; }
  .fund-alert { color: var(--red); font-size: 12px; }
  /* No local narrow-screen rule: `.split-shell`'s own `@media (max-width: 1100px)` in
     app.css collapses this to one column and `rail.showTab` moves the assistant into the
     tab bar. The 900px override this page used to carry set `display: block`, which
     would now drop the grid placement the rail and divider depend on. */
</style>
